import {getUserDefinedObjectAdditionalColumnDetails} from "~/domainModels/userDefinedObjectAdditionalColumns";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import AddIcon from "@atlaskit/icon/core/add";
import CalendarIcon from "@atlaskit/icon/core/calendar";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import ChevronUpIcon from "@atlaskit/icon/core/chevron-up";
import ClockIcon from "@atlaskit/icon/core/clock";
import DeleteIcon from "@atlaskit/icon/core/delete";
import DownloadIcon from "@atlaskit/icon/core/download";
import LocationIcon from "@atlaskit/icon/core/location";
import OfficeBuildingIcon from "@atlaskit/icon/core/office-building";
import PeopleGroupIcon from "@atlaskit/icon/core/people-group";
import ShowMoreVerticalIcon from "@atlaskit/icon/core/show-more-vertical";
import SubtasksIcon from "@atlaskit/icon/core/subtasks";
import {token} from "@atlaskit/tokens";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {Link, Navigate, createFileRoute, useNavigate, useSearch} from "@tanstack/react-router";
import {DateTime} from "luxon";
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent} from "react";
import {twJoin} from "tailwind-merge";
import {z} from "zod";
import {BottomDrawer} from "~/components/BottomDrawer";
import {CopyDataDropdownItem} from "~/components/dataActions/CopyDataDropdownItem";
import {ExportDataDropdownItem} from "~/components/dataActions/ExportDataDropdownItem";
import {ExportFlatDropdownItems} from "~/components/dataActions/ExportFlatDropdownItems";
import {ExportNestedDropdownItem} from "~/components/dataActions/ExportNestedDropdownItem";
import {ExportOptionsDrawerContent} from "~/components/dataActions/ExportOptionsDrawerContent";
import {ModuleMoreActionsDrawerContent} from "~/components/dataActions/ModuleMoreActionsDrawerContent";
import {RestoreDataDropdownItem} from "~/components/dataActions/RestoreDataDropdownItem";
import {EmotoradLeadDispositionModal} from "~/components/emotorad/emotoradLeadDispositionModal";
import {EventContainerEditPanel} from "~/components/eventContainerComponents/eventContainerEditPanel";
import {EventContainerViewPanel} from "~/components/eventContainerComponents/eventContainerViewPanel";
import {JourneyEntityProfileAside} from "~/components/entityProfile/journeyEntityProfileAside";
import {DuluxLeadDispositionModal} from "~/components/dulux/duluxLeadDispositionModal";
import {EventContainerListDispositionModal} from "~/components/eventContainerDetails/eventContainerListDispositionModal";
import {useListNotesPanel} from "~/components/notes/useListNotesPanel";
import {buildAmaronJourneyFilterClearConfig} from "~/components/ottopilotDataViewer/amaronDataViewerFilterClearConfig";
import {resolveAmaronJourneyCalendarDisplayMetrics} from "~/domainModels/calendarDisplayMetrics";
import {getAdditionalColumnOptions} from "~/domainModels/additionalColumns";
import {
    applyJourneyScopeSelectionToFilterConfiguration,
    getJourneyScopeSelectionFromFilter,
} from "~/components/ottopilotDataViewer/journeyScopeFilterConfiguration";
import {mergeModuleStatusFilterPreservationIntoClearConfig} from "~/components/ottopilotDataViewer/dataViewerFilterClearConfig";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import {
    useConfiguredJourneyListCardPresentation,
    type AssignedOuListCardFields,
} from "~/components/ottopilotDataViewer/configuredListCardPresentation";
import {mergeDefaultCreatedAtMonthFilter} from "~/components/ottopilotDataViewer/dataViewerDateRangeDefaults";
import {useEmotoradJourneysListCardPresentation} from "~/components/ottopilotDataViewer/emotoradListCardPresentation";
import {type FilterConfiguration, type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {
    invalidateDataViewerQueriesAfterJourneyArchive,
    invalidateOttopilotDataViewerQueries,
} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import {KanbanCardDispositionControl} from "~/components/ottopilotDataViewer/kanbanCardDispositionControl";
import {
    LIST_INQUEUE_ASSIGNMENT_STATUS,
    createInQueueAssignmentListStatusColumnConfig,
    getInQueueAssignmentSyntheticStatusValue,
} from "~/components/ottopilotDataViewer/listStatusConfig";
import {OttopilotDataViewer} from "~/components/ottopilotDataViewer/ottopilotDataViewer";
import {
    useOttopilotDataViewerController,
    type OttopilotDataViewerController,
} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {KanbanCardRowActions} from "~/components/ottopilotDataViewer/rowEngagement/KanbanCardRowActions";
import {resolveKanbanPhoneDial} from "~/components/ottopilotDataViewer/rowEngagement/resolveKanbanPhoneDial";
import {useEventContainerRowEngagement} from "~/components/ottopilotDataViewer/rowEngagement/useEventContainerRowEngagement";
import type {TableRowActionsConfig, TableRowActionsMenuItem} from "~/components/table/tableRowActions.types";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {SortDirection, type SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import type {SearchConfiguration} from "~/components/ottopilotDataViewer/searchConfiguration";
import {orderColumnsLikeTable} from "~/components/ottopilotDataViewer/exportFromDb";
import {useOttopilotExportActions} from "~/components/ottopilotDataViewer/useOttopilotExportActions";
import {nativeCsvEventColumn} from "~/utilities/nativeCsvDownload";
import {ViewType, type KanbanBoardColumnConfig} from "~/components/ottopilotDataViewer/view";
import {WorkspaceListCardCreateChrome} from "~/components/pageScaffolds/workspacePageScaffold/bottomNav/workspaceListCardCreateChrome";
import {RestoreConfirmationModal} from "~/components/restoreConfirmationModal";
import {ModelVariantIcon} from "~/components/svgs";
import {TableNoArchivedDataEmptyState} from "~/components/table/TableNoArchivedDataEmptyState";
import {RESTORE_SUCCESS_TOAST_BACKGROUND, TableSuccessToast} from "~/components/table/TableOperationToasts";
import {TableOnlyPage} from "~/components/tableOnlyPage";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useBtlActivities} from "~/contexts/workspaceBtlActivitiesContext";
import {
    useOrganizationalUnitTemplates,
    usePlatformUserDetails,
    useProductCatalogueTemplates,
    useWorkspace,
    useWorkspaceUserTemplates as useUserTemplates,
} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useOnlyEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import type {BtlActivity} from "~/domainModels/btlActivities";

import {OttopilotEventStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {eventContainerKanbanCardIconsMap as journeyKanbanCardIconsMap} from "~/domainModels/eventContainerPanelIcons";
import {
    isEventContainerEngagementRestrictedByGlobalConstraints,
    isListCardFooterRestrictedByGlobalConstraints,
} from "~/domainModels/workspaceModules/eventContainerEditPanelConstraints";

import type {OttopilotEventContainer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {eventContainerStatusToDisplayName} from "~/utilities/domainStatusDisplayNames";
import {getProductCatalogueDisplayName} from "~/utilities/productCatalogueDisplayName";
import {
    isKanbanCardSystemFieldId,
    type KanbanCardSystemFieldId,
} from "@vesta/vos-types/modules/workspaceLevel/domain/KanbanBoardColumnConfig";
import {resolveJourneysListConfig, resolveWorkspaceListSupport} from "~/domainModels/listView/workspaceListViewRegistry";
import {
    createAmaronCalendarGetEventColor,
    getAmaronCalendarLegendItems,
    resolveAmaronCalendarEventTypeLabel,
} from "~/domainModels/amaronCalendarStatusColors";
import {resolveWorkspaceListCreateItemsConfiguration} from "~/domainModels/listView/resolveWorkspaceListCreateItemsConfiguration";
import {
    ARCHIVED_MODULE_KANBAN_LAYOUT,
    buildEventContainerModuleInitialFilterConfiguration,
    filterKanbanColumnsForDataSlice,
    mergeModuleSliceMandatoryFilters,
    resolveEventContainerArchivedKanbanStatusIds,
    resolveEventContainerModuleSliceConfig,
    type ModuleDataSlice,
} from "~/domainModels/moduleDataSlice";
import {
    AMARON_INQUEUE_KANBAN_STATUS_FIELD,
    DULUX_MODULE_STATUS_FILTER_SCAFFOLD,
    applyAmaronModuleStatusFilterVisibilityToColumns,
    applyModuleStatusFilterVisibilityToColumns,
    filterKanbanColumnsForModuleStatusId,
    getAmaronInQueueKanbanColumnConfig,
    getAmaronInQueueStatusNavConfig,
    getAmaronJourneyLeadsKanbanColumnConfig,
    getAmaronJourneyLeadsStatusNavConfig,
    getAmaronModuleStatusNavValidStatusIds,
    isAmaronModuleStatusChildView,
    getDuluxJourneyLeadsStatusNavConfig,
    getDuluxModuleStatusNavValidStatusIds,
    getEmotoradInQueueKanbanColumnConfig,
    getEmotoradInQueueStatusNavConfig,
    getEmotoradJourneyLifecycleStatusNavConfig,
    getEmotoradModuleStatusNavValidStatusIds,
    resolveAmaronModuleStatusFilterPickerValues,
    resolveDuluxModuleStatusFilterPickerValues,
} from "~/domainModels/moduleStatusNav";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {OrganizationalUnitTemplateAdditionalColumnType as OrganizationalUnitAdditionalColumnType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplateAdditionalColumns";
import type {ProductCatalogue} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogue";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {OttopilotEventContainerViewPanelSectionKind as OttopilotViewPanelSectionKind} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTemplateConfiguration";
import type {OttopilotEventContainerTableViewColumn} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTableViewColumn";
import type {
    EventContainerTemplate,
    EventContainerTemplate as JourneysModuleConfiguration,
} from "~/domainModels/workspaceModules/eventContainersModule";
import type {ResolvedCalendarScheduleEmptyState} from "@vesta/vos-types/modules/workspaceLevel/domain/SharedCalendarViewConfig";
import {
    AMARON_INQUEUE_TABLE_VISIBLE_COLUMN_IDS,
    AMARON__EVENT_CONTAINER__AON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
    amaronJourneySyntheticFunction,
    createAmaronJourneyListFunnelOptions,
    formatAmaronInqueueStatusFilterValue,
    formatAmaronJourneyLastActivityDisplay,
    formatAmaronScheduledCallDateDisplay,
    resolveAmaronLastActivityOnDisplay,
    getAmaronInqueueStatusFilterPickerValues,
    getAmaronJourneyScopeFilterStatusIdsForEvents,
    getAmaronListModuleConfig,
    AMARON_INQUEUE_KANBAN_LAYOUT,
    isAmaronWorkspace,
    AMARON_DEFAULT_VISIBLE_FILTER_COLUMN_IDS,
    mergeAmaronJourneyDefaultFilterColumnIdsIntoVisibleColumns,
} from "~/domainModels/workspaceSpecific/amaron";
import {
    resolveEventContainerEditPanelGlobalConstraints,
    resolveEventContainerScheduleCallDisableConfig,
} from "~/utilities/eventContainerGlobalConstraints";
import {
    AMARON_JOURNEY_DISPOSITION_LEVEL_BY_COLUMN_ID,
    appendAmaronJourneyDispositionApiFilterIfMissing,
    buildAmaronDispositionFilterColumn,
    isAmaronJourneyDispositionUiFilterColumn,
    sanitizeAmaronDispositionFiltersForExport,
} from "~/domainModels/workspaceSpecific/amaronDispositionFilters";
import {
    BOBCARD_JOURNEY_TABLE_VISIBLE_COLUMN_IDS,
    BOBCARD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    BOBCARD__EVENT_CONTAINER__FIRST_NAME__COLUMN_ID,
    BOBCARD__EVENT_CONTAINER__LAST_NAME__COLUMN_ID,
    BOBCARD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    BOBCARD__EVENT_CONTAINER__STATUS__COLUMN_ID,
    bobcardApplicationStatuses,
    isBobcardWorkspace,
} from "~/domainModels/workspaceSpecific/bobcard";
import {DHIRAAGU__EVENT_CONTAINER__CREATE__FORM__ID, isDhiraaguWorkspace} from "~/domainModels/workspaceSpecific/dhiraagu";
import {
    DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    DULUX_MODULE_KANBAN_LAYOUT,
    buildDuluxJourneyLeadsTableColumns,
    duluxColumnIsExportable,
    getDuluxJourneyLeadsKanbanColumnConfig,
    getDuluxJourneysSearchPlaceholder,
    isDuluxWorkspace,
    resolveDuluxJourneyGlobalConstraints,
    resolveDuluxJourneysSearchColumns,
    wrapDuluxAidOnboardedJourneyRowActions,
} from "~/domainModels/workspaceSpecific/dulux";
import {
    EMOTORAD_INQUEUE_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_JOURNEY_TABLE_VISIBLE_COLUMN_IDS,
    EMOTORAD_LEADS_DEFAULT_VISIBLE_FILTER_SLOTS,
    EMOTORAD_LIFECYCLE_OVERVIEW_NAV_VISIBLE_ROLE_TEMPLATE_IDS,
    EMOTORAD__CUSTOMERS__EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__LIVE_EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CATEGORY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CATEGORY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_AGE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LIFE_CYCLE_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__SOURCE_CHANNEL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__TEST_RIDES_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    EMOTORAD__USER__ADDRESS_PINCODE__COLUMN_ID,
    EMOTORAD__USER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__USER__DEALERSHIP__COLUMN_ID,
    EMOTORAD__USER__EMAIL__COLUMN_ID,
    EmotoradCategoryEnum,
    EmotoradCurrentPlatformEnum,
    EmotoradEventTypes,
    EmotoradEventContainerStatusEnum,
    EmotoradOus,
    emotoradCategories,
    emotoradJourneyAonConfig,
    emotoradEventContainerStatuses,
    emotoradJourneySyntheticFunction,
    formatEmotoradCurrentPlatformCell,
    formatEmotoradJourneyLifecycleStatusIdForDisplay,
    getEmotoradCategorySelectOptions,
    getEmotoradCurrentPlatformSelectOptions,
    getEmotoradDispositionIdsForLevels,
    getEmotoradDynamicJourneySelectOptions,
    getEmotoradJourneysPageScopeFilterConfig,
    getEmotoradListEventCreateItems,
    getEmotoradListModuleConfig,
    getEmotoradUniqueDispositionLevelValues,
    isEmotoradActionPlatformColumnId,
    isEmotoradWorkspace,
    sanitizeEmotoradFilterColumnValues,
} from "~/domainModels/workspaceSpecific/emotorad";
import {EMOTORAD__LEAD__CREATE__FORM__ID} from "~/domainModels/workspaceSpecific/emotorad_old";
import {
    AnankeClockIcon,
    AnankeDatabaseStorageCacheIcon,
    AnankeDevicesIcon,
    AnankeInformationCircleIcon,
    AnankeLinkExternalIcon,
    AnankeModelVariantIcon,
    AnankeOfficeBuildingIcon,
    AnankePersonIcon,
    AnankeServiceIcon,
} from "~/domainModels/workspaceSpecific/icons";
import {useRequiredWorkspaceMobileNavigationConfiguration} from "~/hooks/workspaceConfiguration";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {
    mergeModuleStatusFilterIntoFilterConfiguration,
    resolveModuleStatusIdForFilter,
    useHideAmaronModuleStatusFilterUiInChildView,
    useHideModuleStatusFilterUiInChildView,
    useSyncInQueueAssignmentFilterFromSearch,
    useSyncModuleStatusFilterFromSearch,
} from "~/hooks/useSyncModuleStatusFilterFromSearch";
import {EmotoradLifecycleKanbanBoardCard} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/emotorad/-emotorad-lifecycle-kanban-card";
import {
    buildAmaronInQueueEditPanelConfiguration,
    buildAmaronInQueueViewPanelConfiguration,
    buildAmaronLeadsEditPanelConfiguration,
    buildAmaronLeadsViewPanelConfiguration,
} from "./-amaron-journey-panels";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";

import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeLinkButton} from "@vesta/ananke/components/anankeLinkButton";
import {AnankeSimpleTag} from "@vesta/ananke/components/anankeSimpleTag";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {ComplexFilter} from "@vesta/vos-types/modules/workspaceLevel/domain/ComplexFilter";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import {VerticalSpacer} from "@vesta/global-common-typescript/components/verticalSpacer";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {getAmaronJourneyListCardAssignedOuColumnLabel, getAmaronJourneyListCardAssignedOuName} from "~/utilities/amaronListCardOuFields";
import {
    getEventContainerDispositionOptions,
    getOptionalEventContainerDispositionId,
    getOptionalEventContainerOrganizationalUnitId,
} from "~/utilities/ottopilotSemanticColumns";
import {
    convertFilterConfigurationToSrimanFilters,
    convertSortConfigurationToSrimanOrderBy,
    type EventContainerWithEvents,
} from "~/utilities/apis/api";
import {
    countEventContainersWithEvents,
    getEventContainerColumnValues,
    getEventContainersWithEvents,
} from "~/utilities/apis/eventContainersApi";
import {
    countOrchestratedEventContainersByColumn,
    getOrchestratedEventContainers,
    resolveCalendarOrchestrationEligibility,
} from "~/utilities/apis/eventContainerAndEventOrchestrationApi";
import {emotoradJourneyTestRideCountCellString} from "~/utilities/emotoradJourneyTestRideCount";
import {buildPageTitleExportFilename} from "~/utilities/exportUtilities";
import {
    eventContainerTableViewColumnsToOttopilotColumns,
    resolveEmotoradLastActivityAgeDisplay,
    resolveEmotoradLastActivityEventDisplay,
} from "~/utilities/eventContainerTableViewColumnsTransform";
import {
    augmentEmotoradLifecycleCustomerZoneFromPincode,
    formatEmotoradLifecycleCustomerZoneDisplay,
} from "~/utilities/resolveEmotoradLifecycleCustomerZoneDisplay";
import {restoreEventContainerWithEvents} from "~/utilities/restoreArchivedRow";
import {
    EMOTORAD_LEADS_CALENDAR_SCHEDULE_EMPTY_STATE,
    resolveCalendarAllDayStyle,
    resolveCalendarAllDayStyleForModuleSlice,
    resolveCalendarEventTypeColor,
    resolveCalendarEventTypeLabel,
    resolveCalendarLegendItems,
    resolveCalendarScheduleEmptyState,
    wrapGetEventColorForArchivedModuleSlice,
} from "~/utilities/sharedCalendarConfig";
import {
    buildAonCustomFilter,
    buildDistinctAonFilterValuesFromCreatedAtColumnValues,
    isAonFilterRangesLineItem,
    mapAonFilterLineItemForApi,
    type AonWorkspaceConfig,
} from "~/utilities/syntheticFields/aon";
import {
    dispositionToLevelFormat,
    flatDispositionToDisplayNameFormat,
    formatDateAndTime,
    formatDateTime,
    getUserDisplayName,
    type PlatformUserDisplayDetails,
    transformRowForExport,
} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {uniqueUuid} from "@vesta/unique/unique";
import {applyInQueueAssignmentStatusIdToFilterConfiguration, partitionInQueueFiltersForBackend} from "~/utilities/inQueueAssignmentFilters";
import {formatAmaronSourcePriorityScoreForDisplay, getAmaronSourcePriorityStoredScore} from "~/utilities/workspaceSpecific/amaronPriority";
import {
    buildEventContainerProfileFromForList,
    buildEventContainerProfileNavigateSearch,
} from "~/utilities/eventContainerProfileNavigation";
import {formatKanbanCardFieldValue} from "~/utilities/kanbanCardDisplay";
import {createOttopilotListView} from "~/components/ottopilotDataViewer/ottopilotListCardPresentation";

const journeysSearchSchema = z.object({
    view: z.enum(["list", "table", "kanban", "calendar", "create"]).optional().default("table").catch("table"),
    statusId: z.string().optional(),
});

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/event-containers/",
)({
    validateSearch: (search: Record<string, unknown>) => journeysSearchSchema.parse(search),
    loader: async () => {},
    component: JourneysIndexRouteComponent,
});

function JourneysIndexRouteComponent() {
    const {workspace} = useWorkspace();
    const {workspaceUser} = useWorkspaceUser();
    const canAccessEmotoradLifecycleOverview =
        isEmotoradWorkspace(workspace) !== true || EMOTORAD_LIFECYCLE_OVERVIEW_NAV_VISIBLE_ROLE_TEMPLATE_IDS.has(workspaceUser.templateId);

    if (canAccessEmotoradLifecycleOverview !== true) {
        return (
            <Navigate
                to="/w/$workspaceId"
                params={{workspaceId: workspace.id}}
            />
        );
    }

    return <JourneysRouteComponent key="journeys" />;
}

const JOURNEYS_TABLE_VIEW_ID = uniqueUuid`f74f6723-d5a2-4d84-9733-d1676d2408c1`;
const JOURNEYS_LIST_VIEW_ID = uniqueUuid`8f58b6dd-53b0-40e2-a60b-4f3c81774111`;
const JOURNEYS_INQUEUE_TABLE_VIEW_ID = uniqueUuid`7d1553c0-8d13-44dc-b46a-51986fca43c8`;
const JOURNEYS_INQUEUE_KANBAN_VIEW_ID = uniqueUuid`f7625d2f-c886-4ac0-b9c4-d23af861f950`;
const JOURNEYS_INQUEUE_CALENDAR_VIEW_ID = uniqueUuid`6f619f99-f91f-4ff6-8b6f-0158e52a2eb8`;
const JOURNEYS_EMOTORAD_LIFECYCLE_CALENDAR_VIEW_ID = uniqueUuid`b2c63a1e-9d44-5f80-a31b-4e8c0d7b92f1`;
const JOURNEYS_KANBAN_VIEW_ID = uniqueUuid`e1df721b-c513-4e60-9e5f-2495b277fdb4`;
/** Calendar on `/journeys` (lead list); In Queue uses {@link JOURNEYS_INQUEUE_CALENDAR_VIEW_ID}. */
const JOURNEYS_CALENDAR_VIEW_ID = uniqueUuid`9c8d7e6f-5a4b-4029-c817-2f3e4d5c6b7a`;

/** Lifecycle Overview calendar card title: customer name from journey columns. */
const EMOTORAD_LIFECYCLE_CALENDAR_TITLE_OTTOPILOT_TEMPLATE = `👉fcb46858-4d6e-4afe-bc11-3cd88c580335👆${EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID}👈`;
const BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID = uniqueUuid`a35f1698-4d0a-4d9c-8064-5f3dd7e2ba0b`;
const EMOTORAD_INQUEUE_STATUS_FIELD = "__inQueueStatus";
const AMARON_INQUEUE_STATUS_FIELD = AMARON_INQUEUE_KANBAN_STATUS_FIELD;
/**
 * Bulk fetch only when filters cannot be sent to the API (Bobcard composite customer name).
 * In Queue Assigned/Unassigned uses lead-owner null
 * checks on the backend with normal pagination — not this limit.
 */
const JOURNEYS_CLIENT_SIDE_FILTER_PROBE_FETCH_LIMIT = 5000;

function compactEventContainerColumnValues(values: Array<string | null>): Array<string> {
    return values.filter((value): value is string => value !== null);
}

function toEventContainerFilters(
    filterConfiguration: FilterConfiguration,
    searchConfiguration?: SearchConfiguration,
): ComplexFilter {
    return convertFilterConfigurationToSrimanFilters(
        filterConfiguration,
        searchConfiguration,
    ) as ComplexFilter;
}

function toEventContainerOrderBy(sortConfiguration: SortConfiguration): Array<{
    column: string;
    direction: "asc" | "desc";
}> {
    return convertSortConfigurationToSrimanOrderBy(sortConfiguration) as Array<{
        column: string;
        direction: "asc" | "desc";
    }>;
}

/** UI-only Emotorad journey disposition level filters (slug values); API uses `disposition` line item. */
const EMOTORAD_JOURNEY_DISPOSITION_UI_FILTER_COLUMN_IDS: ReadonlySet<string> = new Set([
    EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
]);

function isJourneyDispositionUiFilterColumn(columnId: string): boolean {
    return isAmaronJourneyDispositionUiFilterColumn(columnId) === true || EMOTORAD_JOURNEY_DISPOSITION_UI_FILTER_COLUMN_IDS.has(columnId);
}

function stripJourneyDispositionLevelFilters(lineItems: FilterConfiguration["lineItems"]): FilterConfiguration["lineItems"] {
    return lineItems.filter((lineItem) => lineItem.columnId !== "disposition" && isJourneyDispositionUiFilterColumn(lineItem.columnId) !== true);
}

const JOURNEY_USER_REFERENCE_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Filter picker stores raw user UUIDs; format labels for display while keeping UUID values for API filters. */
function buildJourneyUserReferenceFilterColumn(
    columnId: string,
    displayName: string,
    getRawUserId: (journey: EventContainerWithEvents) => Uuid | null | undefined,
    deps: {
        users: Array<User>;
        userTemplates: Array<UserTemplate>;
        platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
        workspaceId: Uuid;
        eventContainerTemplateId: Uuid;
        intellsysBaseUrl: string;
        accessToken: string;
    },
): OttopilotColumn<EventContainerWithEvents> {
    return {
        id: columnId,
        type: OttopilotColumnType.string,
        displayName,
        valueGetter: (journey: EventContainerWithEvents): string => {
            const raw = getRawUserId(journey);
            if (raw === null || raw === undefined) {
                return "";
            }
            const trimmed = String(raw).trim();
            return trimmed === "" ? "" : trimmed;
        },
        valueFormatter: (value: string): string => {
            const trimmed = value.trim();
            if (trimmed === "") {
                return "";
            }
            if (JOURNEY_USER_REFERENCE_UUID_PATTERN.test(trimmed) !== true) {
                return trimmed;
            }
            const displayName = getUserDisplayName(trimmed as Uuid, deps.users, deps.userTemplates, deps.platformUserDetailsByUserId);
            return JOURNEY_USER_REFERENCE_UUID_PATTERN.test(displayName) === true ? "-" : displayName;
        },
        sortingCapability: true,
        filteringCapability_contains: false,
        filteringCapability_in: {
            valuesQueryFn: async () => {
                const journeyColumnValuesResult = await getEventContainerColumnValues({
                    workspaceId: deps.workspaceId,
                    column: columnId,
                    templateId: deps.eventContainerTemplateId,
                    intellsysBaseUrl: deps.intellsysBaseUrl,
                    accessToken: deps.accessToken,
                });
                if (journeyColumnValuesResult.success !== true) {
                    throw journeyColumnValuesResult.error;
                }
                return compactEventContainerColumnValues(journeyColumnValuesResult.data);
            },
        },
    };
}

function normalizeLabel(value: string): string {
    return value.trim().toLowerCase();
}

function getJourneyOuDisplayName({
    journey,
    eventContainerTemplate,
    organizationalUnits,
    organizationalUnitTemplates,
}: {
    journey: EventContainerWithEvents;
    eventContainerTemplate: EventContainerTemplate;
    organizationalUnits: ReadonlyArray<OrganizationalUnit>;
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>;
}): string | null {
    const organizationalUnitId = getOptionalEventContainerOrganizationalUnitId(journey, eventContainerTemplate);
    if (organizationalUnitId === null) {
        return null;
    }
    const ou = organizationalUnits.find((item) => item.id === organizationalUnitId);
    if (ou === undefined) {
        return null;
    }
    const template = organizationalUnitTemplates.find((item) => item.id === ou.templateId);
    if (template === undefined) {
        return null;
    }
    const value = processOttopilotStringTemplate(template.instanceDisplayNameTemplate, ou.additionalColumnValues);
    return value.trim() === "" ? null : value;
}

function journeyFilterLineItemCompatibleWithMandatoryNotEqualsNull(lineItem: FilterConfiguration["lineItems"][number]): boolean {
    if (lineItem.operation === FilterOperation.equals && lineItem.value !== null) {
        return true;
    }
    if (lineItem.operation === FilterOperation.in && lineItem.values.length > 0) {
        return lineItem.values.every((value) => value !== null);
    }
    return false;
}

/** Ensures mandatory API constraints apply without dropping compatible filters (e.g. scope "Assigned to me"). */
function mergeEmotoradLifecycleOverviewMandatoryJourneyApiFilters(
    filterConfiguration: FilterConfiguration,
    mandatoryLineItems: FilterConfiguration["lineItems"],
): FilterConfiguration {
    if (mandatoryLineItems.length === 0) {
        return filterConfiguration;
    }
    const mandatoryColumnIds = new Set(mandatoryLineItems.map((lineItem) => lineItem.columnId));
    return {
        ...filterConfiguration,
        lineItems: [
            ...filterConfiguration.lineItems.filter((lineItem) => {
                if (mandatoryColumnIds.has(lineItem.columnId) === false) {
                    return true;
                }
                return journeyFilterLineItemCompatibleWithMandatoryNotEqualsNull(lineItem);
            }),
            ...mandatoryLineItems,
        ],
    };
}

function resolveEmotoradLeadsDefaultVisibleFilterColumnIds(
    slots: ReadonlyArray<{fieldIds: ReadonlyArray<string>; nameHints: ReadonlyArray<string>}>,
    tableViewColumns: Array<OttopilotEventContainerTableViewColumn> | undefined,
    additionalColumns: ReadonlyArray<{id: Uuid; displayName: string}>,
): Array<string> {
    const byField = new Map<string, string>();
    const byNormName = new Map<string, string>();

    if (tableViewColumns !== undefined) {
        for (const col of tableViewColumns) {
            byField.set(col.field, col.id);
            const key = normalizeLabel(col.displayName);
            if (byNormName.has(key) === false) {
                byNormName.set(key, col.id);
            }
        }
    }
    for (const ac of additionalColumns) {
        const idStr = String(ac.id);
        if (byField.has(idStr) === false) {
            byField.set(idStr, idStr);
        }
        const key = normalizeLabel(ac.displayName);
        if (byNormName.has(key) === false) {
            byNormName.set(key, idStr);
        }
    }

    const picked: Array<string> = [];
    const used = new Set<string>();

    for (const slot of slots) {
        let resolved: string | undefined;
        for (const fid of slot.fieldIds) {
            const x = byField.get(fid);
            if (x !== undefined) {
                resolved = x;
                break;
            }
        }
        if (resolved === undefined) {
            for (const hint of slot.nameHints) {
                const x = byNormName.get(normalizeLabel(hint));
                if (x !== undefined) {
                    resolved = x;
                    break;
                }
            }
        }
        if (resolved === undefined) {
            const hintsNorm = slot.nameHints.map((h) => normalizeLabel(h)).filter((h) => h.length > 0);
            for (const ac of additionalColumns) {
                const dn = normalizeLabel(ac.displayName);
                if (hintsNorm.some((h) => dn === h || dn.includes(h))) {
                    resolved = String(ac.id);
                    break;
                }
            }
        }
        if (resolved === undefined && tableViewColumns !== undefined) {
            const hintsNorm = slot.nameHints.map((h) => normalizeLabel(h)).filter((h) => h.length > 0);
            for (const col of tableViewColumns) {
                const dn = normalizeLabel(col.displayName);
                if (hintsNorm.some((h) => dn === h || dn.includes(h))) {
                    resolved = col.id;
                    break;
                }
            }
        }
        if (resolved !== undefined && used.has(resolved) === false) {
            used.add(resolved);
            picked.push(resolved);
        }
    }
    return picked;
}

function buildEmotoradInQueueViewPanelConfiguration(getAdditionalColumns: CapabilityUdoColumnResolver,
    journeysModuleConfiguration: JourneysModuleConfiguration,
): JourneysModuleConfiguration["configuration"]["eventContainersPage"]["viewPanel"] {
    const availableColumnIds = new Set<string>(getAdditionalColumns(journeysModuleConfiguration).map((column) => column.id));
    const byDisplayName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const getColumnId = (displayNames: string[], fallbackColumnIds: string[] = []): Uuid | null => {
        for (const id of fallbackColumnIds) {
            if (availableColumnIds.has(id)) {
                return id as Uuid;
            }
        }
        for (const displayName of displayNames) {
            const id = byDisplayName.get(normalizeLabel(displayName));
            if (id !== undefined) {
                return id;
            }
        }
        return null;
    };
    const toSectionColumns = (
        rows: Array<{
            label: string;
            displayNames: string[];
            fallbackColumnIds?: string[];
            fallbackSystemColumnId?: string;
        }>,
    ): Array<{
        id: Uuid;
        label: string;
        columnId: Uuid | string;
        isAdditionalColumn: boolean;
        columnType: "OttopilotEventContainer";
    }> => {
        return rows
            .map((row) => {
                const columnId = getColumnId(row.displayNames, row.fallbackColumnIds);
                if (columnId !== null) {
                    return {
                        id: columnId,
                        label: row.label,
                        columnId: columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }

                if (row.fallbackSystemColumnId !== undefined) {
                    return {
                        id: `emotorad-inqueue-system-${row.fallbackSystemColumnId}-${normalizeLabel(row.label)}` as Uuid,
                        label: row.label,
                        columnId: row.fallbackSystemColumnId,
                        isAdditionalColumn: false,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }

                return null;
            })
            .filter(
                (
                    item,
                ): item is {
                    id: Uuid;
                    label: string;
                    columnId: Uuid | string;
                    isAdditionalColumn: boolean;
                    columnType: "OttopilotEventContainer";
                } => item !== null,
            );
    };
    return {
        sections: [
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`emotorad-inqueue-customer-details`,
                displayName: "Customer details",
                iconId: AnankePersonIcon,
                iconColor: "var(--ds-icon-accent-blue)",
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {label: "Customer Name", displayNames: ["Customer Name", "customer name"]},
                    {label: "Contact number", displayNames: ["Contact Number", "Contact number", "Phone Number"]},
                    {label: "Pincode", displayNames: ["Customer Pincode", "Pincode", "Customer_PinCode"]},
                ]),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`emotorad-inqueue-product-details`,
                displayName: "Product details",
                iconId: AnankeModelVariantIcon,
                iconColor: "var(--ds-icon-accent-orange)",
                iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([{label: "Model Variant", displayNames: ["Model Variant", "Model Varient", "Model"]}]),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`emotorad-inqueue-showroom-details`,
                displayName: "Showroom details",
                iconId: AnankeOfficeBuildingIcon,
                iconColor: "var(--ds-icon-accent-green)",
                iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {
                        label: "Lifecycle Owner",
                        displayNames: ["Lifecycle Owner", "Lead Owner", "Action Owner"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID],
                    },
                    {
                        label: "Assigned Dealer",
                        displayNames: ["Assigned Dealer", "Dealer Name", "Dealer"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID],
                    },
                    {
                        label: "KAM Name",
                        displayNames: ["KAM Name", "Kam Name"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID],
                    },
                    {
                        label: "RM Name",
                        displayNames: ["RM Name", "Rm Name", "RM name", "Rm name", "RM_Name", "ZSM Name", "Zsm Name"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID],
                    },
                    {
                        label: "Pincode",
                        displayNames: ["Dealer Pincode", "Dealer PinCode", "Dealer Pincode ", "Dealer Pin Code", "Dealer_Pincode"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID],
                    },
                    {
                        label: "City",
                        displayNames: ["Dealer City", "Dealer city", "City", "Dealer_City", "Dealer_City "],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID],
                    },
                    {
                        label: "State",
                        displayNames: ["Dealer State", "Dealer state", "Dealer Sate", "State", "Dealer_State", "Dealer_State "],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID],
                    },
                    {
                        label: "Zone",
                        displayNames: ["Dealer Zone", "Dealer zone", "Zone", "Dealer_Zone", "Dealer_Zone "],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID],
                    },
                ]),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`emotorad-inqueue-activity-details`,
                displayName: "Activity Details",
                iconId: AnankeClockIcon,
                iconColor: "var(--ds-icon-accent-yellow)",
                iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {label: "Assigned on", displayNames: ["Assigned On", "Assigned on"]},
                    {
                        label: "Modified on",
                        displayNames: ["Modified On", "Updated On"],
                        fallbackSystemColumnId: "updatedAt",
                    },
                    {
                        label: "Modified by",
                        displayNames: ["Modified By", "Updated By"],
                        fallbackSystemColumnId: "updatedBy",
                    },
                    {label: "Action Platform", displayNames: ["Action Platform", "Platform"]},
                ]),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`emotorad-inqueue-utm-details`,
                displayName: "UTM Parameters",
                iconId: AnankeLinkExternalIcon,
                iconColor: "var(--ds-icon-accent-purple)",
                iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {label: "Utm Platform", displayNames: ["Utm_platform", "utm_platform", "UTM Platform"]},
                    {label: "Utm Source", displayNames: ["Utm_source", "utm_source", "UTM Source"]},
                    {label: "Utm Campaign", displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign"]},
                    {label: "Utm Term", displayNames: ["Utm_term", "utm_term", "UTM Term"]},
                    {label: "Utm medium", displayNames: ["Utm_medium", "utm_medium", "UTM Medium"]},
                    {label: "Utm Content", displayNames: ["Utm_content", "utm_content", "UTM Content"]},
                ]),
            },
        ],
    };
}

function buildEmotoradLifecycleOverviewViewPanelConfiguration(getAdditionalColumns: CapabilityUdoColumnResolver,
    journeysModuleConfiguration: JourneysModuleConfiguration,
): JourneysModuleConfiguration["configuration"]["eventContainersPage"]["viewPanel"] {
    const availableColumnIds = new Set<string>(getAdditionalColumns(journeysModuleConfiguration).map((column) => column.id));
    const byDisplayName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const getColumnId = (displayNames: string[], fallbackColumnIds: string[] = []): Uuid | null => {
        for (const id of fallbackColumnIds) {
            if (availableColumnIds.has(id)) {
                return id as Uuid;
            }
        }
        for (const displayName of displayNames) {
            const id = byDisplayName.get(normalizeLabel(displayName));
            if (id !== undefined) {
                return id;
            }
        }
        return null;
    };
    const toSectionColumns = (
        rows: Array<{
            label: string;
            displayNames: string[];
            fallbackColumnIds?: string[];
            fallbackSystemColumnId?: string;
        }>,
    ): Array<{
        id: Uuid;
        label: string;
        columnId: Uuid | string;
        isAdditionalColumn: boolean;
        columnType: "OttopilotEventContainer";
    }> => {
        return rows
            .map((row) => {
                const columnId = getColumnId(row.displayNames, row.fallbackColumnIds);
                if (columnId !== null) {
                    return {
                        id: columnId,
                        label: row.label,
                        columnId: columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }

                if (row.fallbackSystemColumnId !== undefined) {
                    return {
                        id: `emotorad-lifecycle-system-${row.fallbackSystemColumnId}-${normalizeLabel(row.label)}` as Uuid,
                        label: row.label,
                        columnId: row.fallbackSystemColumnId,
                        isAdditionalColumn: false,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }

                return null;
            })
            .filter(
                (
                    item,
                ): item is {
                    id: Uuid;
                    label: string;
                    columnId: Uuid | string;
                    isAdditionalColumn: boolean;
                    columnType: "OttopilotEventContainer";
                } => item !== null,
            );
    };
    return {
        sections: [
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`emotorad-lifecycle-customer-details`,
                displayName: "Customer details",
                iconId: AnankePersonIcon,
                iconColor: "var(--ds-icon-accent-blue)",
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {label: "Customer Name", displayNames: ["Customer Name", "customer name"]},
                    {label: "Contact number", displayNames: ["Contact Number", "Contact number", "Phone Number"]},
                    {label: "Pincode", displayNames: ["Customer Pincode", "Pincode", "Customer_PinCode"]},
                    {
                        label: "State",
                        displayNames: ["Customer State", "Customer state", "State"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID],
                    },
                    {
                        label: "City",
                        displayNames: ["Customer City", "Customer city", "City"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID],
                    },
                    {
                        label: "Zone",
                        displayNames: ["Customer Zone", "Customer zone", "Zone"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID],
                    },
                    {
                        label: "Source Channels",
                        displayNames: ["Source channel", "Source Channel", "Source Channels"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__SOURCE_CHANNEL__COLUMN_ID],
                    },
                    {
                        label: "Purchase Timeline",
                        displayNames: ["Purchase Timeline"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID],
                    },
                ]),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`emotorad-lifecycle-product-details`,
                displayName: "Product details",
                iconId: AnankeModelVariantIcon,
                iconColor: "var(--ds-icon-accent-orange)",
                iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {
                        label: "Model",
                        displayNames: ["Model"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID],
                    },
                    {
                        label: "Model Variant",
                        displayNames: ["Model Variant", "Model Varient"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID],
                    },
                ]),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`emotorad-lifecycle-showroom-details`,
                displayName: "Showroom Details",
                iconId: AnankeOfficeBuildingIcon,
                iconColor: "var(--ds-icon-accent-green)",
                iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {
                        label: "Lifecycle Owner",
                        displayNames: ["Lifecycle Owner", "Lead Owner", "Action Owner"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID],
                    },
                    {
                        label: "Assigned Dealer",
                        displayNames: ["Assigned Dealer", "Dealer Name", "Dealer"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID],
                    },
                    {
                        label: "KAM Name",
                        displayNames: ["KAM Name", "Kam Name"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID],
                    },
                    {
                        label: "RM Name",
                        displayNames: ["RM Name", "Rm Name", "RM name", "Rm name", "RM_Name", "ZSM Name", "Zsm Name"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID],
                    },
                    {
                        label: "Pincode",
                        displayNames: ["Dealer Pincode", "Dealer PinCode", "Dealer Pincode ", "Dealer Pin Code", "Dealer_Pincode"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID],
                    },
                    {
                        label: "City",
                        displayNames: ["Dealer City", "Dealer city", "City", "Dealer_City", "Dealer_City "],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID],
                    },
                    {
                        label: "State",
                        displayNames: ["Dealer State", "Dealer state", "Dealer Sate", "State", "Dealer_State", "Dealer_State "],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID],
                    },
                    {
                        label: "Zone",
                        displayNames: ["Dealer Zone", "Dealer zone", "Zone", "Dealer_Zone", "Dealer_Zone "],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID],
                    },
                ]),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`emotorad-lifecycle-activity-details`,
                displayName: "Activity Details",
                iconId: AnankeClockIcon,
                iconColor: "var(--ds-icon-accent-yellow)",
                iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {label: "Assigned on", displayNames: ["Assigned On", "Assigned on"]},
                    {
                        label: "Modified on",
                        displayNames: ["Modified On", "Updated On"],
                        fallbackSystemColumnId: "updatedAt",
                    },
                    {
                        label: "Modified by",
                        displayNames: ["Modified By", "Updated By"],
                        fallbackSystemColumnId: "updatedBy",
                    },
                    {label: "Action Platform", displayNames: ["Action Platform", "Platform"]},
                ]),
            },
            {
                kind: OttopilotViewPanelSectionKind.columnGroup,
                id: uniqueUuid`emotorad-lifecycle-utm-details`,
                displayName: "UTM Parameters",
                iconId: AnankeLinkExternalIcon,
                iconColor: "var(--ds-icon-accent-purple)",
                iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {label: "Utm Platform", displayNames: ["Utm_platform", "utm_platform", "UTM Platform"]},
                    {label: "Utm Source", displayNames: ["Utm_source", "utm_source", "UTM Source"]},
                    {label: "Utm Campaign", displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign"]},
                    {label: "Utm Term", displayNames: ["Utm_term", "utm_term", "UTM Term"]},
                    {label: "Utm medium", displayNames: ["Utm_medium", "utm_medium", "UTM Medium"]},
                    {label: "Utm Content", displayNames: ["Utm_content", "utm_content", "UTM Content"]},
                ]),
            },
        ],
    };
}

function buildEmotoradInQueueEditPanelConfiguration(getAdditionalColumns: CapabilityUdoColumnResolver,
    journeysModuleConfiguration: JourneysModuleConfiguration,
): JourneysModuleConfiguration["configuration"]["eventContainersPage"]["editPanel"] {
    const availableColumnIds = new Set<string>(getAdditionalColumns(journeysModuleConfiguration).map((column) => column.id));
    const byDisplayName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const getColumnId = (displayNames: string[], fallbackColumnIds: string[] = []): Uuid | null => {
        for (const id of fallbackColumnIds) {
            if (availableColumnIds.has(id)) {
                return id as Uuid;
            }
        }
        for (const displayName of displayNames) {
            const id = byDisplayName.get(normalizeLabel(displayName));
            if (id !== undefined) {
                return id;
            }
        }
        return null;
    };
    const toSectionColumns = (
        rows: Array<{
            label: string;
            isEditable: boolean;
            displayNames: string[];
            fallbackColumnIds?: string[];
            fallbackSystemColumnId?: string;
        }>,
    ): Array<{
        id: Uuid;
        isEditable: boolean;
        label: string;
        columnId: Uuid | string;
        isAdditionalColumn: boolean;
        columnType: "OttopilotEventContainer";
    }> => {
        return rows
            .map((row) => {
                const columnId = getColumnId(row.displayNames, row.fallbackColumnIds);
                if (columnId !== null) {
                    return {
                        id: columnId,
                        isEditable: row.isEditable,
                        label: row.label,
                        columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }

                if (row.fallbackSystemColumnId !== undefined) {
                    return {
                        id: `emotorad-inqueue-edit-system-${row.fallbackSystemColumnId}-${normalizeLabel(row.label)}` as Uuid,
                        isEditable: false,
                        label: row.label,
                        columnId: row.fallbackSystemColumnId,
                        isAdditionalColumn: false,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }

                return null;
            })
            .filter(
                (
                    item,
                ): item is {
                    id: Uuid;
                    isEditable: boolean;
                    label: string;
                    columnId: Uuid | string;
                    isAdditionalColumn: boolean;
                    columnType: "OttopilotEventContainer";
                } => item !== null,
            );
    };
    return {
        columnGrouping: [
            {
                id: uniqueUuid`emotorad-inqueue-edit-customer-details`,
                displayName: "Customer details",
                iconId: AnankePersonIcon,
                iconColor: "var(--ds-icon-accent-blue)",
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {label: "Customer Name", isEditable: true, displayNames: ["Customer Name", "customer name"]},
                    {
                        label: "Contact Number",
                        isEditable: false,
                        displayNames: ["Contact Number", "Contact number", "Customer Number", "Customer number"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID],
                    },
                    {label: "Pincode", isEditable: true, displayNames: ["Customer Pincode", "Pincode"]},
                ]),
            },
            {
                id: uniqueUuid`emotorad-inqueue-edit-product-details`,
                displayName: "Product details",
                iconId: AnankeModelVariantIcon,
                iconColor: "var(--ds-icon-accent-orange)",
                iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {
                        label: "Model Variant",
                        isEditable: true,
                        displayNames: ["Model Variant", "Model Varient", "Model"],
                    },
                ]),
            },
            {
                id: uniqueUuid`emotorad-inqueue-edit-showroom-details`,
                displayName: "Showroom Details",
                iconId: AnankeOfficeBuildingIcon,
                iconColor: "var(--ds-icon-accent-green)",
                iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {
                        label: "Lifecycle Owner",
                        isEditable: true,
                        displayNames: ["Lifecycle Owner", "Lead Owner", "Action Owner"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID],
                    },
                    {
                        label: "Assigned Dealer",
                        isEditable: true,
                        displayNames: ["Assigned Dealer", "Dealer Name", "Dealer"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID],
                    },
                    {
                        label: "KAM Name",
                        isEditable: false,
                        displayNames: ["KAM Name", "Kam Name"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID],
                    },
                    {
                        label: "RM Name",
                        isEditable: false,
                        displayNames: ["RM Name", "Rm Name", "RM name", "Rm name", "RM_Name", "ZSM Name", "Zsm Name"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID],
                    },
                    {
                        label: "Pincode",
                        isEditable: false,
                        displayNames: ["Dealer Pincode", "Dealer PinCode", "Dealer Pincode ", "Dealer Pin Code", "Dealer_Pincode"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID],
                    },
                    {
                        label: "City",
                        isEditable: false,
                        displayNames: ["Dealer City", "Dealer city", "City", "Dealer_City", "Dealer_City "],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID],
                    },
                    {
                        label: "State",
                        isEditable: false,
                        displayNames: ["Dealer State", "Dealer state", "Dealer Sate", "State", "Dealer_State", "Dealer_State "],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID],
                    },
                    {
                        label: "Zone",
                        isEditable: false,
                        displayNames: ["Dealer Zone", "Dealer zone", "Zone", "Dealer_Zone", "Dealer_Zone "],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID],
                    },
                ]),
            },
            {
                id: uniqueUuid`emotorad-inqueue-edit-activity-details`,
                displayName: "Activity Details",
                iconId: AnankeClockIcon,
                iconColor: "var(--ds-icon-accent-yellow)",
                iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {label: "Assigned on", isEditable: false, displayNames: ["Assigned On", "Assigned on"]},
                    {
                        label: "Modified on",
                        isEditable: false,
                        displayNames: ["Modified On", "Updated On"],
                        fallbackSystemColumnId: "updatedAt",
                    },
                    {
                        label: "Modified by",
                        isEditable: false,
                        displayNames: ["Modified By", "Updated By"],
                        fallbackSystemColumnId: "updatedBy",
                    },
                    {label: "Action Platform", isEditable: false, displayNames: ["Action Platform", "Platform"]},
                    {label: "Created on", isEditable: false, displayNames: ["Created On", "Created on"]},
                ]),
            },
            {
                id: uniqueUuid`emotorad-inqueue-edit-utm-details`,
                displayName: "UTM Parameters",
                iconId: AnankeLinkExternalIcon,
                iconColor: "var(--ds-icon-accent-purple)",
                iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {
                        label: "Utm Platform",
                        isEditable: false,
                        displayNames: ["Utm_platform", "utm_platform", "UTM Platform"],
                    },
                    {label: "Utm Source", isEditable: false, displayNames: ["Utm_source", "utm_source", "UTM Source"]},
                    {
                        label: "Utm Campaign",
                        isEditable: false,
                        displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign"],
                    },
                    {label: "Utm Term", isEditable: false, displayNames: ["Utm_term", "utm_term", "UTM Term"]},
                    {label: "Utm Medium", isEditable: false, displayNames: ["Utm_medium", "utm_medium", "UTM Medium"]},
                    {
                        label: "Utm Content",
                        isEditable: false,
                        displayNames: ["Utm_content", "utm_content", "UTM Content"],
                    },
                ]),
            },
        ],
    };
}

function buildEmotoradLifecycleEditPanelConfiguration(getAdditionalColumns: CapabilityUdoColumnResolver,
    journeysModuleConfiguration: JourneysModuleConfiguration,
): JourneysModuleConfiguration["configuration"]["eventContainersPage"]["editPanel"] {
    const existingEditPanel = journeysModuleConfiguration.configuration.eventContainersPage.editPanel;

    const availableColumnIds = new Set<string>(getAdditionalColumns(journeysModuleConfiguration).map((column) => column.id));
    const byDisplayName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
    const getColumnId = (displayNames: string[], fallbackColumnIds: string[] = []): Uuid | null => {
        for (const id of fallbackColumnIds) {
            if (availableColumnIds.has(id)) {
                return id as Uuid;
            }
        }
        for (const displayName of displayNames) {
            const id = byDisplayName.get(normalizeLabel(displayName));
            if (id !== undefined) {
                return id;
            }
        }
        return null;
    };
    const toSectionColumns = (
        rows: Array<{
            label: string;
            isEditable: boolean;
            displayNames: string[];
            fallbackColumnIds?: string[];
            fallbackSystemColumnId?: string;
        }>,
    ): Array<{
        id: Uuid;
        isEditable: boolean;
        label: string;
        columnId: Uuid | string;
        isAdditionalColumn: boolean;
        columnType: "OttopilotEventContainer";
    }> => {
        return rows
            .map((row) => {
                const columnId = getColumnId(row.displayNames, row.fallbackColumnIds);
                if (columnId !== null) {
                    return {
                        id: columnId,
                        isEditable: row.isEditable,
                        label: row.label,
                        columnId: columnId,
                        isAdditionalColumn: true as const,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }

                if (row.fallbackSystemColumnId !== undefined) {
                    return {
                        id: `emotorad-lifecycle-edit-system-${row.fallbackSystemColumnId}-${normalizeLabel(row.label)}` as Uuid,
                        isEditable: false,
                        label: row.label,
                        columnId: row.fallbackSystemColumnId,
                        isAdditionalColumn: false,
                        columnType: "OttopilotEventContainer" as const,
                    };
                }

                return null;
            })
            .filter(
                (
                    item,
                ): item is {
                    id: Uuid;
                    isEditable: boolean;
                    label: string;
                    columnId: Uuid | string;
                    isAdditionalColumn: boolean;
                    columnType: "OttopilotEventContainer";
                } => item !== null,
            );
    };

    return {
        columnGrouping: [
            {
                id: uniqueUuid`emotorad-lifecycle-edit-customer-details`,
                displayName: "Customer details",
                iconId: AnankePersonIcon,
                iconColor: "var(--ds-icon-accent-blue)",
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {label: "Customer Name", isEditable: true, displayNames: ["Customer Name", "customer name"]},
                    {
                        label: "Contact Number",
                        isEditable: false,
                        displayNames: ["Contact Number", "Contact number", "Customer Number", "Customer number", "Phone Number"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID],
                    },
                    {
                        label: "Pincode",
                        isEditable: true,
                        displayNames: ["Customer Pincode", "Pincode", "Customer_PinCode"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID],
                    },
                    {
                        label: "State",
                        isEditable: false,
                        displayNames: ["Customer State", "Customer state", "State"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID],
                    },
                    {
                        label: "City",
                        isEditable: false,
                        displayNames: ["Customer City", "Customer city", "City"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID],
                    },
                    {
                        label: "Zone",
                        isEditable: false,
                        displayNames: ["Customer Zone", "Customer zone", "Zone"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID],
                    },
                    {
                        label: "Source Channels",
                        isEditable: true,
                        displayNames: ["Source channel", "Source Channel", "Source Channels"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__SOURCE_CHANNEL__COLUMN_ID],
                    },
                    {
                        label: "Purchase Timeline",
                        isEditable: true,
                        displayNames: ["Purchase Timeline"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID],
                    },
                ]),
            },
            {
                id: uniqueUuid`emotorad-lifecycle-edit-product-details`,
                displayName: "Product details",
                iconId: AnankeModelVariantIcon,
                iconColor: "var(--ds-icon-accent-orange)",
                iconBackgroundColor: "var(--ds-background-accent-orange-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {
                        label: "Model",
                        isEditable: true,
                        displayNames: ["Model"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__MODEL__COLUMN_ID],
                    },
                    {
                        label: "Model Variant",
                        isEditable: true,
                        displayNames: ["Model Variant", "Model Varient"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID],
                    },
                ]),
            },
            {
                id: uniqueUuid`emotorad-lifecycle-edit-showroom-details`,
                displayName: "Showroom Details",
                iconId: AnankeOfficeBuildingIcon,
                iconColor: "var(--ds-icon-accent-green)",
                iconBackgroundColor: "var(--ds-background-accent-green-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {
                        label: "Lifecycle Owner",
                        isEditable: true,
                        displayNames: ["Lifecycle Owner", "Lead Owner", "Action Owner"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID],
                    },
                    {
                        label: "Assigned Dealer",
                        isEditable: false,
                        displayNames: ["Assigned Dealer", "Dealer Name", "Dealer"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID],
                    },
                    {
                        label: "KAM Name",
                        isEditable: false,
                        displayNames: ["KAM Name", "Kam Name"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID],
                    },
                    {
                        label: "RM Name",
                        isEditable: false,
                        displayNames: ["RM Name", "Rm Name", "RM name", "Rm name", "RM_Name", "ZSM Name", "Zsm Name"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID],
                    },
                    {
                        label: "Pincode",
                        isEditable: false,
                        displayNames: ["Dealer Pincode", "Dealer PinCode", "Dealer Pincode ", "Dealer Pin Code", "Dealer_Pincode"],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID],
                    },
                    {
                        label: "City",
                        isEditable: false,
                        displayNames: ["Dealer City", "Dealer city", "City", "Dealer_City", "Dealer_City "],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID],
                    },
                    {
                        label: "State",
                        isEditable: false,
                        displayNames: ["Dealer State", "Dealer state", "Dealer Sate", "State", "Dealer_State", "Dealer_State "],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID],
                    },
                    {
                        label: "Zone",
                        isEditable: false,
                        displayNames: ["Dealer Zone", "Dealer zone", "Zone", "Dealer_Zone", "Dealer_Zone "],
                        fallbackColumnIds: [EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID],
                    },
                ]),
            },
            {
                id: uniqueUuid`emotorad-lifecycle-edit-activity-details`,
                displayName: "Activity Details",
                iconId: AnankeClockIcon,
                iconColor: "var(--ds-icon-accent-yellow)",
                iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {label: "Assigned on", isEditable: false, displayNames: ["Assigned On", "Assigned on"]},
                    {
                        label: "Modified on",
                        isEditable: false,
                        displayNames: ["Modified On", "Updated On"],
                        fallbackSystemColumnId: "updatedAt",
                    },
                    {
                        label: "Modified by",
                        isEditable: false,
                        displayNames: ["Modified By", "Updated By"],
                        fallbackSystemColumnId: "updatedBy",
                    },
                    {label: "Action Platform", isEditable: false, displayNames: ["Action Platform", "Platform"]},
                    {label: "Created on", isEditable: false, displayNames: ["Created On", "Created on"]},
                ]),
            },
            {
                id: uniqueUuid`emotorad-lifecycle-edit-utm-details`,
                displayName: "UTM Parameters",
                iconId: AnankeLinkExternalIcon,
                iconColor: "var(--ds-icon-accent-purple)",
                iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                initialIsOpen: true,
                columns: toSectionColumns([
                    {
                        label: "Utm Platform",
                        isEditable: false,
                        displayNames: ["Utm_platform", "utm_platform", "UTM Platform"],
                    },
                    {label: "Utm Source", isEditable: false, displayNames: ["Utm_source", "utm_source", "UTM Source"]},
                    {
                        label: "Utm Campaign",
                        isEditable: false,
                        displayNames: ["Utm_campaign", "utm_campaign", "UTM Campaign"],
                    },
                    {label: "Utm Term", isEditable: false, displayNames: ["Utm_term", "utm_term", "UTM Term"]},
                    {label: "Utm Medium", isEditable: false, displayNames: ["Utm_medium", "utm_medium", "UTM Medium"]},
                    {
                        label: "Utm Content",
                        isEditable: false,
                        displayNames: ["Utm_content", "utm_content", "UTM Content"],
                    },
                ]),
            },
        ],
        globalConstraints: existingEditPanel?.globalConstraints,
    };
}

function resolveJourneyShowKanbanDispositionAction(args: {
    isEmotoradLifecycleOverviewView: boolean;
    emotoradLifecycleShowFooter: boolean;
    isRegistryLeadsView: boolean;
    registryLeadsShowFooter: boolean | undefined;
}): boolean {
    return (
        (args.isEmotoradLifecycleOverviewView === true && args.emotoradLifecycleShowFooter === true) ||
        (args.isRegistryLeadsView === true && args.registryLeadsShowFooter === true)
    );
}

export function JourneysRouteComponent({forcedView, dataSlice = "active"}: {forcedView?: "inqueue"; dataSlice?: ModuleDataSlice}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const {
        workspace,
        workspaceCapabilities,
        workspaceCapabilityAccessMatrix,
        userDefinedObjectOptionsByTemplateId,
    } = useWorkspace();
    const calendarOrchestrationEligibility = useMemo(
        () => resolveCalendarOrchestrationEligibility({workspaceCapabilities, workspaceCapabilityAccessMatrix}),
        [workspaceCapabilities, workspaceCapabilityAccessMatrix],
    );
    const search = useSearch({strict: false}) as {
        view?: "list" | "table" | "kanban" | "calendar" | "create";
        statusId?: string;
    };
    const journeysModuleConfiguration = useOnlyEventContainerTemplate();
    const getJourneyOrganizationalUnitId = useCallback(
        (journey: EventContainerWithEvents) => getOptionalEventContainerOrganizationalUnitId(journey, journeysModuleConfiguration),
        [journeysModuleConfiguration],
    );
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser} = useWorkspaceUser();
    const eventTemplates = useEventTemplates();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const platformUserDetails = usePlatformUserDetails();
    const btlActivities = useBtlActivities() as Array<BtlActivity>;
    const products = useProductCatalogue() ?? [];
    const productCatalogueTemplates = useProductCatalogueTemplates();
    const queryClient = useQueryClient();
    const [selectedDispositionJourney, setSelectedDispositionJourney] = useState<EventContainerWithEvents | null>(null);
    const openDispositionJourney = useCallback((journey: EventContainerWithEvents) => {
        setSelectedDispositionJourney((currentJourney) =>
            currentJourney?.id === journey.id ? currentJourney : journey,
        );
    }, []);
    const {openNotesPanel, closeNotesPanel, listNotesPanelChrome, notesPanelContext} = useListNotesPanel();
    const restoreJourneyTargetsRef = useRef<Array<EventContainerWithEvents> | null>(null);
    const [restoreJourneyTargets, setRestoreJourneyTargetsState] = useState<Array<EventContainerWithEvents> | null>(null);
    const setRestoreJourneyTargets = useCallback((targets: Array<EventContainerWithEvents> | null) => {
        restoreJourneyTargetsRef.current = targets;
        setRestoreJourneyTargetsState(targets);
    }, []);
    const [isRestoreJourneyModalOpen, setIsRestoreJourneyModalOpen] = useState(false);
    const [showRestoreSuccessToast, setShowRestoreSuccessToast] = useState(false);
    const tableViewColumnsById = useMemo(
        () => new Map((journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns ?? []).map((column) => [column.id, column])),
        [journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns],
    );
    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail])),
        [platformUserDetails],
    );

    const resolveTableColumnField = (columnId: string): string => {
        return tableViewColumnsById.get(columnId as Uuid)?.field ?? columnId;
    };

    const journeyAonConfigs: Array<AonWorkspaceConfig> = [emotoradJourneyAonConfig];

    const mapFilterConfigurationToFields = (filterConfiguration: FilterConfiguration): FilterConfiguration => ({
        ...filterConfiguration,
        lineItems: filterConfiguration.lineItems.flatMap((lineItem) => {
            let mappedLineItems: Array<FilterConfigurationLineItem> = [lineItem];
            for (const aonConfig of journeyAonConfigs) {
                mappedLineItems = mappedLineItems.flatMap((candidate) => mapAonFilterLineItemForApi(aonConfig, candidate));
            }
            return mappedLineItems.map((mappedLineItem) => {
                const isAnyAonRangesItem = journeyAonConfigs.some((aonConfig) => isAonFilterRangesLineItem(aonConfig, mappedLineItem));
                if (isAnyAonRangesItem === true) {
                    return mappedLineItem;
                }
                return {
                    ...mappedLineItem,
                    columnId: resolveTableColumnField(mappedLineItem.columnId),
                };
            });
        }),
    });

    const queryJourneyAonFilterValuesFromCreatedAt = async (): Promise<Array<string>> => {
        const journeysColumnValuesResult = await getEventContainerColumnValues({
            workspaceId: workspace.id,
            column: "createdAt",
            templateId: journeysModuleConfiguration.id,
            intellsysBaseUrl: intellsysBaseUrl,
            accessToken: heimdallUser.accessToken,
        });
        if (journeysColumnValuesResult.success !== true) {
            return [];
        }
        return buildDistinctAonFilterValuesFromCreatedAtColumnValues(
            compactEventContainerColumnValues(journeysColumnValuesResult.data ?? []),
        );
    };

    const buildJourneyAonCustomFilterForColumn = (config: AonWorkspaceConfig, aonDisplayColumnId: string) => {
        return buildAonCustomFilter(config, aonDisplayColumnId);
    };

    const mapSortConfigurationToFields = (sortConfiguration: SortConfiguration): SortConfiguration => ({
        ...sortConfiguration,
        lineItems: sortConfiguration.lineItems
            .filter(
                (lineItem) =>
                    !(isAmaronWorkspace(workspaceCapabilities) === true && isAmaronJourneyDispositionUiFilterColumn(lineItem.columnId) === true),
            )
            .map((lineItem) => ({
                ...lineItem,
                columnId: resolveTableColumnField(lineItem.columnId),
            })),
    });

    const getOuDisplayName = useMemo(() => {
        return (ouId: Uuid): string => {
            const ou = organizationalUnits.find((unit) => unit.id === ouId);
            if (ou === undefined) {
                return "-";
            }
            const template = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
            if (template === undefined) {
                return "-";
            }
            const nameColumn = template.additionalColumns.find((col) => col.displayName === "Name");
            if (nameColumn === undefined) {
                return "-";
            }
            const nameValue = ou.additionalColumnValues[nameColumn.id];
            return typeof nameValue === "string" && nameValue !== "" ? nameValue : "-";
        };
    }, [organizationalUnits, organizationalUnitTemplates]);

    const navigate = useNavigate();
    const {showFlag} = useAnankeFlags();
    const isMobile = useCheckMobile();
    const [moreActionsDrawerOpen, setMoreActionsDrawerOpen] = useState(false);
    const [exportDrawerOpen, setExportDrawerOpen] = useState(false);

    const activeView = forcedView ?? search.view ?? "table";
    const isInQueueView = activeView === "inqueue";
    const emotoradLifecycleListConfig = getEmotoradListModuleConfig("journeysLifecycleOverview");
    const emotoradInQueueListConfig = getEmotoradListModuleConfig("journeysInQueue");
    const amaronInQueueListConfig = getAmaronListModuleConfig("journeysInQueue");
    const workspaceListSupport = useMemo(
        () => resolveWorkspaceListSupport(workspace, workspaceCapabilities),
        [workspace, workspaceCapabilities],
    );
    const registryJourneysLeadsListConfig = resolveJourneysListConfig(workspaceListSupport, "leads");
    const isEmotoradJourneysCustomer = isEmotoradWorkspace(workspace) === true;
    const isEmotoradLifecycleOverviewView = isInQueueView !== true && isEmotoradJourneysCustomer === true;
    const isArchivedSlice = dataSlice === "archived";
    const journeyActivePageTitle = isEmotoradLifecycleOverviewView
        ? "Lifecycle Overview"
        : journeysModuleConfiguration.configuration.moduleDisplayNameVariants.pluralSentenceCase;
    const journeySliceConfig = useMemo(
        () =>
            resolveEventContainerModuleSliceConfig({
                slice: dataSlice,
                eventContainerTemplateConfiguration: journeysModuleConfiguration.configuration,
                activePageTitle: journeyActivePageTitle,
                activeRoutePath: `/w/${workspace.id}/ottopilot/event-containers`,
            }),
        [dataSlice, journeysModuleConfiguration, journeyActivePageTitle, workspace.id],
    );
    const showArchivedDataMenuItem = journeySliceConfig.capabilities.showArchivedDataMenuItem === true && isInQueueView !== true;
    const emotoradLifecycleKanbanColumnConfig = useMemo((): Array<KanbanBoardColumnConfig> => {
        return [
            {
                id: uniqueUuid`f2000001-0001-4001-8001-000000000001`,
                displayName: "Live",
                background: token("color.background.accent.blue.subtlest"),
                textColor: token("color.text.accent.blue"),
                headerBorderRadius: "4px 4px 0 0",
                headerBorderColor: token("color.border"),
                headerBorderWidth: "0.5px",
                possibleValues: [EmotoradEventContainerStatusEnum.LIVE],
            },
            {
                id: uniqueUuid`f2000001-0001-4001-8001-000000000002`,
                displayName: "Churned",
                background: token("color.background.accent.yellow.subtlest"),
                textColor: token("color.text.accent.yellow"),
                headerBorderRadius: "4px 4px 0 0",
                headerBorderColor: token("color.border"),
                headerBorderWidth: "0.5px",
                possibleValues: [EmotoradEventContainerStatusEnum.CHURNED],
            },
            {
                id: uniqueUuid`f2000001-0001-4001-8001-000000000003`,
                displayName: "Converted",
                background: token("color.background.accent.green.subtlest"),
                textColor: token("color.text.accent.green"),
                headerBorderRadius: "4px 4px 0 0",
                headerBorderColor: token("color.border"),
                headerBorderWidth: "0.5px",
                possibleValues: [EmotoradEventContainerStatusEnum.CONVERTED],
            },
            {
                id: uniqueUuid`f2000001-0001-4001-8001-000000000004`,
                displayName: emotoradEventContainerStatuses[EmotoradEventContainerStatusEnum.ARCHIVE].displayName,
                background: token("color.background.accent.red.subtlest"),
                textColor: token("color.text.danger"),
                headerBorderRadius: "4px 4px 0 0",
                headerBorderColor: token("color.border"),
                headerBorderWidth: "0.5px",
                possibleValues: [EmotoradEventContainerStatusEnum.ARCHIVE],
            },
        ];
    }, []);
    const journeyArchivedKanbanStatusIds = useMemo(
        () =>
            resolveEventContainerArchivedKanbanStatusIds(
                journeysModuleConfiguration.configuration,
                journeysModuleConfiguration.configuration.eventContainersPage.kanbanBoardPageView.columnConfig,
            ),
        [journeysModuleConfiguration],
    );
    const applyJourneyKanbanSliceAndStatusFilter = useCallback(
        (columnConfig: Array<KanbanBoardColumnConfig>) => {
            const forSlice = filterKanbanColumnsForDataSlice(columnConfig, dataSlice, journeyArchivedKanbanStatusIds);
            if (journeySliceConfig.ignoreStatusIdSearchParam === true) {
                return forSlice;
            }
            return filterKanbanColumnsForModuleStatusId(forSlice, search.statusId);
        },
        [dataSlice, journeyArchivedKanbanStatusIds, journeySliceConfig.ignoreStatusIdSearchParam, search.statusId],
    );
    const emotoradLifecycleKanbanColumnConfigForStatus = useMemo(
        () => applyJourneyKanbanSliceAndStatusFilter(emotoradLifecycleKanbanColumnConfig),
        [applyJourneyKanbanSliceAndStatusFilter, emotoradLifecycleKanbanColumnConfig],
    );
    const emotoradInQueueKanbanColumnConfig = useMemo(() => getEmotoradInQueueKanbanColumnConfig(), []);
    const emotoradInQueueKanbanColumnConfigForStatus = useMemo(
        () => applyJourneyKanbanSliceAndStatusFilter(emotoradInQueueKanbanColumnConfig),
        [applyJourneyKanbanSliceAndStatusFilter, emotoradInQueueKanbanColumnConfig],
    );
    const amaronLeadsKanbanColumnConfig = useMemo(() => getAmaronJourneyLeadsKanbanColumnConfig(), []);
    const amaronLeadsKanbanColumnConfigForStatus = useMemo(
        () => applyJourneyKanbanSliceAndStatusFilter(amaronLeadsKanbanColumnConfig),
        [applyJourneyKanbanSliceAndStatusFilter, amaronLeadsKanbanColumnConfig],
    );
    const duluxLeadsKanbanColumnConfig = useMemo(() => getDuluxJourneyLeadsKanbanColumnConfig(), []);
    const duluxLeadsKanbanColumnConfigForStatus = useMemo(
        () => applyJourneyKanbanSliceAndStatusFilter(duluxLeadsKanbanColumnConfig),
        [applyJourneyKanbanSliceAndStatusFilter, duluxLeadsKanbanColumnConfig],
    );
    const amaronInQueueKanbanColumnConfig = useMemo(() => getAmaronInQueueKanbanColumnConfig(), []);
    const amaronInQueueKanbanColumnConfigForStatus = useMemo(
        () => applyJourneyKanbanSliceAndStatusFilter(amaronInQueueKanbanColumnConfig),
        [applyJourneyKanbanSliceAndStatusFilter, amaronInQueueKanbanColumnConfig],
    );
    const journeysModuleKanbanColumnConfigForSlice = useMemo(
        () => applyJourneyKanbanSliceAndStatusFilter(journeysModuleConfiguration.configuration.eventContainersPage.kanbanBoardPageView.columnConfig),
        [applyJourneyKanbanSliceAndStatusFilter, journeysModuleConfiguration.configuration.eventContainersPage.kanbanBoardPageView.columnConfig],
    );
    const isBobcardOrAmaronLeadsView = (isBobcardWorkspace(workspace) === true || isAmaronWorkspace(workspaceCapabilities) === true) && isInQueueView !== true;
    const isRegistryLeadsView = workspaceListSupport !== null && registryJourneysLeadsListConfig !== null && isInQueueView !== true;
    const isEmotoradInQueueView = isEmotoradJourneysCustomer === true && isInQueueView === true;
    const isAmaronInQueueView = isAmaronWorkspace(workspaceCapabilities) === true && isInQueueView === true;
    /** Unique enquiries (`/journeys`) and InQueue (`/inqueue`) share the same side panel fields and styling. */
    const isEmotoradInQueueStyleJourneyPanel = isEmotoradJourneysCustomer === true;
    const isAmaronScaffoldStyleJourneyPanel = isAmaronWorkspace(workspaceCapabilities) === true;
    const isScaffoldStyleJourneyPanel =
        isEmotoradInQueueStyleJourneyPanel === true || isAmaronScaffoldStyleJourneyPanel === true;
    const isAmaronLeadsView = isRegistryLeadsView === true && isAmaronWorkspace(workspaceCapabilities) === true;
    const isDuluxLeadsView = isRegistryLeadsView === true && isDuluxWorkspace(workspaceCapabilities) === true;
    const useTwoColumnJourneyFieldGrid = isScaffoldStyleJourneyPanel === true || isDuluxLeadsView === true;
    const showKanbanDispositionAction = resolveJourneyShowKanbanDispositionAction({
        isEmotoradLifecycleOverviewView: isEmotoradLifecycleOverviewView,
        emotoradLifecycleShowFooter: emotoradLifecycleListConfig.showFooter,
        isRegistryLeadsView: isRegistryLeadsView,
        registryLeadsShowFooter: registryJourneysLeadsListConfig?.showFooter,
    });
    const initialJourneySortConfiguration = useMemo((): SortConfiguration => {
        if (isAmaronLeadsView === true) {
            return {
                revisionId: uniqueUuid`c8f4a2e1-9b3d-4f7a-a6e5-1d2c3b4a5e6f`,
                lineItems: [
                    {
                        columnId: AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID,
                        direction: SortDirection.ascending,
                    },
                ],
            };
        }
        return {
            revisionId: uniqueUuid`a3b2e64a-5040-4e0d-aabe-8451d0bd0ae4`,
            lineItems: [
                {
                    columnId: "updatedAt",
                    direction: SortDirection.descending,
                },
            ],
        };
    }, [isAmaronLeadsView]);
    const inQueueCalendarConfig = journeysModuleConfiguration.configuration.eventContainersPage.inQueueCalendarConfig;
    const inQueueCalendarLegendItems = useMemo(() => {
        if (isAmaronInQueueView === true) {
            return getAmaronCalendarLegendItems("inQueue");
        }
        if (inQueueCalendarConfig === undefined) {
            return [];
        }
        return resolveCalendarLegendItems(inQueueCalendarConfig, inQueueCalendarConfig);
    }, [inQueueCalendarConfig, isAmaronInQueueView]);
    const getInQueueCalendarTypeLabel = useMemo(() => {
        return (type: string) => {
            if (isAmaronInQueueView === true) {
                return resolveAmaronCalendarEventTypeLabel("inQueue", type);
            }
            if (inQueueCalendarConfig === undefined) {
                return type;
            }
            return resolveCalendarEventTypeLabel(type, inQueueCalendarConfig, inQueueCalendarConfig, type);
        };
    }, [inQueueCalendarConfig, isAmaronInQueueView]);
    const getInQueueCalendarTypeColor = useMemo(() => {
        if (isAmaronInQueueView === true) {
            return createAmaronCalendarGetEventColor("inQueue", (type) => {
                if (inQueueCalendarConfig === undefined) {
                    return {};
                }
                return resolveCalendarEventTypeColor(type, inQueueCalendarConfig, inQueueCalendarConfig);
            });
        }
        return (type: string | undefined) => {
            if (inQueueCalendarConfig === undefined) {
                return {};
            }
            return resolveCalendarEventTypeColor(type, inQueueCalendarConfig, inQueueCalendarConfig);
        };
    }, [inQueueCalendarConfig, isAmaronInQueueView]);
    const inQueueCalendarAllDayStyle = useMemo(() => {
        if (inQueueCalendarConfig === undefined) {
            return {mode: "fixedGreen"} as const;
        }
        return resolveCalendarAllDayStyle(inQueueCalendarConfig, inQueueCalendarConfig);
    }, [inQueueCalendarConfig]);
    const journeyCalendarConfig = journeysModuleConfiguration.configuration.eventContainersPage.eventContainerCalendarConfig;
    const journeysCalendarRowColumns = useMemo((): Array<string> | null => {
        const cfg =
            isInQueueView === true
                ? journeysModuleConfiguration.configuration.eventContainersPage.inQueueCalendarConfig
                : journeyCalendarConfig;
        if (cfg === undefined) {
            return null;
        }
        const columns = new Set<string>(["id", "createdAt", "updatedAt", "disposition", "status"]);
        if (cfg.calendarStartDateColumnId !== undefined) {
            columns.add(String(cfg.calendarStartDateColumnId));
        }
        const eventTypeColumnId = cfg.eventTypeSource?.columnId;
        if (
            eventTypeColumnId !== undefined &&
            eventTypeColumnId !== "status" &&
            eventTypeColumnId !== "disposition"
        ) {
            columns.add(String(eventTypeColumnId));
        }
        const titleTemplate = cfg.calendarTitleOttopilotStringTemplate;
        if (typeof titleTemplate === "string") {
            for (const match of titleTemplate.matchAll(
                /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi,
            )) {
                columns.add(match[0].toLowerCase());
            }
        }
        const leadOwnerColumnIdForCalendar =
            journeysModuleConfiguration.configuration.eventContainersPage.leadOwnerColumnId;
        if (isInQueueView === true && leadOwnerColumnIdForCalendar !== undefined) {
            columns.add(String(leadOwnerColumnIdForCalendar));
        }
        if (isAmaronWorkspace(workspaceCapabilities) === true) {
            columns.add(AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID);
            columns.add(AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID);
            columns.add(AMARON__EVENT_CONTAINER__DISPOSITION__COLUMN_ID);
            columns.add(AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID);
        }
        if (isDuluxWorkspace(workspaceCapabilities) === true) {
            columns.add(DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID);
        }
        return [...columns];
    }, [
        isInQueueView,
        journeyCalendarConfig,
        journeysModuleConfiguration.configuration.eventContainersPage.inQueueCalendarConfig,
        journeysModuleConfiguration.configuration.eventContainersPage.leadOwnerColumnId,
        workspaceCapabilities,
    ]);
    const journeyCalendarLegendItems = useMemo(() => {
        if (isAmaronLeadsView === true) {
            return getAmaronCalendarLegendItems("journeyLeads");
        }
        if (journeyCalendarConfig === undefined) {
            return [];
        }
        return resolveCalendarLegendItems(journeyCalendarConfig, journeyCalendarConfig);
    }, [journeyCalendarConfig, isAmaronLeadsView]);
    const getJourneyCalendarTypeLabel = useMemo(() => {
        return (type: string) => {
            if (isAmaronLeadsView === true) {
                return resolveAmaronCalendarEventTypeLabel("journeyLeads", type);
            }
            if (journeyCalendarConfig === undefined) {
                return type;
            }
            return resolveCalendarEventTypeLabel(type, journeyCalendarConfig, journeyCalendarConfig, type);
        };
    }, [journeyCalendarConfig, isAmaronLeadsView]);
    const getJourneyCalendarTypeColor = useMemo(() => {
        if (isAmaronLeadsView === true) {
            return createAmaronCalendarGetEventColor("journeyLeads", (type) => {
                if (journeyCalendarConfig === undefined) {
                    return {};
                }
                return resolveCalendarEventTypeColor(type, journeyCalendarConfig, journeyCalendarConfig);
            });
        }
        return (type: string | undefined) => {
            if (journeyCalendarConfig === undefined) {
                return {};
            }
            return resolveCalendarEventTypeColor(type, journeyCalendarConfig, journeyCalendarConfig);
        };
    }, [journeyCalendarConfig, isAmaronLeadsView]);
    const journeyCalendarAllDayStyle = useMemo(() => {
        if (journeyCalendarConfig === undefined) {
            return {mode: "fixedGreen"} as const;
        }
        return resolveCalendarAllDayStyle(journeyCalendarConfig, journeyCalendarConfig);
    }, [journeyCalendarConfig]);
    const journeyCalendarGetEventColorForView = useMemo(
        () => wrapGetEventColorForArchivedModuleSlice(getJourneyCalendarTypeColor, isArchivedSlice),
        [getJourneyCalendarTypeColor, isArchivedSlice],
    );
    const journeyCalendarAllDayStyleForView = useMemo(
        () => resolveCalendarAllDayStyleForModuleSlice(journeyCalendarAllDayStyle, isArchivedSlice),
        [journeyCalendarAllDayStyle, isArchivedSlice],
    );
    const selectedViewId =
        activeView === "list"
            ? JOURNEYS_LIST_VIEW_ID
            : activeView === "kanban"
              ? JOURNEYS_KANBAN_VIEW_ID
              : activeView === "calendar" && isEmotoradLifecycleOverviewView === true
                ? JOURNEYS_EMOTORAD_LIFECYCLE_CALENDAR_VIEW_ID
                : isInQueueView
                  ? JOURNEYS_INQUEUE_TABLE_VIEW_ID
                  : JOURNEYS_TABLE_VIEW_ID;
    const initialViewId = selectedViewId;
    const configuredJourneysSearchColumns = journeysModuleConfiguration.configuration.eventContainersPage.ottopilotDataViewerConfig?.searchColumns ?? [];
    const journeysSearchColumns = useMemo(() => {
        if (configuredJourneysSearchColumns.length > 0) {
            return configuredJourneysSearchColumns;
        }
        if (isDuluxWorkspace(workspaceCapabilities) === true) {
            return resolveDuluxJourneysSearchColumns(configuredJourneysSearchColumns);
        }
        /** Emotorad Lifecycle Overview / Amaron Leads (table + Kanban): module config often omits searchColumns; mirror In Queue fallback. */
        if (isEmotoradLifecycleOverviewView === true || isAmaronLeadsView === true) {
            const lifecycleOverviewFallbackSearchLabels = new Set([
                "customer name",
                "first name",
                "last name",
                "contact number",
                "lead owner",
                "lifecycle owner",
                "action owner",
                "application status",
                "utm_platform",
                "utm source",
                "utm_source",
                "utm medium",
                "utm_medium",
                "utm campaign",
                "utm_campaign",
                "utm term",
                "utm_term",
                "utm content",
                "utm_content",
            ]);
            return getAdditionalColumns(journeysModuleConfiguration)
                .filter((column) => lifecycleOverviewFallbackSearchLabels.has(normalizeLabel(column.displayName)))
                .map((column) => column.id);
        }
        if (isInQueueView !== true) {
            return configuredJourneysSearchColumns;
        }

        // In Queue (Emotorad / Amaron / others) should still expose search when searchColumns are not configured.
        const inQueueFallbackSearchLabels = new Set([
            "customer name",
            "first name",
            "last name",
            "contact number",
            "lead owner",
            "application status",
            "utm_platform",
            "utm source",
            "utm_source",
            "utm medium",
            "utm_medium",
            "utm campaign",
            "utm_campaign",
            "utm term",
            "utm_term",
            "utm content",
            "utm_content",
        ]);

        return getAdditionalColumns(journeysModuleConfiguration)
            .filter((column) => inQueueFallbackSearchLabels.has(normalizeLabel(column.displayName)))
            .map((column) => column.id);
    }, [getAdditionalColumns, configuredJourneysSearchColumns, isEmotoradLifecycleOverviewView, isAmaronLeadsView, isInQueueView, journeysModuleConfiguration, workspace]);
    const leadOwnerColumnId = journeysModuleConfiguration.configuration.eventContainersPage.leadOwnerColumnId;
    const journeysSearchPlaceholder = journeysModuleConfiguration.configuration.eventContainersPage.ottopilotDataViewerConfig?.searchPlaceholder;
    const journeysSearchPlaceholderResolved =
        journeysSearchPlaceholder
        ?? (isDuluxWorkspace(workspaceCapabilities) === true
            ? getDuluxJourneysSearchPlaceholder()
            : isEmotoradLifecycleOverviewView === true
              ? "Search by customer name, contact number…"
              : undefined);
    const journeysScopeFilterConfig = useMemo(() => {
        const configuredScopeFilter = journeysModuleConfiguration.configuration.eventContainersPage.ottopilotDataViewerConfig?.scopeFilter;
        if (configuredScopeFilter !== undefined) {
            return configuredScopeFilter;
        }
        if (isEmotoradJourneysCustomer !== true || leadOwnerColumnId === undefined) {
            return undefined;
        }
        return getEmotoradJourneysPageScopeFilterConfig(leadOwnerColumnId);
    }, [journeysModuleConfiguration.configuration.eventContainersPage.ottopilotDataViewerConfig?.scopeFilter, isEmotoradJourneysCustomer, leadOwnerColumnId]);
    const leadViewFilterLineItems = useMemo((): FilterConfiguration["lineItems"] => {
        if ((isBobcardWorkspace(workspace) !== true && isAmaronWorkspace(workspaceCapabilities) !== true) || leadOwnerColumnId === undefined) {
            return [];
        }
        return [{columnId: leadOwnerColumnId, operation: FilterOperation.notEquals, value: null}];
    }, [journeysModuleConfiguration, leadOwnerColumnId, workspace]);

    const emotoradLeadsDefaultVisibleFilterColumnIds = useMemo(() => {
        if (isEmotoradJourneysCustomer !== true) {
            return [];
        }
        if (isEmotoradLifecycleOverviewView !== true && isEmotoradInQueueView !== true) {
            return [];
        }
        return resolveEmotoradLeadsDefaultVisibleFilterColumnIds(
            EMOTORAD_LEADS_DEFAULT_VISIBLE_FILTER_SLOTS,
            journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns,
            getAdditionalColumns(journeysModuleConfiguration),
        );
    }, [getAdditionalColumns, isEmotoradJourneysCustomer, isEmotoradLifecycleOverviewView, isEmotoradInQueueView, journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns, journeysModuleConfiguration]);

    const inQueueVisibleColumns = useMemo<Array<string>>(() => {
        if (isEmotoradInQueueView === true) {
            return EMOTORAD_INQUEUE_TABLE_VISIBLE_COLUMN_IDS;
        }
        if (isAmaronInQueueView === true) {
            return AMARON_INQUEUE_TABLE_VISIBLE_COLUMN_IDS;
        }
        const orderedInQueueDisplayNames: Array<string> = [
            "Customer Name",
            "First Name",
            "Last Name",
            "Contact Number",
            "Lead Owner",
            "Application Status",
            "utm_platform",
            "utm_source",
            "utm_medium",
            "utm_campaign",
            "utm_term",
            "utm_content",
        ];
        const systemColumnIdByDisplayName = new Map<string, string>([
            ["Modified By", "updatedBy"],
            ["Modified On", "updatedAt"],
            ["Created On", "createdAt"],
        ]);
        const displayNameToColumnId = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [column.displayName, column.id]));
        return orderedInQueueDisplayNames
            .map((displayName) => displayNameToColumnId.get(displayName) ?? systemColumnIdByDisplayName.get(displayName))
            .filter((id): id is string => id !== undefined);
    }, [getAdditionalColumns, journeysModuleConfiguration, journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns, isAmaronInQueueView, isEmotoradInQueueView]);

    const emotoradInQueueLifecycleOwnerColumnId = useMemo(() => {
        if (isEmotoradWorkspace(workspace) !== true) {
            return null;
        }
        const lifecycleOwnerColumn = getAdditionalColumns(journeysModuleConfiguration).find((column) =>
            ["lifecycle owner", "lead owner", "action owner"].includes(normalizeLabel(column.displayName)),
        );
        return lifecycleOwnerColumn?.id ?? null;
    }, [getAdditionalColumns, journeysModuleConfiguration, workspace]);

    /**
     * Emotorad Lifecycle Overview: only journeys with a lifecycle owner and an OU (dealer assignment path).
     * Rows without a dealer within range stay in In Queue and are excluded here via API filters.
     */
    const emotoradLifecycleOverviewMandatoryJourneyApiFilterLineItems = useMemo((): FilterConfiguration["lineItems"] => {
        if (isEmotoradWorkspace(workspace) !== true || isEmotoradLifecycleOverviewView !== true) {
            return [];
        }
        const lineItems: FilterConfiguration["lineItems"] = [];
        if (emotoradInQueueLifecycleOwnerColumnId !== null) {
            lineItems.push({
                columnId: emotoradInQueueLifecycleOwnerColumnId,
                operation: FilterOperation.notEquals,
                value: null,
            });
        }
        lineItems.push({
            columnId: "ouId",
            operation: FilterOperation.notEquals,
            value: null,
        });
        return lineItems;
    }, [workspace, isEmotoradLifecycleOverviewView, emotoradInQueueLifecycleOwnerColumnId]);

    const inQueueAssignmentOwnerColumnId = useMemo(() => {
        if (isBobcardWorkspace(workspace) === true) {
            return leadOwnerColumnId ?? null;
        }
        if (isAmaronWorkspace(workspaceCapabilities) === true) {
            return leadOwnerColumnId ?? null;
        }
        if (isEmotoradWorkspace(workspace) === true) {
            return emotoradInQueueLifecycleOwnerColumnId;
        }
        return null;
    }, [workspace, leadOwnerColumnId, emotoradInQueueLifecycleOwnerColumnId]);

    const getInQueueAssignmentStatus = (journey: EventContainerWithEvents): "Assigned" | "Unassigned" => {
        if (inQueueAssignmentOwnerColumnId === null) {
            return "Unassigned";
        }
        const ownerRaw = journey.additionalColumnValues[inQueueAssignmentOwnerColumnId];
        const isAssigned = ownerRaw !== null && ownerRaw !== undefined && (typeof ownerRaw !== "string" || ownerRaw.trim() !== "");
        return isAssigned ? "Assigned" : "Unassigned";
    };

    const getInQueueCalendarEventType = (journey: EventContainerWithEvents): string | undefined => {
        if (inQueueCalendarConfig === undefined) {
            return getInQueueAssignmentStatus(journey).toLowerCase();
        }
        const source = inQueueCalendarConfig.eventTypeSource;
        if (source !== undefined) {
            if (source.columnId === "status") {
                return String(journey.status);
            }
            const value = journey.additionalColumnValues[source.columnId as Uuid];
            if (value === undefined || value === null) {
                return undefined;
            }
            return String(value);
        }
        return getInQueueAssignmentStatus(journey).toLowerCase();
    };

    const getJourneyLifecycleCalendarEventType = (journey: EventContainerWithEvents): string | undefined => {
        if (journeyCalendarConfig === undefined) {
            return undefined;
        }
        const source = journeyCalendarConfig.eventTypeSource;
        if (source !== undefined) {
            if (source.columnId === "status") {
                return String(journey.status);
            }
            const value = journey.additionalColumnValues[source.columnId as Uuid];
            if (value === undefined || value === null) {
                return undefined;
            }
            return String(value);
        }
        return getInQueueAssignmentStatus(journey).toLowerCase();
    };

    const emotoradInQueueFieldIds = useMemo(() => {
        const availableColumnIds = new Set(getAdditionalColumns(journeysModuleConfiguration).map((column) => column.id));
        const byName = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [normalizeLabel(column.displayName), column.id]));
        const getByNames = (names: string[], fallbackColumnIds: string[] = []): Uuid | null => {
            for (const id of fallbackColumnIds) {
                if (availableColumnIds.has(id as Uuid)) {
                    return id as Uuid;
                }
            }
            for (const name of names) {
                const id = byName.get(normalizeLabel(name));
                if (id !== undefined) {
                    return id;
                }
            }
            return null;
        };
        return {
            customerName: getByNames(["Customer Name"]),
            pincode: getByNames(["Customer Pincode", "Pincode"]),
            modelVariant: getByNames(["Model Variant"]),
            kamName: getByNames(["KAM Name"], [EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID]),
            assignedDealer: getByNames(["Assigned Dealer", "Dealer Name"], [EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID]),
        };
    }, [getAdditionalColumns, journeysModuleConfiguration]);

    const getJourneyAdditionalDisplayValue = (journey: EventContainerWithEvents, columnId: Uuid | null): string => {
        if (columnId === null) {
            return "-";
        }
        const rawValue = journey.additionalColumnValues[columnId];
        if (rawValue === null || rawValue === undefined || rawValue === "") {
            return "-";
        }
        const configuredColumn = getAdditionalColumns(journeysModuleConfiguration).find((column) => column.id === columnId);
        if (configuredColumn === undefined) {
            return String(rawValue);
        }
        // In some emotorad workspaces "Assigned Dealer" can carry an OU UUID.
        // Resolve it to OU display name instead of showing raw UUID on kanban cards.
        const isAssignedDealerColumn =
            configuredColumn.id === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID ||
            ["assigned dealer", "dealer name", "dealer"].includes(normalizeLabel(configuredColumn.displayName));
        if (isAssignedDealerColumn === true && typeof rawValue === "string") {
            const ou = organizationalUnits.find((unit) => unit.id === rawValue);
            if (ou !== undefined) {
                const ouTemplate = organizationalUnitTemplates.find((template) => template.id === ou.templateId);
                if (ouTemplate !== undefined) {
                    return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
                }
            }
        }
        return getUserDefinedObjectAdditionalColumnDetails(configuredColumn).stringValueGetter({
            value: rawValue as never,
            users: users,
            userTemplates: userTemplates,
            organizationalUnits: organizationalUnits,
            organizationalUnitTemplates: organizationalUnitTemplates,
            products: products,
        });
    };

    const openInQueueKanbanViewPanelRef = useRef<(journey: EventContainerWithEvents) => void>(() => {});
    /** Skip baseline filter reset on first mount so localStorage-persisted filters survive refresh. */
    const journeysFilterBaselineScopeRef = useRef<string | null>(null);

    const renderEmotoradInQueueKanbanCard = (journey: EventContainerWithEvents, rowActionsConfig: TableRowActionsConfig<EventContainerWithEvents>) => {
        const customerName = getJourneyAdditionalDisplayValue(journey, emotoradInQueueFieldIds.customerName);
        const pincode = getJourneyAdditionalDisplayValue(journey, emotoradInQueueFieldIds.pincode);
        const modelVariant = getJourneyAdditionalDisplayValue(journey, emotoradInQueueFieldIds.modelVariant);
        const kamName = getJourneyAdditionalDisplayValue(journey, emotoradInQueueFieldIds.kamName);
        const assignedDealer = getJourneyAdditionalDisplayValue(journey, emotoradInQueueFieldIds.assignedDealer);
        const dateAndTime = formatDateAndTime(journey.updatedAt);
        return (
            <div
                className="rounded-[4px] border p-ds-space-200"
                style={{
                    borderColor: token("color.border"),
                    backgroundColor: token("color.background.input"),
                }}
            >
                <div className="grid grid-cols-[minmax(0,1fr)_max-content] items-center gap-ds-space-100">
                    <div className="max-w-[170px] text-[1rem] font-ds-font-weight-bold leading-ds-space-300 truncate">{customerName}</div>
                    <KanbanCardRowActions
                        row={journey}
                        config={rowActionsConfig}
                        phoneDial={null}
                    />
                </div>
                <div className="mt-ds-space-100 text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 text-ds-text-subtle">
                    {`${dateAndTime.date}, ${dateAndTime.time}`}
                </div>
                <div
                    className="mt-ds-space-150 h-px"
                    style={{background: token("color.border")}}
                />
                <div className="mt-ds-space-150 grid grid-cols-2 gap-x-ds-space-250 gap-y-ds-space-150">
                    <div>
                        <div className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 text-ds-text-subtlest grid grid-cols-[max-content_max-content] items-center gap-1">
                            <LocationIcon
                                label=""
                                color={token("color.icon.subtle")}
                            />
                            <span>Pincode</span>
                        </div>
                        <div className="grid grid-cols-[max-content_1fr] items-center gap-1">
                            <div className="invisible">
                                <LocationIcon
                                    label=""
                                    color={token("color.icon.subtle")}
                                />
                            </div>
                            <div className="text-[0.875rem] leading-ds-space-250 text-ds-text">{pincode}</div>
                        </div>
                    </div>
                    <div>
                        <div className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 text-ds-text-subtlest grid grid-cols-[max-content_max-content] items-center gap-1">
                            <ModelVariantIcon
                                className="w-4 h-4"
                                style={{color: token("color.icon.subtle")}}
                            />
                            <span>Model Variant</span>
                        </div>
                        <div className="grid grid-cols-[max-content_1fr] items-center gap-1">
                            <div className="invisible">
                                <ModelVariantIcon
                                    className="w-4 h-4"
                                    style={{color: token("color.icon.subtle")}}
                                />
                            </div>
                            <div className="text-[0.875rem] leading-ds-space-250 text-ds-text">{modelVariant}</div>
                        </div>
                    </div>
                    <div>
                        <div className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 text-ds-text-subtlest grid grid-cols-[max-content_max-content] items-center gap-1">
                            <PeopleGroupIcon
                                label=""
                                color={token("color.icon.subtle")}
                            />
                            <span>KAM Name</span>
                        </div>
                        <div className="grid grid-cols-[max-content_1fr] items-center gap-1">
                            <div className="invisible">
                                <PeopleGroupIcon
                                    label=""
                                    color={token("color.icon.subtle")}
                                />
                            </div>
                            <div className="text-[0.875rem] leading-ds-space-250 text-ds-text">{kamName}</div>
                        </div>
                    </div>
                    <div>
                        <div className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 text-ds-text-subtlest grid grid-cols-[max-content_max-content] items-center gap-1">
                            <OfficeBuildingIcon
                                label=""
                                color={token("color.icon.subtle")}
                            />
                            <span>Assigned Dealer</span>
                        </div>
                        <div className="grid grid-cols-[max-content_1fr] items-center gap-1">
                            <div className="invisible">
                                <OfficeBuildingIcon
                                    label=""
                                    color={token("color.icon.subtle")}
                                />
                            </div>
                            <div className="text-[0.875rem] leading-ds-space-250 text-ds-text">{assignedDealer}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const emotoradInQueueStatusColumnId = useMemo(() => {
        if (isInQueueView !== true || isEmotoradInQueueView !== true) {
            return null;
        }
        const inQueueStatusLabels = new Set(["status", "lifecycle status"]);
        const statusColumn = getAdditionalColumns(journeysModuleConfiguration).find((column) => inQueueStatusLabels.has(normalizeLabel(column.displayName)));
        return statusColumn?.id ?? null;
    }, [getAdditionalColumns, journeysModuleConfiguration, isEmotoradInQueueView, isInQueueView]);

    const emotoradInQueueStatusFilterColumnIds = useMemo(() => {
        if (isEmotoradInQueueView !== true) {
            return new Set<string>();
        }
        return new Set(["status", emotoradInQueueStatusColumnId, EMOTORAD_INQUEUE_STATUS_FIELD].filter((id): id is string => id !== null && id !== undefined));
    }, [isEmotoradInQueueView, emotoradInQueueStatusColumnId]);

    const isJourneyAssignedForEmotoradInQueue = (journey: EventContainerWithEvents): boolean => {
        if (emotoradInQueueLifecycleOwnerColumnId === null) {
            return false;
        }
        const ownerRaw = journey.additionalColumnValues[emotoradInQueueLifecycleOwnerColumnId];
        return ownerRaw !== null && ownerRaw !== undefined && (typeof ownerRaw !== "string" || ownerRaw.trim() !== "");
    };

    const bobcardStatusFilterColumnIds = useMemo(() => {
        if (isBobcardWorkspace(workspace) !== true) {
            return new Set<string>();
        }
        if (isInQueueView === true) {
            return new Set([BOBCARD__EVENT_CONTAINER__STATUS__COLUMN_ID, BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID]);
        }
        return new Set([BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID]);
    }, [workspace, isInQueueView]);

    const amaronInQueueStatusFilterColumnIds = useMemo(() => {
        if (isAmaronInQueueView !== true) {
            return new Set<string>();
        }
        return new Set(["status", AMARON_INQUEUE_STATUS_FIELD, AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID]);
    }, [isAmaronInQueueView]);

    const inQueueUiStatusFilterColumnIds = useMemo(() => {
        if (isInQueueView !== true) {
            return new Set<string>();
        }
        const ids = new Set<string>();
        for (const columnId of emotoradInQueueStatusFilterColumnIds) {
            ids.add(columnId);
        }
        for (const columnId of amaronInQueueStatusFilterColumnIds) {
            ids.add(columnId);
        }
        for (const columnId of bobcardStatusFilterColumnIds) {
            ids.add(columnId);
        }
        return ids;
    }, [amaronInQueueStatusFilterColumnIds, bobcardStatusFilterColumnIds, emotoradInQueueStatusFilterColumnIds, isInQueueView]);

    const inQueueAssignmentValidStatusIds = useMemo(
        () => new Set(["assigned", "unassigned", LIST_INQUEUE_ASSIGNMENT_STATUS.assigned, LIST_INQUEUE_ASSIGNMENT_STATUS.unassigned]),
        [],
    );

    const doesFrontendCustomerNameMatchFilter = (journey: EventContainerWithEvents, lineItem: FilterConfiguration["lineItems"][number]): boolean => {
        const firstName = journey.additionalColumnValues[BOBCARD__EVENT_CONTAINER__FIRST_NAME__COLUMN_ID];
        const lastName = journey.additionalColumnValues[BOBCARD__EVENT_CONTAINER__LAST_NAME__COLUMN_ID];
        const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim().toLowerCase();
        const filterValue =
            "value" in lineItem
                ? String(lineItem.value ?? "")
                      .trim()
                      .toLowerCase()
                : "";
        switch (lineItem.operation) {
            case FilterOperation.equals:
                return fullName === filterValue;
            case FilterOperation.notEquals:
                return fullName !== filterValue;
            case FilterOperation.contains:
                return fullName.includes(filterValue);
            case FilterOperation.notContains:
                return !fullName.includes(filterValue);
            case FilterOperation.startsWith:
                return fullName.startsWith(filterValue);
            case FilterOperation.endsWith:
                return fullName.endsWith(filterValue);
            case FilterOperation.in:
                return (lineItem.values ?? []).map((v) => v.trim().toLowerCase()).includes(fullName);
            default:
                return true;
        }
    };

    const doesFrontendBobcardApplicationStatusMatchFilter = (journey: EventContainerWithEvents, lineItem: FilterConfiguration["lineItems"][number]): boolean => {
        const rawValue = journey.additionalColumnValues[BOBCARD__EVENT_CONTAINER__STATUS__COLUMN_ID];
        const status = Object.values(bobcardApplicationStatuses).find((s) => s.id === rawValue);
        const displayValue = (status?.displayName ?? "").trim().toLowerCase();
        const filterValue = lineItem.operation === FilterOperation.in ? "" : String(lineItem.value).trim().toLowerCase();
        switch (lineItem.operation) {
            case FilterOperation.equals:
                return displayValue === filterValue;
            case FilterOperation.notEquals:
                return displayValue !== filterValue;
            case FilterOperation.contains:
                return displayValue.includes(filterValue);
            case FilterOperation.notContains:
                return !displayValue.includes(filterValue);
            case FilterOperation.startsWith:
                return displayValue.startsWith(filterValue);
            case FilterOperation.endsWith:
                return displayValue.endsWith(filterValue);
            case FilterOperation.in:
                return (lineItem.values ?? []).map((v) => v.trim().toLowerCase()).includes(displayValue);
            default:
                return true;
        }
    };

    const resolveEnumFilterValue = (columnId: string, value: string): string => {
        const normalizedValue = value.trim().toLowerCase();
        const resolvedFieldColumnId = resolveTableColumnField(columnId);
        const additionalColumn = getAdditionalColumns(journeysModuleConfiguration).find((column) => String(column.id) === String(resolvedFieldColumnId));
        if (
            additionalColumn !== undefined &&
            (additionalColumn.type === UdoColumnType.userDefinedObject ||
                additionalColumn.type === UdoColumnType.userDefinedObjects)
        ) {
            const option = getAdditionalColumnOptions(additionalColumn, userDefinedObjectOptionsByTemplateId)?.find(
                (candidate) => candidate.displayName.trim().toLowerCase() === normalizedValue || candidate.id.trim().toLowerCase() === normalizedValue,
            );
            if (option !== undefined) {
                return option.id;
            }
        }

        if (isBobcardWorkspace(workspace) === true && columnId === BOBCARD__EVENT_CONTAINER__STATUS__COLUMN_ID && isInQueueView !== true) {
            const status = Object.values(bobcardApplicationStatuses).find(
                (candidate) => candidate.displayName.trim().toLowerCase() === normalizedValue || candidate.id.trim().toLowerCase() === normalizedValue,
            );
            if (status !== undefined) {
                return status.id;
            }
        }

        return value;
    };

    const normalizeEnumFilterLineItem = (lineItem: FilterConfiguration["lineItems"][number]): FilterConfiguration["lineItems"][number] => {
        if (lineItem.operation === FilterOperation.in) {
            return {
                ...lineItem,
                values: lineItem.values.map((value) => resolveEnumFilterValue(lineItem.columnId, value)),
            };
        }
        if (typeof lineItem.value === "string") {
            return {
                ...lineItem,
                value: resolveEnumFilterValue(lineItem.columnId, lineItem.value),
            };
        }
        return lineItem;
    };

    const journeyVisibleColumns = useMemo(() => {
        if (isEmotoradLifecycleOverviewView === true) {
            return EMOTORAD_JOURNEY_TABLE_VISIBLE_COLUMN_IDS;
        }
        if (isRegistryLeadsView === true && workspaceListSupport !== null) {
            return [...workspaceListSupport.journeyLeadsTableVisibleColumnIds];
        }
        if (isBobcardWorkspace(workspace) === true) {
            return BOBCARD_JOURNEY_TABLE_VISIBLE_COLUMN_IDS;
        }
        const orderedJourneyDisplayNames = [
            "First Name",
            "Last Name",
            "Date of Birth",
            "Application ID",
            "Alias ID",
            "Contact Number",
            "Application Status",
            "Application Stage",
            "Rejection Stage",
            "Employment Status",
            "Net Monthly Income / Net Annual",
            "Current Pincode",
            "Gender",
            "Marital Status",
            "Email Address",
            "PAN Number",
            "Company Name",
            "Company Address",
            "Permanent Address",
            "Current Address",
            "PAN Validation",
            "Credit History",
            "Credit Score",
            "Aadhar Number",
            "Aadhar Verification",
            "Network Selected",
            "E-Consent",
            "V-KYC Successful",
            "UPI Linkage",
            "Interested Card Variant",
            "Selected Card Variant",
            "Remarks (Recent)",
            "Lead Count",
            "Last Activity",
            "Last Activity On",
            "Lead Owner",
            "Applications Initiated Count",
            "Calls Count",
            "Assigned On",
            "utm_platform",
            "utm_source",
            "utm_medium",
            "utm_campaign",
            "utm_term",
            "utm_content",
            "Customer Name",
        ];
        const displayNameToColumnId = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [column.displayName, column.id]));
        const mapped = orderedJourneyDisplayNames.map((displayName) => displayNameToColumnId.get(displayName)).filter((id): id is Uuid => id !== undefined);
        return [...mapped, "disposition", "updatedBy", "updatedAt"];
    }, [getAdditionalColumns, journeysModuleConfiguration, isRegistryLeadsView, isEmotoradLifecycleOverviewView, workspace, workspaceListSupport]);

    const initialVisibleFilterColumnIds = useMemo<Array<string>>(() => {
        if ((isEmotoradLifecycleOverviewView === true || isEmotoradInQueueView === true) && emotoradLeadsDefaultVisibleFilterColumnIds.length > 0) {
            return emotoradLeadsDefaultVisibleFilterColumnIds;
        }
        if (isAmaronWorkspace(workspaceCapabilities) === true) {
            return [...AMARON_DEFAULT_VISIBLE_FILTER_COLUMN_IDS];
        }
        if (isBobcardWorkspace(workspace) !== true) {
            return journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns !== undefined
                ? (() => {
                      const first = journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns?.[0];
                      return first !== undefined ? [first.id] : [];
                  })()
                : (() => {
                      const first = getAdditionalColumns(journeysModuleConfiguration)[0];
                      return first !== undefined ? [first.id] : [];
                  })();
        }
        if (isInQueueView) {
            return [];
        }
        const displayNameToColumnId = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [column.displayName, column.id]));
        return [displayNameToColumnId.get("Customer Name"), displayNameToColumnId.get("Application Status"), displayNameToColumnId.get("Lead Owner")].filter(
            (columnId): columnId is Uuid => columnId !== undefined,
        );
    }, [getAdditionalColumns, journeysModuleConfiguration, journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns, isInQueueView, workspace, workspaceCapabilities, isEmotoradLifecycleOverviewView, isEmotoradInQueueView, emotoradLeadsDefaultVisibleFilterColumnIds]);
    const effectiveJourneysModuleConfigurationForPanel = useMemo(() => {
        if (isScaffoldStyleJourneyPanel !== true) {
            return journeysModuleConfiguration;
        }
        if (isAmaronScaffoldStyleJourneyPanel === true) {
            return {
                ...journeysModuleConfiguration,
                configuration: {
                    ...journeysModuleConfiguration.configuration,
                    eventContainersPage: {
                        ...journeysModuleConfiguration.configuration.eventContainersPage,
                        viewPanel:
                            isAmaronInQueueView === true
                                ? buildAmaronInQueueViewPanelConfiguration(getAdditionalColumns, journeysModuleConfiguration)
                                : buildAmaronLeadsViewPanelConfiguration(getAdditionalColumns, journeysModuleConfiguration),
                    },
                },
            };
        }
        if (isEmotoradLifecycleOverviewView === true) {
            return {
                ...journeysModuleConfiguration,
                configuration: {
                    ...journeysModuleConfiguration.configuration,
                    eventContainersPage: {
                        ...journeysModuleConfiguration.configuration.eventContainersPage,
                        viewPanel: buildEmotoradLifecycleOverviewViewPanelConfiguration(getAdditionalColumns, journeysModuleConfiguration),
                    },
                },
            };
        }
        return {
            ...journeysModuleConfiguration,
            configuration: {
                ...journeysModuleConfiguration.configuration,
                eventContainersPage: {
                    ...journeysModuleConfiguration.configuration.eventContainersPage,
                    viewPanel: buildEmotoradInQueueViewPanelConfiguration(getAdditionalColumns, journeysModuleConfiguration),
                },
            },
        };
    }, [getAdditionalColumns, journeysModuleConfiguration, isScaffoldStyleJourneyPanel, isAmaronScaffoldStyleJourneyPanel, isAmaronInQueueView, isEmotoradLifecycleOverviewView]);
    const effectiveJourneysModuleConfigurationForEditPanel = useMemo(() => {
        if (isScaffoldStyleJourneyPanel !== true) {
            return journeysModuleConfiguration;
        }
        if (isAmaronScaffoldStyleJourneyPanel === true) {
            if (isAmaronInQueueView === true) {
                return {
                    ...journeysModuleConfiguration,
                    configuration: {
                        ...journeysModuleConfiguration.configuration,
                        eventContainersPage: {
                            ...journeysModuleConfiguration.configuration.eventContainersPage,
                            editPanel: buildAmaronInQueueEditPanelConfiguration(getAdditionalColumns, journeysModuleConfiguration),
                        },
                    },
                };
            }
            const amaronLeadsEditPanelConfig = buildAmaronLeadsEditPanelConfiguration(getAdditionalColumns, journeysModuleConfiguration);
            if (amaronLeadsEditPanelConfig !== undefined) {
                return {
                    ...journeysModuleConfiguration,
                    configuration: {
                        ...journeysModuleConfiguration.configuration,
                        eventContainersPage: {
                            ...journeysModuleConfiguration.configuration.eventContainersPage,
                            editPanel: amaronLeadsEditPanelConfig,
                        },
                    },
                };
            }
            return journeysModuleConfiguration;
        }
        if (isEmotoradInQueueView === true) {
            return {
                ...journeysModuleConfiguration,
                configuration: {
                    ...journeysModuleConfiguration.configuration,
                    eventContainersPage: {
                        ...journeysModuleConfiguration.configuration.eventContainersPage,
                        editPanel: buildEmotoradInQueueEditPanelConfiguration(getAdditionalColumns, journeysModuleConfiguration),
                    },
                },
            };
        }
        if (isEmotoradLifecycleOverviewView === true) {
            return {
                ...journeysModuleConfiguration,
                configuration: {
                    ...journeysModuleConfiguration.configuration,
                    eventContainersPage: {
                        ...journeysModuleConfiguration.configuration.eventContainersPage,
                        editPanel: buildEmotoradLifecycleEditPanelConfiguration(getAdditionalColumns, journeysModuleConfiguration),
                    },
                },
            };
        }
        return {
            ...journeysModuleConfiguration,
            configuration: {
                ...journeysModuleConfiguration.configuration,
                eventContainersPage: {
                    ...journeysModuleConfiguration.configuration.eventContainersPage,
                    editPanel: buildEmotoradInQueueEditPanelConfiguration(getAdditionalColumns, journeysModuleConfiguration),
                },
            },
        };
    }, [getAdditionalColumns,
        journeysModuleConfiguration,
        isScaffoldStyleJourneyPanel,
        isAmaronScaffoldStyleJourneyPanel,
        isAmaronInQueueView,
        isEmotoradInQueueView,
        isEmotoradLifecycleOverviewView,
    ]);

    const hideJourneysViewPanelEditFooter = useMemo(() => {
        if (isAmaronLeadsView === true && isArchivedSlice !== true) {
            return effectiveJourneysModuleConfigurationForEditPanel.configuration.eventContainersPage.editPanel === undefined;
        }
        if (isAmaronInQueueView === true) {
            return effectiveJourneysModuleConfigurationForEditPanel.configuration.eventContainersPage.editPanel === undefined;
        }
        return true;
    }, [effectiveJourneysModuleConfigurationForEditPanel, isAmaronInQueueView, isAmaronLeadsView, isArchivedSlice]);

    const journeyCalendarRangeFieldIdResolved = (() => {
        const cfg =
            isInQueueView === true
                ? journeysModuleConfiguration.configuration.eventContainersPage.inQueueCalendarConfig
                : journeysModuleConfiguration.configuration.eventContainersPage.eventContainerCalendarConfig;
        if (cfg?.calendarStartDateColumnId === undefined) {
            return undefined;
        }
        return resolveTableColumnField(cfg.calendarStartDateColumnId);
    })();

    const amaronJourneyCalendarDisplayMetrics = useMemo(
        () =>
            resolveAmaronJourneyCalendarDisplayMetrics(workspaceCapabilities, {
                isInQueueView: isInQueueView === true,
                statusId: search.statusId,
            }),
        [isInQueueView, search.statusId, workspaceCapabilities],
    );

    const journeysModuleDisplayNameVariants = journeysModuleConfiguration.configuration.moduleDisplayNameVariants;
    const journeysRowDisplayNameVariants = {
        singularSentenceCase: isInQueueView
            ? "In Queue"
            : isEmotoradLifecycleOverviewView
              ? "Lifecycle Overview"
              : journeysModuleDisplayNameVariants.singularSentenceCase,
        pluralSentenceCase: isInQueueView
            ? "In Queue"
            : isEmotoradLifecycleOverviewView
              ? "Lifecycle Overview"
              : journeysModuleDisplayNameVariants.pluralSentenceCase,
        singularLowerCase: isInQueueView
            ? "in queue"
            : isEmotoradLifecycleOverviewView
              ? "lifecycle overview"
              : journeysModuleDisplayNameVariants.singularLowerCase,
        pluralLowerCase: isInQueueView
            ? "in queue"
            : isEmotoradLifecycleOverviewView
              ? "lifecycle overview"
              : journeysModuleDisplayNameVariants.pluralLowerCase,
    } as const;

    const journeysDataViewerControllerRef = useRef<OttopilotDataViewerController<EventContainerWithEvents> | null>(null);

    const baseWorkspaceMobileNavigationConfiguration = useRequiredWorkspaceMobileNavigationConfiguration();
    const workspaceMobileNavigationConfiguration = useMemo(
        () =>
            resolveWorkspaceListCreateItemsConfiguration({
                workspace,
                configuration: baseWorkspaceMobileNavigationConfiguration,
            }),
        [workspace, baseWorkspaceMobileNavigationConfiguration],
    );
    const listCreateDrawerItems = useMemo(() => {
        if (isRegistryLeadsView === true && workspaceListSupport !== null) {
            if (registryJourneysLeadsListConfig?.includeEventCreateDrawer !== true) {
                return [] as const;
            }
            return workspaceListSupport.getListCreateItems({workspaceMobileNavigationConfiguration});
        }
        if (isEmotoradLifecycleOverviewView !== true) {
            return [] as const;
        }
        if (emotoradLifecycleListConfig.includeEventCreateDrawer !== true) {
            return [] as const;
        }
        return getEmotoradListEventCreateItems(workspaceMobileNavigationConfiguration);
    }, [
        isRegistryLeadsView,
        registryJourneysLeadsListConfig?.includeEventCreateDrawer,
        workspaceListSupport,
        isEmotoradLifecycleOverviewView,
        workspaceMobileNavigationConfiguration,
        emotoradLifecycleListConfig.includeEventCreateDrawer,
    ]);

    const activeJourneyListModuleConfig = useMemo(() => {
        if (isInQueueView === true) {
            return (
                resolveJourneysListConfig(workspaceListSupport, "inQueue") ??
                (isEmotoradWorkspace(workspace) === true ? emotoradInQueueListConfig : isAmaronWorkspace(workspaceCapabilities) === true ? amaronInQueueListConfig : null)
            );
        }
        if (isRegistryLeadsView === true) {
            return registryJourneysLeadsListConfig;
        }
        if (isEmotoradLifecycleOverviewView === true) {
            return emotoradLifecycleListConfig;
        }
        return null;
    }, [
        isInQueueView,
        workspaceListSupport,
        workspace,
        registryJourneysLeadsListConfig,
        isRegistryLeadsView,
        isEmotoradLifecycleOverviewView,
        emotoradInQueueListConfig,
        amaronInQueueListConfig,
        emotoradLifecycleListConfig,
    ]);

    const journeysDataViewerQueryKeyPrefix = useMemo(
        () => [uniqueUuid`efadf6c4-78b9-4e6e-b474-0f8c46f661bf`, isInQueueView === true ? "inqueue" : journeySliceConfig.queryKeySegment, workspace.id],
        [isInQueueView, journeySliceConfig.queryKeySegment, workspace.id],
    );

    const journeyRowActionGlobalConstraints = useMemo(
        () =>
            resolveDuluxJourneyGlobalConstraints(workspaceCapabilities) ??
            resolveEventContainerEditPanelGlobalConstraints({
                workspace,
                workspaceCapabilities,
                editPanel: journeysModuleConfiguration.configuration.eventContainersPage.editPanel,
            }),
        [workspace, workspaceCapabilities, journeysModuleConfiguration.configuration.eventContainersPage.editPanel],
    );

    const scheduleCallDisableConfig = useMemo(
        () =>
            resolveEventContainerScheduleCallDisableConfig({
                workspace,
                workspaceCapabilities,
                eventContainersPage: journeysModuleConfiguration.configuration.eventContainersPage,
                eventContainerDetailsPage: journeysModuleConfiguration.configuration.eventContainerDetailsPage,
                context: "list",
            }),
        [workspace, workspaceCapabilities, journeysModuleConfiguration.configuration.eventContainersPage, journeysModuleConfiguration.configuration.eventContainerDetailsPage],
    );

    const isJourneyListCardFooterRestricted = useCallback(
        (journey: EventContainerWithEvents) =>
            isListCardFooterRestrictedByGlobalConstraints(journey, journeyRowActionGlobalConstraints),
        [journeyRowActionGlobalConstraints],
    );

    const eventContainerRowEngagement = useEventContainerRowEngagement({
        workspaceId: workspace.id,
        navigate,
        isArchivedView: isArchivedSlice,
        isInQueueView: isInQueueView === true,
        canEditInQueueJourney: (journey) => getInQueueAssignmentStatus(journey) === "Unassigned",
        listModuleConfig: activeJourneyListModuleConfig,
        showDisposition: showKanbanDispositionAction,
        showNotes: isRegistryLeadsView === true || isEmotoradLifecycleOverviewView === true,
        includeCreateDrawer:
            (isRegistryLeadsView === true && registryJourneysLeadsListConfig?.includeEventCreateDrawer === true) ||
            (isEmotoradLifecycleOverviewView === true && emotoradLifecycleListConfig.includeEventCreateDrawer === true),
        listCreateDrawerItemsCount: listCreateDrawerItems.length,
        scheduleRoute: activeJourneyListModuleConfig?.scheduleRoute,
        onScheduleSuccess: () => {
            void invalidateOttopilotDataViewerQueries(queryClient, journeysDataViewerQueryKeyPrefix);
        },
        queryKeyPrefix: journeysDataViewerQueryKeyPrefix,
        controllerRef: journeysDataViewerControllerRef,
        selectedDispositionEventContainerId:
            selectedDispositionJourney?.id ?? null,
        notesPanelEventContainerId:
            notesPanelContext?.eventContainerId ?? null,
        openNotesPanel: openNotesPanel,
        closeNotesPanel,
        notesEventContainerSourceLabel: journeysRowDisplayNameVariants.singularSentenceCase,
        onOpenDisposition: openDispositionJourney,
        onCloseDisposition: () => setSelectedDispositionJourney(null),
        globalConstraints: journeyRowActionGlobalConstraints,
        scheduleCallDisableConfig,
        isAmaron: isAmaronWorkspace(workspaceCapabilities) === true,
    });

    const effectiveJourneyRowActions = useMemo((): TableRowActionsConfig<EventContainerWithEvents> => {
        if (isDuluxWorkspace(workspaceCapabilities) !== true) {
            return eventContainerRowEngagement.tableRowActions;
        }
        return wrapDuluxAidOnboardedJourneyRowActions(eventContainerRowEngagement.tableRowActions);
    }, [workspace, eventContainerRowEngagement.tableRowActions]);

    const amaronInQueueKanbanRowActions = useMemo((): TableRowActionsConfig<EventContainerWithEvents> => {
        const base = eventContainerRowEngagement.tableRowActions;
        const moreMenu: TableRowActionsMenuItem[] = ["edit", "view"];

        return {
            ...base,
            toolbar: ["more"],
            moreMenu,
            isMenuItemVisible: (item, journey) => {
                if (item === "edit") {
                    return getInQueueAssignmentStatus(journey) === "Unassigned";
                }
                return base.isMenuItemVisible?.(item, journey) ?? true;
            },
            isMenuItemDisabled: (item, journey) => {
                if (item === "edit") {
                    return base.canEdit?.(journey) !== true;
                }
                return base.isMenuItemDisabled?.(item, journey) ?? false;
            },
            getActiveState: (journey) => {
                const state = base.getActiveState?.(journey) ?? {};
                return {
                    ...state,
                    more:
                        state.more === true ||
                        state.edit === true ||
                        state.view === true ||
                        state.profile === true ||
                        state.disposition === true ||
                        state.create === true ||
                        state.notes === true ||
                        state.scheduleCall === true ||
                        state.archive === true,
                };
            },
        };
    }, [eventContainerRowEngagement.tableRowActions]);

    const sharedEventContainerRowEngagementHandlers = eventContainerRowEngagement.handlers;
    const sharedJourneyFooterBindings = eventContainerRowEngagement.footerBindings;

    const emotoradJourneysListPresentation = useEmotoradJourneysListCardPresentation({
        mode: isInQueueView === true ? "inQueue" : "lifecycle",
        workspaceId: workspace.id,
        navigate,
        journeysDisplayNameOttopilotStringTemplate: journeysModuleConfiguration.configuration.displayNameTemplate,
        emotoradInQueueListConfig,
        emotoradLifecycleListConfig,
        inQueueListStatusBoard: {
            columnConfig: createInQueueAssignmentListStatusColumnConfig(),
        },
        lifecycleListStatusBoard: {
            columnConfig: emotoradLifecycleKanbanColumnConfig,
        },
        lifecycleStatusColumnId: journeysModuleConfiguration.configuration.statusColumnConfig?.columnId ?? EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID,
        getJourneyOuDisplayName: (journey) => getJourneyOuDisplayName({journey, eventContainerTemplate: journeysModuleConfiguration, organizationalUnits, organizationalUnitTemplates}),
        resolveInQueueAssignmentLabel: getInQueueAssignmentStatus,
        onLifecycleDisposeRequest: openDispositionJourney,
        openNotesPanel,
        notesEventContainerSourceLabel: journeysRowDisplayNameVariants.singularSentenceCase,
        rowEngagement: sharedEventContainerRowEngagementHandlers,
        sharedFooterBindings: sharedJourneyFooterBindings,
    });

    const amaronAssignedOuListCardFields = useMemo((): AssignedOuListCardFields | undefined => {
        if (isAmaronWorkspace(workspaceCapabilities) !== true) {
            return undefined;
        }
        return {
            getAssignedOuColumnLabel: (journey) => getAmaronJourneyListCardAssignedOuColumnLabel(journey, journeysModuleConfiguration, organizationalUnits, organizationalUnitTemplates),
            getAssignedOuName: (journey) => getAmaronJourneyListCardAssignedOuName(journey, journeysModuleConfiguration, organizationalUnits, organizationalUnitTemplates),
        };
    }, [workspace, organizationalUnits, organizationalUnitTemplates]);

    const registryJourneysListPresentation = useConfiguredJourneyListCardPresentation({
        mode: isInQueueView === true ? "inQueue" : "leads",
        workspaceId: workspace.id,
        navigate,
        columnIds: workspaceListSupport?.journeyColumnIds ?? {
            contactNumber: AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
            remarks: AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
            leadStatus: AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
        },
        journeysDisplayNameOttopilotStringTemplate: journeysModuleConfiguration.configuration.displayNameTemplate,
        listConfig: registryJourneysLeadsListConfig ?? {
            enabled: false,
            showFooter: false,
            scheduleRoute: "",
            includeEventCreateDrawer: false,
        },
        inQueueListConfig: resolveJourneysListConfig(workspaceListSupport, "inQueue") ?? undefined,
        resolveJourneyStatus: (journey) =>
            workspaceListSupport?.resolveJourneyListStatus(journey, {
                page: isInQueueView === true ? "inQueue" : "leads",
                resolveInQueueAssignmentLabel: getInQueueAssignmentStatus,
            }) ?? {name: "Active", pillType: "Active"},
        onLeadsDisposeRequest: openDispositionJourney,
        openNotesPanel,
        notesEventContainerSourceLabel: journeysRowDisplayNameVariants.singularSentenceCase,
        assignedOuListCardFields: amaronAssignedOuListCardFields,
        funnelOptions:
            isAmaronWorkspace(workspaceCapabilities) === true
                ? createAmaronJourneyListFunnelOptions((journey: EventContainerWithEvents) => journey.additionalColumnValues)
                : undefined,
        rowEngagement: sharedEventContainerRowEngagementHandlers,
        sharedFooterBindings: sharedJourneyFooterBindings,
        isListCardFooterRestricted: isJourneyListCardFooterRestricted,
        scheduleCallDisableConfig,
        resolveListCardDisplayExtras:
            isAmaronLeadsView === true || isDuluxLeadsView === true
                ? () => ({
                      tableRowActionsConfig: effectiveJourneyRowActions,
                  })
                : undefined,
    });

    const journeysListPresentation = workspaceListSupport !== null ? registryJourneysListPresentation : emotoradJourneysListPresentation;
    const writeConfig = journeysModuleConfiguration.configuration.eventContainersPage.writeConfig;
    const createJourneyHref =
        isEmotoradLifecycleOverviewView === true || isInQueueView === true
            ? null
            : writeConfig === undefined || writeConfig === null
              ? isEmotoradWorkspace(workspace) === true
                  ? `/w/${workspace.id}/ottopilot/emotorad/lead/create`
                  : null
              : writeConfig.formId === DHIRAAGU__EVENT_CONTAINER__CREATE__FORM__ID
                ? `/w/${workspace.id}/ottopilot/dhiraagu/journey/create`
                : isEmotoradWorkspace(workspace) === true
                  ? `/w/${workspace.id}/ottopilot/emotorad/lead/create`
                  : `/w/${workspace.id}/ottopilot/event-containers/create`;

    const buildJourneyCalendarScheduleEmptyFallback = (singularSentenceCase: string, pluralLowerCase: string): ResolvedCalendarScheduleEmptyState => {
        if (isEmotoradWorkspace(workspace) === true) {
            return {
                ...EMOTORAD_LEADS_CALENDAR_SCHEDULE_EMPTY_STATE,
                title: `No ${pluralLowerCase} scheduled yet`,
                titleWithFilters: `No ${pluralLowerCase} match your filters`,
                description: `Create your first ${singularSentenceCase.toLowerCase()} to start tracking scheduled items on the calendar.`,
                descriptionWithFilters: `Try adjusting your filters to see more ${pluralLowerCase}, or create a new ${singularSentenceCase.toLowerCase()}.`,
                createFormId: EMOTORAD__LEAD__CREATE__FORM__ID,
            };
        }
        return {
            createButtonLabel: `Create ${singularSentenceCase}`,
            title: `No ${pluralLowerCase} scheduled yet`,
            titleWithFilters: `No ${pluralLowerCase} match your filters`,
            description: `Create your first ${singularSentenceCase.toLowerCase()} to start tracking scheduled items on the calendar.`,
            descriptionWithFilters: `Try adjusting your filters to see more ${pluralLowerCase}, or create a new ${singularSentenceCase.toLowerCase()}.`,
        };
    };

    const journeyCalendarScheduleEmptyState = useMemo(() => {
        if (journeyCalendarConfig === undefined) {
            return undefined;
        }
        const variants = journeysModuleConfiguration.configuration.moduleDisplayNameVariants;
        return resolveCalendarScheduleEmptyState(
            journeyCalendarConfig,
            buildJourneyCalendarScheduleEmptyFallback(
                isEmotoradLifecycleOverviewView === true ? "Lifecycle Overview" : variants.singularSentenceCase,
                isEmotoradLifecycleOverviewView === true ? "lifecycle overviews" : variants.pluralLowerCase,
            ),
        );
    }, [isEmotoradLifecycleOverviewView, journeyCalendarConfig, journeysModuleConfiguration.configuration.moduleDisplayNameVariants]);

    const onJourneyCalendarCreateClick =
        createJourneyHref === null
            ? undefined
            : () => {
                  void navigate({to: createJourneyHref});
              };

    const amaronJourneyStatusNavConfigForFilterUi = useMemo(() => {
        if (isAmaronWorkspace(workspaceCapabilities) !== true) {
            return null;
        }
        if (isAmaronLeadsView === true) {
            return getAmaronJourneyLeadsStatusNavConfig();
        }
        if (isAmaronInQueueView === true) {
            return getAmaronInQueueStatusNavConfig();
        }
        return null;
    }, [isAmaronInQueueView, isAmaronLeadsView, workspace]);

    const duluxJourneyStatusNavConfigForFilterUi = useMemo(() => {
        if (isDuluxLeadsView !== true) {
            return null;
        }
        return getDuluxJourneyLeadsStatusNavConfig();
    }, [isDuluxLeadsView]);

    const duluxJourneyActiveStatusIdForFilterUi = useMemo(() => {
        if (duluxJourneyStatusNavConfigForFilterUi === null) {
            return null;
        }
        return resolveModuleStatusIdForFilter({
            statusIdFromSearch: search.statusId,
            validStatusIds: getDuluxModuleStatusNavValidStatusIds(duluxJourneyStatusNavConfigForFilterUi),
            ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
        });
    }, [duluxJourneyStatusNavConfigForFilterUi, journeySliceConfig.ignoreStatusIdSearchParam, search.statusId]);

    const amaronJourneyActiveStatusIdForFilterUi = useMemo(() => {
        if (amaronJourneyStatusNavConfigForFilterUi === null) {
            return null;
        }
        return resolveModuleStatusIdForFilter({
            statusIdFromSearch: search.statusId,
            validStatusIds: getAmaronModuleStatusNavValidStatusIds(amaronJourneyStatusNavConfigForFilterUi),
            ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
        });
    }, [amaronJourneyStatusNavConfigForFilterUi, journeySliceConfig.ignoreStatusIdSearchParam, search.statusId]);

    const amaronLeadsJourneyScopeFilterConfig = useMemo(() => {
        if (isAmaronLeadsView !== true || isArchivedSlice === true) {
            return undefined;
        }
        if (journeysModuleConfiguration.configuration.statusColumnConfig === undefined) {
            return undefined;
        }
        const statusGroups = getAmaronJourneyScopeFilterStatusIdsForEvents(workspaceCapabilities);
        if (statusGroups === null) {
            return undefined;
        }
        return {
            statusColumnId: journeysModuleConfiguration.configuration.statusColumnConfig.columnId,
            activeStatusIds: statusGroups.activeStatusIds,
            inactiveStatusIds: statusGroups.inactiveStatusIds,
        };
    }, [isAmaronLeadsView, isArchivedSlice, journeysModuleConfiguration.configuration.statusColumnConfig, workspace]);

    const isAmaronLeadsJourneyScopeFilterDisabled = useMemo(() => {
        if (amaronJourneyStatusNavConfigForFilterUi === null || amaronLeadsJourneyScopeFilterConfig === undefined) {
            return false;
        }
        return isAmaronModuleStatusChildView({
            statusNavConfig: amaronJourneyStatusNavConfigForFilterUi,
            activeStatusId: amaronJourneyActiveStatusIdForFilterUi,
        });
    }, [
        amaronJourneyActiveStatusIdForFilterUi,
        amaronJourneyStatusNavConfigForFilterUi,
        amaronLeadsJourneyScopeFilterConfig,
    ]);

    const journeysFilterClearConfig = useMemo(() => {
        let config:
            | ReturnType<typeof buildAmaronJourneyFilterClearConfig>
            | {
                  preservedFilterLineItemColumnIds?: ReadonlyArray<string>;
                  preservedFilterLineItems?: FilterConfiguration["lineItems"];
              }
            | undefined;

        if (isArchivedSlice === true) {
            config = {
                preservedFilterLineItemColumnIds: journeySliceConfig.preservedFilterColumnIds,
            };
        } else if (isAmaronWorkspace(workspaceCapabilities) === true) {
            config = buildAmaronJourneyFilterClearConfig({
                leadViewFilterLineItems: leadViewFilterLineItems,
                preserveLeadViewBaseline: isInQueueView !== true,
            });
            if (
                isAmaronLeadsView === true &&
                amaronLeadsJourneyScopeFilterConfig !== undefined &&
                amaronJourneyActiveStatusIdForFilterUi === null
            ) {
                config = {
                    ...config,
                    preservedFilterLineItems: [
                        ...(config?.preservedFilterLineItems ?? []),
                        {
                            columnId: amaronLeadsJourneyScopeFilterConfig.statusColumnId,
                            operation: FilterOperation.in,
                            values: amaronLeadsJourneyScopeFilterConfig.activeStatusIds.map((id) => String(id)),
                        },
                    ],
                };
            }
        } else {
            const preservedColumnIds = new Set<string>(journeySliceConfig.preservedFilterColumnIds);
            const preservedLineItems: FilterConfiguration["lineItems"] = [];
            if (isEmotoradLifecycleOverviewView === true && emotoradLifecycleOverviewMandatoryJourneyApiFilterLineItems.length > 0) {
                for (const lineItem of emotoradLifecycleOverviewMandatoryJourneyApiFilterLineItems) {
                    preservedColumnIds.add(lineItem.columnId);
                }
            }
            if (preservedColumnIds.size === 0 && preservedLineItems.length === 0) {
                config = undefined;
            } else {
                config = {
                    preservedFilterLineItemColumnIds: preservedColumnIds.size > 0 ? [...preservedColumnIds] : undefined,
                    preservedFilterLineItems: preservedLineItems.length > 0 ? preservedLineItems : undefined,
                };
            }
        }

        const emotoradStatusNavConfig =
            isEmotoradInQueueView === true
                ? getEmotoradInQueueStatusNavConfig()
                : isEmotoradLifecycleOverviewView === true
                  ? getEmotoradJourneyLifecycleStatusNavConfig()
                  : null;
        const emotoradActiveStatusId =
            emotoradStatusNavConfig === null
                ? null
                : resolveModuleStatusIdForFilter({
                      statusIdFromSearch: search.statusId,
                      validStatusIds: getEmotoradModuleStatusNavValidStatusIds(emotoradStatusNavConfig),
                      ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
                  });

        const inQueueActiveStatusId =
            isInQueueView === true
                ? resolveModuleStatusIdForFilter({
                      statusIdFromSearch: search.statusId,
                      validStatusIds: inQueueAssignmentValidStatusIds,
                      ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
                  })
                : null;

        if (isDuluxWorkspace(workspaceCapabilities) === true && isInQueueView !== true) {
            return mergeModuleStatusFilterPreservationIntoClearConfig(config, {
                statusNavConfig: duluxJourneyStatusNavConfigForFilterUi,
                activeStatusId: duluxJourneyActiveStatusIdForFilterUi,
                moduleStatusFilterScaffold: DULUX_MODULE_STATUS_FILTER_SCAFFOLD,
            });
        }

        return mergeModuleStatusFilterPreservationIntoClearConfig(config, {
            statusNavConfig:
                isInQueueView === true ? null : isAmaronWorkspace(workspaceCapabilities) === true ? amaronJourneyStatusNavConfigForFilterUi : emotoradStatusNavConfig,
            activeStatusId:
                isInQueueView === true
                    ? inQueueActiveStatusId
                    : isAmaronWorkspace(workspaceCapabilities) === true
                      ? amaronJourneyActiveStatusIdForFilterUi
                      : emotoradActiveStatusId,
            inQueueLeadOwnerColumnId: isInQueueView === true ? inQueueAssignmentOwnerColumnId : null,
        });
    }, [
        workspace,
        isEmotoradLifecycleOverviewView,
        isEmotoradInQueueView,
        isInQueueView,
        isArchivedSlice,
        journeySliceConfig.preservedFilterColumnIds,
        journeySliceConfig.ignoreStatusIdSearchParam,
        emotoradLifecycleOverviewMandatoryJourneyApiFilterLineItems,
        leadViewFilterLineItems,
        amaronJourneyStatusNavConfigForFilterUi,
        amaronJourneyActiveStatusIdForFilterUi,
        amaronLeadsJourneyScopeFilterConfig,
        isAmaronLeadsView,
        duluxJourneyStatusNavConfigForFilterUi,
        duluxJourneyActiveStatusIdForFilterUi,
        inQueueAssignmentOwnerColumnId,
        inQueueAssignmentValidStatusIds,
        search.statusId,
    ]);

    const emotoradMandatoryJourneyApiFilterLineItemsForQuery = useMemo(
        () => (isArchivedSlice === true ? [] : emotoradLifecycleOverviewMandatoryJourneyApiFilterLineItems),
        [isArchivedSlice, emotoradLifecycleOverviewMandatoryJourneyApiFilterLineItems],
    );

    const applyJourneysApiFilterConfiguration = (filterConfiguration: FilterConfiguration): FilterConfiguration =>
        mergeEmotoradLifecycleOverviewMandatoryJourneyApiFilters(
            mergeModuleSliceMandatoryFilters(filterConfiguration, journeySliceConfig.mandatoryFilters),
            emotoradMandatoryJourneyApiFilterLineItemsForQuery,
        );

    const renderJourneysTableNoDataEmptyState = useMemo(
        () =>
            isArchivedSlice === true
                ? (props: {viewportBoundsElement: HTMLElement | null}) => <TableNoArchivedDataEmptyState viewportBoundsElement={props.viewportBoundsElement} />
                : undefined,
        [isArchivedSlice],
    );

    const journeysInitialFilterConfiguration = useMemo((): FilterConfiguration => {
        if (isArchivedSlice === true) {
            return buildEventContainerModuleInitialFilterConfiguration({
                slice: "archived",
                eventContainerTemplateConfiguration: journeysModuleConfiguration.configuration,
            });
        }
        return {
            revisionId: uniqueUuid`f0f0bbef-5f9b-4cc1-a4de-4b0dca981f79`,
            lineItems: mergeEmotoradLifecycleOverviewMandatoryJourneyApiFilters(
                {
                    revisionId: uniqueUuid`f0f0bbef-5f9b-4cc1-a4de-4b0dca981f79`,
                    lineItems: isInQueueView ? [] : leadViewFilterLineItems,
                },
                emotoradLifecycleOverviewMandatoryJourneyApiFilterLineItems,
            ).lineItems,
        };
    }, [emotoradLifecycleOverviewMandatoryJourneyApiFilterLineItems, isArchivedSlice, isInQueueView, journeysModuleConfiguration, leadViewFilterLineItems]);

    const applyInQueueJourneysInitialFilterOverlay = useCallback(
        (filterConfiguration: FilterConfiguration): FilterConfiguration => {
            if (isInQueueView !== true || journeySliceConfig.ignoreStatusIdSearchParam === true) {
                return filterConfiguration;
            }
            if (inQueueAssignmentOwnerColumnId === null) {
                return filterConfiguration;
            }
            const statusId = resolveModuleStatusIdForFilter({
                statusIdFromSearch: search.statusId,
                validStatusIds: inQueueAssignmentValidStatusIds,
                ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
            });
            return applyInQueueAssignmentStatusIdToFilterConfiguration(filterConfiguration, inQueueAssignmentOwnerColumnId, statusId);
        },
        [inQueueAssignmentOwnerColumnId, inQueueAssignmentValidStatusIds, isInQueueView, journeySliceConfig.ignoreStatusIdSearchParam, search.statusId],
    );

    const applyAmaronJourneysInitialFilterOverlay = useCallback(
        (filterConfiguration: FilterConfiguration): FilterConfiguration => {
            if (isAmaronWorkspace(workspaceCapabilities) !== true || journeySliceConfig.ignoreStatusIdSearchParam === true) {
                return filterConfiguration;
            }
            if (isAmaronLeadsView !== true) {
                return filterConfiguration;
            }
            const statusNavConfig = getAmaronJourneyLeadsStatusNavConfig();
            const statusId = resolveModuleStatusIdForFilter({
                statusIdFromSearch: search.statusId,
                validStatusIds: getAmaronModuleStatusNavValidStatusIds(statusNavConfig),
            });
            if (statusId !== null) {
                return mergeModuleStatusFilterIntoFilterConfiguration(filterConfiguration, statusNavConfig, statusId, undefined, true);
            }
            if (amaronLeadsJourneyScopeFilterConfig === undefined) {
                return filterConfiguration;
            }
            if (getJourneyScopeSelectionFromFilter(filterConfiguration, amaronLeadsJourneyScopeFilterConfig) !== "all") {
                return filterConfiguration;
            }
            return applyJourneyScopeSelectionToFilterConfiguration(filterConfiguration, amaronLeadsJourneyScopeFilterConfig, "active");
        },
        [
            amaronLeadsJourneyScopeFilterConfig,
            isAmaronLeadsView,
            journeySliceConfig.ignoreStatusIdSearchParam,
            search.statusId,
            workspace,
        ],
    );

    const applyDuluxJourneysInitialFilterOverlay = useCallback(
        (filterConfiguration: FilterConfiguration): FilterConfiguration => {
            if (isDuluxLeadsView !== true || journeySliceConfig.ignoreStatusIdSearchParam === true) {
                return filterConfiguration;
            }
            const statusNavConfig = getDuluxJourneyLeadsStatusNavConfig();
            const statusId = resolveModuleStatusIdForFilter({
                statusIdFromSearch: search.statusId,
                validStatusIds: getDuluxModuleStatusNavValidStatusIds(statusNavConfig),
            });
            return mergeModuleStatusFilterIntoFilterConfiguration(filterConfiguration, statusNavConfig, statusId, undefined, true);
        },
        [isDuluxLeadsView, journeySliceConfig.ignoreStatusIdSearchParam, search.statusId],
    );

    const applyJourneysInitialFilterOverlay = useCallback(
        (filterConfiguration: FilterConfiguration): FilterConfiguration => {
            let next = filterConfiguration;
            if (isInQueueView === true) {
                next = applyInQueueJourneysInitialFilterOverlay(next);
            }
            if (isAmaronWorkspace(workspaceCapabilities) === true) {
                next = applyAmaronJourneysInitialFilterOverlay(next);
            }
            if (isDuluxWorkspace(workspaceCapabilities) === true) {
                next = applyDuluxJourneysInitialFilterOverlay(next);
            }
            return next;
        },
        [applyAmaronJourneysInitialFilterOverlay, applyDuluxJourneysInitialFilterOverlay, applyInQueueJourneysInitialFilterOverlay, isInQueueView, workspace],
    );

    const prepareJourneysCalendarFilters = (filterConfiguration: FilterConfiguration) => {
        const bobcardAssignmentStatusFilters =
            isBobcardWorkspace(workspace) === true
                ? filterConfiguration.lineItems.filter(
                      (lineItem) => lineItem.columnId === BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID,
                  )
                : [];
        const frontendCustomerNameFilters =
            isBobcardWorkspace(workspace) === true
                ? filterConfiguration.lineItems.filter(
                      (lineItem) => lineItem.columnId === BOBCARD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
                  )
                : [];
        const frontendBobcardApplicationStatusFilters: FilterConfiguration["lineItems"] = [];
        const bobcardLeadOwnerColumnId =
            isBobcardWorkspace(workspace) === true
                ? (leadOwnerColumnId ?? BOBCARD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID)
                : undefined;
        const bobcardAssignmentBackendLineItems: FilterConfiguration["lineItems"] =
            bobcardLeadOwnerColumnId !== undefined && isInQueueView !== true
                ? bobcardAssignmentStatusFilters.flatMap((lineItem) => {
                      const values =
                          lineItem.operation === FilterOperation.in
                              ? lineItem.values.map((value) => value.toLowerCase())
                              : [];
                      const wantsAssigned = values.includes("assigned");
                      const wantsUnassigned = values.includes("unassigned");
                      return wantsAssigned === wantsUnassigned
                          ? []
                          : [{
                                columnId: bobcardLeadOwnerColumnId,
                                operation: wantsAssigned ? FilterOperation.notEquals : FilterOperation.equals,
                                value: null,
                            }];
                  })
                : [];
        const inQueuePartition =
            isInQueueView === true && inQueueAssignmentOwnerColumnId !== null
                ? partitionInQueueFiltersForBackend({
                      lineItems: filterConfiguration.lineItems,
                      statusFilterColumnIds: inQueueUiStatusFilterColumnIds,
                      leadOwnerColumnId: inQueueAssignmentOwnerColumnId,
                  })
                : null;
        const backendLineItems =
            inQueuePartition !== null
                ? [...inQueuePartition.backendLineItems, ...inQueuePartition.inQueueAssignmentBackendLineItems]
                : filterConfiguration.lineItems.filter(
                      (lineItem) =>
                          !(isBobcardWorkspace(workspace) === true &&
                            lineItem.columnId === BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID) &&
                          !(isBobcardWorkspace(workspace) === true &&
                            lineItem.columnId === BOBCARD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID) &&
                          !(isAmaronWorkspace(workspaceCapabilities) === true &&
                            isAmaronJourneyDispositionUiFilterColumn(lineItem.columnId) === true) &&
                          !(isEmotoradLifecycleOverviewView === true &&
                            isJourneyDispositionUiFilterColumn(lineItem.columnId) === true),
                  );
        const amaronBackendLineItems =
            isAmaronWorkspace(workspaceCapabilities) === true && isAmaronInQueueView !== true
                ? appendAmaronJourneyDispositionApiFilterIfMissing(
                      backendLineItems,
                      filterConfiguration.lineItems,
                  )
                : backendLineItems;
        const filterConfigurationForBackend: FilterConfiguration = {
            ...filterConfiguration,
            lineItems: [...amaronBackendLineItems, ...bobcardAssignmentBackendLineItems].map(
                normalizeEnumFilterLineItem,
            ),
        };
        return {
            frontendBobcardApplicationStatusFilters,
            frontendCustomerNameFilters,
            mappedFilterConfiguration: mapFilterConfigurationToFields(
                applyJourneysApiFilterConfiguration(filterConfigurationForBackend),
            ),
            usesClientSidePostFetchFiltering:
                frontendCustomerNameFilters.length > 0 || frontendBobcardApplicationStatusFilters.length > 0,
        };
    };

    const nativeCsvRowExportRef = useRef<(id: Uuid) => void>(() => undefined);
    const journeysDataViewerController = useOttopilotDataViewerController<EventContainerWithEvents>({
        rowDisplayNameVariants: journeysRowDisplayNameVariants,
        queryKeyPrefix: journeysDataViewerQueryKeyPrefix,
        hideEditPanelFooter: hideJourneysViewPanelEditFooter,
        hideReadPanelEditMenuItem: isAmaronWorkspace(workspaceCapabilities) === true,
        searchColumns: journeysSearchColumns,
        searchPlaceholder: journeysSearchPlaceholderResolved,
        scopeFilterConfig: journeysScopeFilterConfig,
        journeyScopeFilterConfig: amaronLeadsJourneyScopeFilterConfig,
        journeyScopeFilterDisabled: isAmaronLeadsJourneyScopeFilterDisabled,
        filterClearConfig: journeysFilterClearConfig,
        applyInitialFilterOverlay: isInQueueView === true || isAmaronWorkspace(workspaceCapabilities) === true ? applyJourneysInitialFilterOverlay : undefined,
        defaultCreatedAtMonthFilter: isArchivedSlice !== true,
        initialFilterConfiguration: journeysInitialFilterConfiguration,
        calendarDisplayMetrics: amaronJourneyCalendarDisplayMetrics,
        calendarDisplayMetricContext: {statusId: search.statusId},
        dateRangeFieldIds:
            journeyCalendarRangeFieldIdResolved !== undefined && amaronJourneyCalendarDisplayMetrics === undefined
                ? {
                      startDateColumnId: journeyCalendarRangeFieldIdResolved,
                      endDateColumnId: journeyCalendarRangeFieldIdResolved,
                  }
                : undefined,
        calendarRowColumns: journeysCalendarRowColumns,
        calendarDataSource: "orchestration",
        calendarOrchestrationEligibility: calendarOrchestrationEligibility,
        calendarYearDayCountsQueryFn: calendarOrchestrationEligibility !== "allowed" ? null : async ({filterConfiguration, searchConfiguration, dateColumnId}) => {
            const {mappedFilterConfiguration, usesClientSidePostFetchFiltering} =
                prepareJourneysCalendarFilters(filterConfiguration);
            if (usesClientSidePostFetchFiltering === true) {
                throw new Error("Year view is unavailable with the selected derived filter. Clear that filter to continue.");
            }
            const result = await countOrchestratedEventContainersByColumn({
                accessToken: heimdallUser.accessToken,
                bucket: "day",
                column: dateColumnId,
                filters: toEventContainerFilters(mappedFilterConfiguration, searchConfiguration),
                intellsysBaseUrl: intellsysBaseUrl,
                templateId: journeysModuleConfiguration.id,
                workspaceId: workspace.id,
            });
            if (result.success !== true) {
                throw result.error;
            }
            return result.data;
        },
        rowsQueryFn: async ({
            filterConfiguration,
            limit,
            offset,
            sortConfiguration,
            searchConfiguration,
        }) => {
            const bobcardAssignmentStatusFilters =
                isBobcardWorkspace(workspace) === true
                    ? filterConfiguration.lineItems.filter((lineItem) => lineItem.columnId === BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID)
                    : [];
            const frontendCustomerNameFilters =
                isBobcardWorkspace(workspace) === true
                    ? filterConfiguration.lineItems.filter((lineItem) => lineItem.columnId === BOBCARD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID)
                    : [];
            const frontendBobcardApplicationStatusFilters: FilterConfiguration["lineItems"] = [];
            const bobcardLeadOwnerColumnId =
                isBobcardWorkspace(workspace) === true ? (leadOwnerColumnId ?? BOBCARD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID) : undefined;
            const bobcardAssignmentBackendLineItems: FilterConfiguration["lineItems"] =
                bobcardLeadOwnerColumnId !== undefined && isInQueueView !== true
                    ? bobcardAssignmentStatusFilters.flatMap((lineItem) => {
                          const values = lineItem.operation === FilterOperation.in ? lineItem.values.map((value) => value.toLowerCase()) : [];
                          const wantsAssigned = values.includes("assigned");
                          const wantsUnassigned = values.includes("unassigned");
                          if (wantsAssigned === wantsUnassigned) {
                              return [];
                          }
                          return [
                              {
                                  columnId: bobcardLeadOwnerColumnId,
                                  operation: wantsAssigned ? FilterOperation.notEquals : FilterOperation.equals,
                                  value: null,
                              },
                          ];
                      })
                    : [];
            const inQueuePartition =
                isInQueueView === true && inQueueAssignmentOwnerColumnId !== null
                    ? partitionInQueueFiltersForBackend({
                          lineItems: filterConfiguration.lineItems,
                          statusFilterColumnIds: inQueueUiStatusFilterColumnIds,
                          leadOwnerColumnId: inQueueAssignmentOwnerColumnId,
                      })
                    : null;
            const backendLineItems = (
                inQueuePartition !== null
                    ? [...inQueuePartition.backendLineItems, ...inQueuePartition.inQueueAssignmentBackendLineItems]
                    : filterConfiguration.lineItems.filter(
                          (lineItem) =>
                              !(isBobcardWorkspace(workspace) === true && lineItem.columnId === BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID) &&
                              !(isBobcardWorkspace(workspace) === true && lineItem.columnId === BOBCARD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID) &&
                              !(isAmaronWorkspace(workspaceCapabilities) === true && isAmaronJourneyDispositionUiFilterColumn(lineItem.columnId) === true) &&
                              !(isEmotoradLifecycleOverviewView === true && isJourneyDispositionUiFilterColumn(lineItem.columnId) === true),
                      )
            ).filter(
                (lineItem) =>
                    !(
                        isBobcardWorkspace(workspace) === true &&
                        bobcardAssignmentBackendLineItems.length > 0 &&
                        bobcardLeadOwnerColumnId !== undefined &&
                        lineItem.columnId === bobcardLeadOwnerColumnId &&
                        lineItem.operation === FilterOperation.notEquals &&
                        lineItem.value === null
                    ),
            );
            const amaronBackendLineItems =
                isAmaronWorkspace(workspaceCapabilities) === true && isAmaronInQueueView !== true
                    ? appendAmaronJourneyDispositionApiFilterIfMissing(backendLineItems, filterConfiguration.lineItems)
                    : backendLineItems;
            const filterConfigurationForBackend: FilterConfiguration = {
                ...filterConfiguration,
                lineItems: [...amaronBackendLineItems, ...bobcardAssignmentBackendLineItems].map(normalizeEnumFilterLineItem),
            };
            const mappedFilterConfiguration = mapFilterConfigurationToFields(applyJourneysApiFilterConfiguration(filterConfigurationForBackend));
            const mappedSortConfiguration = mapSortConfigurationToFields(sortConfiguration);
            const usesClientSidePostFetchFiltering = frontendCustomerNameFilters.length > 0 || frontendBobcardApplicationStatusFilters.length > 0;
            const journeysResult = await getEventContainersWithEvents({
                workspaceId: workspace.id,
                offset: usesClientSidePostFetchFiltering === true ? 0 : offset,
                limit: usesClientSidePostFetchFiltering === true ? JOURNEYS_CLIENT_SIDE_FILTER_PROBE_FETCH_LIMIT : limit,
                filters: toEventContainerFilters(mappedFilterConfiguration, searchConfiguration),
                orderBy: toEventContainerOrderBy(mappedSortConfiguration),
                templateId: journeysModuleConfiguration.id,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (journeysResult.success !== true) {
                throw journeysResult.error;
            }
            let journeys: Array<EventContainerWithEvents> =
                isEmotoradWorkspace(workspace) === true
                    ? journeysResult.data.map((journey) =>
                          emotoradJourneySyntheticFunction(journey, journeysModuleConfiguration),
                      )
                    : isAmaronWorkspace(workspaceCapabilities) === true
                      ? journeysResult.data.map((journey) =>
                            amaronJourneySyntheticFunction(
                                journey,
                                getOptionalEventContainerDispositionId(journey, journeysModuleConfiguration),
                            ),
                        )
                      : journeysResult.data;
            if (isEmotoradLifecycleOverviewView === true && isEmotoradWorkspace(workspace) === true && limit <= 100) {
                journeys = await augmentEmotoradLifecycleCustomerZoneFromPincode(getAdditionalColumns, journeys, {
                    intellsysBaseUrl,
                    accessToken: heimdallUser.accessToken,
                    journeysModuleConfiguration,
                    eventTemplates,
                    users,
                    userTemplates,
                    organizationalUnits,
                    organizationalUnitTemplates,
                    btlActivities,
                    products,
                });
            }
            const rowsCalendarAugmentConfig =
                isInQueueView === true
                    ? journeysModuleConfiguration.configuration.eventContainersPage.inQueueCalendarConfig
                    : (journeysModuleConfiguration.configuration.eventContainersPage.eventContainerCalendarConfig ?? journeysModuleConfiguration.configuration.eventContainersPage.inQueueCalendarConfig);
            const augmentJourneysForCalendarView = (rows: Array<EventContainerWithEvents>): Array<EventContainerWithEvents> => {
                const cfg = rowsCalendarAugmentConfig;
                if (cfg === undefined || cfg.calendarTitleOttopilotStringTemplate === undefined || cfg.calendarStartDateColumnId === undefined) {
                    return rows;
                }
                if (isEmotoradWorkspace(workspace) === true && isInQueueView !== true) {
                    return rows.map((journey) => {
                        const startRaw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID];
                        const startDate = startRaw !== null && startRaw !== undefined && String(startRaw).trim() !== "" ? String(startRaw) : journey.updatedAt;
                        const title = processOttopilotStringTemplate(
                            EMOTORAD_LIFECYCLE_CALENDAR_TITLE_OTTOPILOT_TEMPLATE,
                            journey.additionalColumnValues as Record<Uuid, unknown>,
                        );
                        return {
                            ...journey,
                            title: title,
                            startDate: startDate,
                            endDate: startDate,
                            isAlldayEvent: false,
                        };
                    });
                }
                const calendarTitleTemplate = cfg.calendarTitleOttopilotStringTemplate;
                const calendarStartColId = cfg.calendarStartDateColumnId;
                if (isAmaronWorkspace(workspaceCapabilities) === true && amaronJourneyCalendarDisplayMetrics !== undefined) {
                    if (calendarTitleTemplate === undefined) {
                        return rows;
                    }
                    return rows.map((journey) => {
                        const title = processOttopilotStringTemplate(
                            calendarTitleTemplate,
                            journey.additionalColumnValues as Record<Uuid, unknown>,
                        );
                        return {
                            ...journey,
                            title: title,
                            isAlldayEvent: false,
                        };
                    });
                }
                return rows.map((journey) => {
                    const startRaw = journey.additionalColumnValues[calendarStartColId as Uuid];
                    const startDate = startRaw !== null && startRaw !== undefined && String(startRaw).trim() !== "" ? String(startRaw) : journey.updatedAt;
                    const title = processOttopilotStringTemplate(calendarTitleTemplate, journey.additionalColumnValues as Record<Uuid, unknown>);
                    return {
                        ...journey,
                        title: title,
                        startDate: startDate,
                        endDate: startDate,
                        isAlldayEvent: false,
                    };
                });
            };
            const journeysWithDerivedStatus: Array<EventContainerWithEvents> =
                isInQueueView === true
                    ? journeys.map((journey) => {
                          const inQueueStatus = getInQueueAssignmentStatus(journey).toLowerCase();
                          if (isEmotoradWorkspace(workspace) === true) {
                              return {
                                  ...journey,
                                  [EMOTORAD_INQUEUE_STATUS_FIELD]: isJourneyAssignedForEmotoradInQueue(journey)
                                      ? LIST_INQUEUE_ASSIGNMENT_STATUS.assigned
                                      : LIST_INQUEUE_ASSIGNMENT_STATUS.unassigned,
                              };
                          }
                          if (isBobcardWorkspace(workspace) === true) {
                              return {
                                  ...journey,
                                  [BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID]: inQueueStatus,
                              };
                          }
                          if (isAmaronWorkspace(workspaceCapabilities) === true) {
                              return {
                                  ...journey,
                                  [AMARON_INQUEUE_STATUS_FIELD]: getInQueueAssignmentSyntheticStatusValue(getInQueueAssignmentStatus(journey) === "Assigned"),
                              };
                          }
                          return journey;
                      })
                    : journeys;

            const filteredByCustomerName =
                frontendCustomerNameFilters.length > 0
                    ? journeysWithDerivedStatus.filter((journey) =>
                          frontendCustomerNameFilters.every((lineItem) => doesFrontendCustomerNameMatchFilter(journey, lineItem)),
                      )
                    : journeysWithDerivedStatus;

            const filteredByBobcardAppStatus =
                frontendBobcardApplicationStatusFilters.length > 0
                    ? filteredByCustomerName.filter((journey) =>
                          frontendBobcardApplicationStatusFilters.every((lineItem) => doesFrontendBobcardApplicationStatusMatchFilter(journey, lineItem)),
                      )
                    : filteredByCustomerName;

            return augmentJourneysForCalendarView(
                usesClientSidePostFetchFiltering === true ? filteredByBobcardAppStatus.slice(offset, offset + limit) : filteredByBobcardAppStatus,
            );
        },
        calendarRowsQueryFn: async ({
            filterConfiguration,
            limit,
            offset,
            sortConfiguration,
            searchConfiguration,
            columns,
            maxRowsPerDay,
            maxRowsPerDayColumn,
        }) => {
            const {
                frontendBobcardApplicationStatusFilters,
                frontendCustomerNameFilters,
                mappedFilterConfiguration,
                usesClientSidePostFetchFiltering,
            } = prepareJourneysCalendarFilters(filterConfiguration);
            const mappedSortConfiguration = mapSortConfigurationToFields(sortConfiguration);
            const journeysResult = await getOrchestratedEventContainers({
                accessToken: heimdallUser.accessToken,
                columns: columns ?? null,
                filters: toEventContainerFilters(mappedFilterConfiguration, searchConfiguration),
                intellsysBaseUrl,
                limit:
                    usesClientSidePostFetchFiltering === true
                        ? JOURNEYS_CLIENT_SIDE_FILTER_PROBE_FETCH_LIMIT
                        : limit,
                maxRowsPerDay: maxRowsPerDay ?? null,
                maxRowsPerDayColumn: maxRowsPerDayColumn ?? null,
                offset: usesClientSidePostFetchFiltering === true ? 0 : offset,
                orderBy: toEventContainerOrderBy(mappedSortConfiguration),
                templateId: journeysModuleConfiguration.id,
                workspaceId: workspace.id,
            });
            if (journeysResult.success !== true) {
                throw journeysResult.error;
            }
            let rows: Array<EventContainerWithEvents> =
                isEmotoradWorkspace(workspace) === true
                    ? journeysResult.data.map((journey) =>
                          emotoradJourneySyntheticFunction(journey, journeysModuleConfiguration),
                      )
                    : isAmaronWorkspace(workspaceCapabilities) === true
                      ? journeysResult.data.map((journey) =>
                            amaronJourneySyntheticFunction(
                                journey,
                                getOptionalEventContainerDispositionId(journey, journeysModuleConfiguration),
                            ),
                        )
                      : journeysResult.data;
            if (
                isEmotoradLifecycleOverviewView === true &&
                isEmotoradWorkspace(workspace) === true &&
                limit <= 100
            ) {
                rows = await augmentEmotoradLifecycleCustomerZoneFromPincode(getAdditionalColumns, rows, {
                    intellsysBaseUrl,
                    accessToken: heimdallUser.accessToken,
                    journeysModuleConfiguration,
                    eventTemplates,
                    users,
                    userTemplates,
                    organizationalUnits,
                    organizationalUnitTemplates,
                    btlActivities,
                    products,
                });
            }
            const rowsWithDerivedStatus: Array<EventContainerWithEvents> =
                isInQueueView === true
                    ? rows.map((journey) => {
                          const inQueueStatus = getInQueueAssignmentStatus(journey).toLowerCase();
                          if (isEmotoradWorkspace(workspace) === true) {
                              return {
                                  ...journey,
                                  [EMOTORAD_INQUEUE_STATUS_FIELD]: isJourneyAssignedForEmotoradInQueue(journey)
                                      ? LIST_INQUEUE_ASSIGNMENT_STATUS.assigned
                                      : LIST_INQUEUE_ASSIGNMENT_STATUS.unassigned,
                              };
                          }
                          if (isBobcardWorkspace(workspace) === true) {
                              return {...journey, [BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID]: inQueueStatus};
                          }
                          if (isAmaronWorkspace(workspaceCapabilities) === true) {
                              return {
                                  ...journey,
                                  [AMARON_INQUEUE_STATUS_FIELD]: getInQueueAssignmentSyntheticStatusValue(
                                      getInQueueAssignmentStatus(journey) === "Assigned",
                                  ),
                              };
                          }
                          return journey;
                      })
                    : rows;
            const filteredByCustomerName =
                frontendCustomerNameFilters.length > 0
                    ? rowsWithDerivedStatus.filter((journey) =>
                          frontendCustomerNameFilters.every((lineItem) =>
                              doesFrontendCustomerNameMatchFilter(journey, lineItem),
                          ),
                      )
                    : rowsWithDerivedStatus;
            const filteredRows =
                frontendBobcardApplicationStatusFilters.length > 0
                    ? filteredByCustomerName.filter((journey) =>
                          frontendBobcardApplicationStatusFilters.every((lineItem) =>
                              doesFrontendBobcardApplicationStatusMatchFilter(journey, lineItem),
                          ),
                      )
                    : filteredByCustomerName;
            rows =
                usesClientSidePostFetchFiltering === true
                    ? filteredRows.slice(offset, offset + limit)
                    : filteredRows;
            const calendarConfig =
                isInQueueView === true
                    ? journeysModuleConfiguration.configuration.eventContainersPage.inQueueCalendarConfig
                    : (journeysModuleConfiguration.configuration.eventContainersPage.eventContainerCalendarConfig ??
                      journeysModuleConfiguration.configuration.eventContainersPage.inQueueCalendarConfig);
            const calendarTitleTemplate = calendarConfig?.calendarTitleOttopilotStringTemplate;
            const calendarStartDateColumnId = calendarConfig?.calendarStartDateColumnId;
            if (calendarTitleTemplate === undefined || calendarStartDateColumnId === undefined) {
                return rows;
            }
            if (isEmotoradWorkspace(workspace) === true && isInQueueView !== true) {
                return rows.map((journey) => {
                    const startRaw =
                        journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID];
                    const startDate =
                        typeof startRaw === "string" && startRaw.trim() !== ""
                            ? startRaw
                            : journey.updatedAt;
                    const title = processOttopilotStringTemplate(
                        EMOTORAD_LIFECYCLE_CALENDAR_TITLE_OTTOPILOT_TEMPLATE,
                        journey.additionalColumnValues as Record<Uuid, unknown>,
                    );
                    return {...journey, title, startDate, endDate: startDate, isAlldayEvent: false};
                });
            }
            return rows.map((journey) => {
                const title = processOttopilotStringTemplate(
                    calendarTitleTemplate,
                    journey.additionalColumnValues as Record<Uuid, unknown>,
                );
                if (amaronJourneyCalendarDisplayMetrics !== undefined) {
                    return {...journey, title, isAlldayEvent: false};
                }
                const startRaw = journey.additionalColumnValues[calendarStartDateColumnId as Uuid];
                const startDate =
                    typeof startRaw === "string" && startRaw.trim() !== ""
                        ? startRaw
                        : journey.updatedAt;
                return {...journey, title, startDate, endDate: startDate, isAlldayEvent: false};
            });
        },
        rowCountQueryFn: async ({filterConfiguration, searchConfiguration}) => {
            const bobcardAssignmentStatusFiltersForCount =
                isBobcardWorkspace(workspace) === true
                    ? filterConfiguration.lineItems.filter((lineItem) => lineItem.columnId === BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID)
                    : [];
            const frontendCustomerNameFilters =
                isBobcardWorkspace(workspace) === true
                    ? filterConfiguration.lineItems.filter((lineItem) => lineItem.columnId === BOBCARD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID)
                    : [];
            const frontendBobcardApplicationStatusFilters: FilterConfiguration["lineItems"] = [];
            const bobcardLeadOwnerColumnIdForCount =
                isBobcardWorkspace(workspace) === true ? (leadOwnerColumnId ?? BOBCARD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID) : undefined;
            const bobcardAssignmentBackendLineItemsForCount: FilterConfiguration["lineItems"] =
                bobcardLeadOwnerColumnIdForCount !== undefined && isInQueueView !== true
                    ? bobcardAssignmentStatusFiltersForCount.flatMap((lineItem) => {
                          const values = lineItem.operation === FilterOperation.in ? lineItem.values.map((value) => value.toLowerCase()) : [];
                          const wantsAssigned = values.includes("assigned");
                          const wantsUnassigned = values.includes("unassigned");
                          if (wantsAssigned === wantsUnassigned) {
                              return [];
                          }
                          return [
                              {
                                  columnId: bobcardLeadOwnerColumnIdForCount,
                                  operation: wantsAssigned ? FilterOperation.notEquals : FilterOperation.equals,
                                  value: null,
                              },
                          ];
                      })
                    : [];
            const inQueuePartitionForCount =
                isInQueueView === true && inQueueAssignmentOwnerColumnId !== null
                    ? partitionInQueueFiltersForBackend({
                          lineItems: filterConfiguration.lineItems,
                          statusFilterColumnIds: inQueueUiStatusFilterColumnIds,
                          leadOwnerColumnId: inQueueAssignmentOwnerColumnId,
                      })
                    : null;
            const backendLineItems = (
                inQueuePartitionForCount !== null
                    ? [...inQueuePartitionForCount.backendLineItems, ...inQueuePartitionForCount.inQueueAssignmentBackendLineItems]
                    : filterConfiguration.lineItems.filter(
                          (lineItem) =>
                              !(isBobcardWorkspace(workspace) === true && lineItem.columnId === BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID) &&
                              !(isBobcardWorkspace(workspace) === true && lineItem.columnId === BOBCARD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID) &&
                              !(isAmaronWorkspace(workspaceCapabilities) === true && isAmaronJourneyDispositionUiFilterColumn(lineItem.columnId) === true) &&
                              !(isEmotoradLifecycleOverviewView === true && isJourneyDispositionUiFilterColumn(lineItem.columnId) === true),
                      )
            ).filter(
                (lineItem) =>
                    !(
                        isBobcardWorkspace(workspace) === true &&
                        bobcardAssignmentBackendLineItemsForCount.length > 0 &&
                        bobcardLeadOwnerColumnIdForCount !== undefined &&
                        lineItem.columnId === bobcardLeadOwnerColumnIdForCount &&
                        lineItem.operation === FilterOperation.notEquals &&
                        lineItem.value === null
                    ),
            );
            const amaronBackendLineItemsForCount =
                isAmaronWorkspace(workspaceCapabilities) === true && isAmaronInQueueView !== true
                    ? appendAmaronJourneyDispositionApiFilterIfMissing(backendLineItems, filterConfiguration.lineItems)
                    : backendLineItems;
            const filterConfigurationForBackend: FilterConfiguration = {
                ...filterConfiguration,
                lineItems: [...amaronBackendLineItemsForCount, ...bobcardAssignmentBackendLineItemsForCount].map(normalizeEnumFilterLineItem),
            };
            if (isBobcardWorkspace(workspace) === true && isBobcardOrAmaronLeadsView === true && leadOwnerColumnId !== undefined) {
                const effectiveFilterConfiguration = {
                    ...filterConfigurationForBackend,
                    lineItems: filterConfigurationForBackend.lineItems.filter(
                        (lineItem) => !(lineItem.columnId === leadOwnerColumnId && lineItem.operation === FilterOperation.notEquals && lineItem.value === null),
                    ),
                };
                const mappedFilterConfiguration = mapFilterConfigurationToFields(applyJourneysApiFilterConfiguration(effectiveFilterConfiguration));
                const mappedSortConfiguration = mapSortConfigurationToFields({
                    revisionId: uniqueUuid`3e2519d9-3f68-4f75-8642-582f8cb0f5ea`,
                    lineItems: [],
                });
                const journeysResult = await getEventContainersWithEvents({
                    workspaceId: workspace.id,
                    offset: 0,
                    limit: JOURNEYS_CLIENT_SIDE_FILTER_PROBE_FETCH_LIMIT,
                    filters: toEventContainerFilters(mappedFilterConfiguration, searchConfiguration),
                    orderBy: toEventContainerOrderBy(mappedSortConfiguration),
                    templateId: journeysModuleConfiguration.id,
                    intellsysBaseUrl: intellsysBaseUrl,
                    accessToken: heimdallUser.accessToken,
                });
                if (journeysResult.success !== true) {
                    throw journeysResult.error;
                }
                const frontendFilteredByCustomerName =
                    frontendCustomerNameFilters.length > 0
                        ? journeysResult.data.filter((journey) =>
                              frontendCustomerNameFilters.every((lineItem) => doesFrontendCustomerNameMatchFilter(journey, lineItem)),
                          )
                        : journeysResult.data;
                const frontendFilteredByAppStatus =
                    frontendBobcardApplicationStatusFilters.length > 0
                        ? frontendFilteredByCustomerName.filter((journey) =>
                              frontendBobcardApplicationStatusFilters.every((lineItem) => doesFrontendBobcardApplicationStatusMatchFilter(journey, lineItem)),
                          )
                        : frontendFilteredByCustomerName;
                return frontendFilteredByAppStatus.filter((journey) => {
                    const leadOwnerValue = journey.additionalColumnValues[leadOwnerColumnId];
                    return leadOwnerValue !== null && leadOwnerValue !== undefined && String(leadOwnerValue) !== "";
                }).length as any;
            }

            const usesClientSidePostFetchFilteringForCount = frontendCustomerNameFilters.length > 0 || frontendBobcardApplicationStatusFilters.length > 0;

            if (usesClientSidePostFetchFilteringForCount === true) {
                const mappedFilterConfiguration = mapFilterConfigurationToFields(applyJourneysApiFilterConfiguration(filterConfigurationForBackend));
                const mappedSortConfiguration = mapSortConfigurationToFields({
                    revisionId: uniqueUuid`3e2519d9-3f68-4f75-8642-582f8cb0f5eb`,
                    lineItems: [],
                });
                const journeysResult = await getEventContainersWithEvents({
                    workspaceId: workspace.id,
                    offset: 0,
                    limit: JOURNEYS_CLIENT_SIDE_FILTER_PROBE_FETCH_LIMIT,
                    filters: toEventContainerFilters(mappedFilterConfiguration, searchConfiguration),
                    orderBy: toEventContainerOrderBy(mappedSortConfiguration),
                    templateId: journeysModuleConfiguration.id,
                    intellsysBaseUrl: intellsysBaseUrl,
                    accessToken: heimdallUser.accessToken,
                });
                if (journeysResult.success !== true) {
                    throw journeysResult.error;
                }
                let rows: Array<EventContainerWithEvents> =
                    isAmaronWorkspace(workspaceCapabilities) === true
                        ? journeysResult.data.map((journey) =>
                              amaronJourneySyntheticFunction(
                                  journey,
                                  getOptionalEventContainerDispositionId(journey, journeysModuleConfiguration),
                              ),
                          )
                        : journeysResult.data;
                const filteredByCustomerName =
                    frontendCustomerNameFilters.length > 0
                        ? rows.filter((journey) => frontendCustomerNameFilters.every((lineItem) => doesFrontendCustomerNameMatchFilter(journey, lineItem)))
                        : rows;
                return (
                    frontendBobcardApplicationStatusFilters.length > 0
                        ? filteredByCustomerName.filter((journey) =>
                              frontendBobcardApplicationStatusFilters.every((lineItem) => doesFrontendBobcardApplicationStatusMatchFilter(journey, lineItem)),
                          )
                        : filteredByCustomerName
                ).length as any;
            }

            const mappedFilterConfiguration = mapFilterConfigurationToFields(applyJourneysApiFilterConfiguration(filterConfigurationForBackend));
            const journeysLengthResult = await countEventContainersWithEvents({
                workspaceId: workspace.id,
                filters: toEventContainerFilters(
                    mappedFilterConfiguration,
                    searchConfiguration,
                ),
                templateId: journeysModuleConfiguration.id,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (journeysLengthResult.success !== true) {
                throw journeysLengthResult.error;
            }
            return journeysLengthResult.data;
        },
        columnValuesQueryFn: async ({columnId}) => {
            if (isAmaronLeadsView === true && String(columnId) === String(AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID)) {
                const statusNavConfig = getAmaronJourneyLeadsStatusNavConfig();
                const activeStatusId = resolveModuleStatusIdForFilter({
                    statusIdFromSearch: search.statusId,
                    validStatusIds: getAmaronModuleStatusNavValidStatusIds(statusNavConfig),
                    ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
                });
                return resolveAmaronModuleStatusFilterPickerValues({
                    statusNavConfig,
                    activeStatusId,
                });
            }
            if (isDuluxLeadsView === true && String(columnId) === String(DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID)) {
                const statusNavConfig = getDuluxJourneyLeadsStatusNavConfig();
                const activeStatusId = resolveModuleStatusIdForFilter({
                    statusIdFromSearch: search.statusId,
                    validStatusIds: getDuluxModuleStatusNavValidStatusIds(statusNavConfig),
                    ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
                });
                return resolveDuluxModuleStatusFilterPickerValues({
                    statusNavConfig,
                    activeStatusId,
                });
            }
            if (isEmotoradInQueueView === true && emotoradInQueueStatusFilterColumnIds.has(columnId)) {
                return ["Assigned", "Unassigned"];
            }
            if (isBobcardWorkspace(workspace) === true && bobcardStatusFilterColumnIds.has(columnId)) {
                return ["Assigned", "Unassigned"];
            }
            if (isAmaronInQueueView === true && amaronInQueueStatusFilterColumnIds.has(columnId)) {
                return getAmaronInqueueStatusFilterPickerValues();
            }
            if (isBobcardWorkspace(workspace) === true && columnId === BOBCARD__EVENT_CONTAINER__STATUS__COLUMN_ID && isInQueueView !== true) {
                return Object.values(bobcardApplicationStatuses)
                    .map((s) => s.displayName)
                    .sort();
            }
            const resolvedColumnId = resolveTableColumnField(columnId);
            const journeysColumnValuesResult = await getEventContainerColumnValues({
                workspaceId: workspace.id,
                column: resolvedColumnId,
                templateId: journeysModuleConfiguration.id,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (journeysColumnValuesResult.success !== true) {
                throw journeysColumnValuesResult.error;
            }
            const journeysColumnValues = compactEventContainerColumnValues(
                journeysColumnValuesResult.data,
            );

            if (isEmotoradActionPlatformColumnId(resolvedColumnId) === true) {
                return sanitizeEmotoradFilterColumnValues(journeysColumnValues);
            }
            return journeysColumnValues;
        },
        columns: {
            revisionId: uniqueUuid`70b76934-ebd6-4aae-a73a-0d61b8f38cee`,
            lineItems: (() => {
                let baseColumns = (
                    journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns !== undefined
                        ? eventContainerTableViewColumnsToOttopilotColumns(
                              getAdditionalColumns,
                              journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns,
                              {
                                  userDefinedObjectOptionsByTemplateId,
                                  users,
                                  userTemplates,
                                  platformUserDetailsByUserId,
                                  formatDateTime,
                                  eventContainerAdditionalColumns: getAdditionalColumns(journeysModuleConfiguration),
                                  eventTemplates,
                                  btlActivities,
                                  getOuDisplayName,
                                  getProductCatalogueDisplayName: (productCatalogueId: Uuid) => {
                                      const productCatalogue = products.find((p) => p.id === productCatalogueId);
                                      return productCatalogue !== undefined
                                          ? getProductCatalogueDisplayName(productCatalogue, productCatalogueTemplates)
                                          : productCatalogueId;
                                  },
                                  emotoradLifecycleZsmColumnDisplayName: isEmotoradLifecycleOverviewView === true ? "RM Name" : undefined,
                              },
                          )
                        : [
                              // Default columns for non-Dulux workspaces
                              {
                                  id: "id",
                                  type: OttopilotColumnType.uuid,
                                  displayName: "ID",
                                  valueGetter: (journey) => journey.id,
                                  // filteringCapability_in: {},
                              },
                              {
                                  id: "createdBy",
                                  type: OttopilotColumnType.uuid,
                                  displayName: "Created by",
                                  valueGetter: (journey) => journey.createdBy,
                                  valueFormatter: (value) => getUserDisplayName(value, users, userTemplates),
                              },
                              {
                                  id: "createdAt",
                                  type: OttopilotColumnType.iso8601DateTime,
                                  displayName: "Created on",
                                  valueGetter: (journey) => journey.createdAt,
                                  valueFormatter: (value) => formatDateTime(value),
                                  sortingCapability: true,
                                  filteringCapability_contains: true,
                              },
                              {
                                  id: "updatedBy",
                                  type: OttopilotColumnType.uuid,
                                  displayName: "Modified by",
                                  valueGetter: (journey) => journey.updatedBy,
                                  valueFormatter: (value) => getUserDisplayName(value, users, userTemplates),
                                  // filteringCapability_in: true,
                              },
                              {
                                  id: "updatedAt",
                                  type: OttopilotColumnType.iso8601DateTime,
                                  displayName: "Modified at",
                                  valueGetter: (journey) => journey.updatedAt,
                                  valueFormatter: (value) => formatDateTime(value),
                                  sortingCapability: true,
                                  filteringCapability_contains: true,
                              },
                              {
                                  id: "status",
                                  type: OttopilotColumnType.uuid,
                                  displayName: "Status",
                                  valueGetter: (journey) => journey.status as any,
                                  valueFormatter: (value) => eventContainerStatusToDisplayName(value as any),
                              },
                              ...getAdditionalColumns(journeysModuleConfiguration).map((column): OttopilotColumn<OttopilotEventContainer> => {
                                  const isTimestamp = column.type === UdoColumnType.timestamp;

                                  if (isTimestamp) {
                                      return {
                                          id: column.id,
                                          type: OttopilotColumnType.iso8601DateTime,
                                          displayName: column.displayName,
                                          valueGetter: (journey) => {
                                              const value = journey.additionalColumnValues[column.id];
                                              // Return undefined if value is null/undefined, otherwise cast to Iso8601DateTime
                                              return (value ?? undefined) as any;
                                          },
                                          valueFormatter: (value) => formatDateTime(value),
                                          sortingCapability: true,
                                          filteringCapability_contains: true,
                                      };
                                  }

                                  return {
                                      id: column.id,
                                      type: OttopilotColumnType.string,
                                      displayName: column.displayName,
                                      valueGetter: (journey): string => {
                                          const rawValue = journey.additionalColumnValues[column.id];
                                          return rawValue === null || rawValue === undefined ? "" : String(rawValue);
                                      },
                                      valueFormatter: (value: string) => {
                                          return getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
                                              value: value as any,
                                              users: users,
                                              userTemplates: userTemplates,
                                              organizationalUnits: organizationalUnits,
                                              organizationalUnitTemplates: organizationalUnitTemplates,
                                              products: products,
                                          });
                                      },
                                      sortingCapability: true,
                                      filteringCapability_contains: true,
                                  };
                              }),
                          ]
                ) as Array<OttopilotColumn<EventContainerWithEvents>>;

                if (isBobcardWorkspace(workspace)) {
                    baseColumns = baseColumns.map((column): OttopilotColumn<EventContainerWithEvents> => {
                        if (column.id === BOBCARD__EVENT_CONTAINER__STATUS__COLUMN_ID && !isInQueueView && column.type === OttopilotColumnType.string) {
                            return {
                                ...column,
                                valueFormatter: (value: string) => {
                                    const status = Object.values(bobcardApplicationStatuses).find((s) => s.id === value);
                                    return status?.displayName ?? String(value ?? "-");
                                },
                            };
                        }
                        if (column.id === BOBCARD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID && column.type === OttopilotColumnType.string) {
                            return {
                                ...column,
                                valueGetter: (journey) => {
                                    const firstName = journey.additionalColumnValues[BOBCARD__EVENT_CONTAINER__FIRST_NAME__COLUMN_ID];
                                    const lastName = journey.additionalColumnValues[BOBCARD__EVENT_CONTAINER__LAST_NAME__COLUMN_ID];
                                    const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
                                    return fullName !== "" ? fullName : "-";
                                },
                                valueFormatter: (value: string) => String(value),
                            };
                        }
                        if (column.id === "updatedAt") {
                            return {
                                ...column,
                                filteringCapability_contains: true,
                            };
                        }
                        if (column.id === "createdAt") {
                            return {
                                ...column,
                                filteringCapability_contains: false,
                            };
                        }
                        return column;
                    });
                }
                if (isEmotoradJourneysCustomer === true) {
                    baseColumns = baseColumns.map((column): OttopilotColumn<EventContainerWithEvents> => {
                        if (column.id === "createdAt") {
                            return {...column, displayName: "Created on"};
                        }
                        if (column.id === "updatedAt") {
                            return {...column, displayName: "Modified at"};
                        }
                        if (column.id === "updatedBy") {
                            return {...column, displayName: "Modified by"};
                        }
                        return column;
                    });
                }
                if (isInQueueView === true && isAmaronWorkspace(workspaceCapabilities) !== true) {
                    const inQueueBaseColumns = baseColumns.map((column) => {
                        if (isEmotoradInQueueView === true) {
                            if (column.id === EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID) {
                                return {
                                    ...column,
                                    displayName: "RM Name",
                                };
                            }
                            if (column.id === EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID) {
                                const journeyActionPlatformColumn = getAdditionalColumns(journeysModuleConfiguration).find(
                                    (item) => item.id === EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
                                );
                                const inQueueActionPlatformLabelsById = (() => {
                                    const map = new Map<string, string>();
                                    const opts =
                                        journeyActionPlatformColumn === undefined
                                            ? undefined
                                            : getAdditionalColumnOptions(journeyActionPlatformColumn, userDefinedObjectOptionsByTemplateId);
                                    if (opts !== undefined) {
                                        for (const option of opts) {
                                            const key = String(option.id);
                                            if (!map.has(key)) {
                                                map.set(key, option.displayName);
                                            }
                                        }
                                    }
                                    for (const option of getEmotoradCurrentPlatformSelectOptions()) {
                                        const key = String(option.id);
                                        if (!map.has(key)) {
                                            map.set(key, option.displayName);
                                        }
                                    }
                                    return map;
                                })();
                                return {
                                    ...column,
                                    displayName: journeyActionPlatformColumn?.displayName ?? column.displayName ?? "Action Platform",
                                    valueGetter: (journey: EventContainerWithEvents) => {
                                        const raw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID];
                                        if (raw === null || raw === undefined || String(raw).trim() === "") {
                                            return "";
                                        }
                                        return String(raw).trim();
                                    },
                                    valueFormatter: (raw: string) => {
                                        if (raw === "") {
                                            return "";
                                        }
                                        return inQueueActionPlatformLabelsById.get(raw) ?? formatEmotoradCurrentPlatformCell(raw);
                                    },
                                } as OttopilotColumn<EventContainerWithEvents>;
                            }
                            if (column.id === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID) {
                                return {
                                    ...column,
                                    valueGetter: (journey: EventContainerWithEvents) => {
                                        const value = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID];
                                        return typeof value === "string" ? value : "";
                                    },
                                    valueFormatter: (value: unknown) => {
                                        if (value === null || value === undefined || value === "") {
                                            return "-";
                                        }
                                        if (typeof value === "string") {
                                            const ou = organizationalUnits.find((unit) => unit.id === value);
                                            if (ou !== undefined) {
                                                const ouTemplate = organizationalUnitTemplates.find((template) => template.id === ou.templateId);
                                                if (ouTemplate !== undefined) {
                                                    return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
                                                }
                                            }
                                            return value;
                                        }
                                        return String(value);
                                    },
                                } as OttopilotColumn<EventContainerWithEvents>;
                            }
                            const isSystemStatusColumn = column.id === "status";
                            const isConfiguredStatusColumn = emotoradInQueueStatusColumnId !== null && column.id === emotoradInQueueStatusColumnId;
                            const isStatusColumn = isSystemStatusColumn || isConfiguredStatusColumn;
                            if (isStatusColumn !== true) {
                                return column;
                            }
                            return {
                                ...column,
                                displayName: "Status",
                                type: OttopilotColumnType.string,
                                filteringCapability_contains: true,
                                sortingCapability: true,
                                valueGetter: (journey: EventContainerWithEvents) => {
                                    if (emotoradInQueueLifecycleOwnerColumnId === null) {
                                        return "Unassigned";
                                    }
                                    return getInQueueAssignmentStatus(journey);
                                },
                                valueFormatter: (value: string) => value,
                                cellRenderer: (journey: EventContainerWithEvents) => {
                                    const label = getInQueueAssignmentStatus(journey);
                                    const isAssigned = label === "Assigned";
                                    return (
                                        <span
                                            className="inline-flex items-center px-2 py-0.5 text-xs font-medium"
                                            style={{
                                                borderRadius: "3px",
                                                borderWidth: "0.5px",
                                                borderStyle: "solid",
                                                borderColor: isAssigned ? token("color.border.success") : token("color.chart.yellow.bolder"),
                                                background: isAssigned ? token("color.background.accent.lime.subtlest") : token("color.background.accent.yellow.subtlest"),
                                            }}
                                        >
                                            {label}
                                        </span>
                                    );
                                },
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (isBobcardWorkspace(workspace) === true && bobcardStatusFilterColumnIds.has(column.id)) {
                            return {
                                ...column,
                                displayName: column.id === BOBCARD__EVENT_CONTAINER__STATUS__COLUMN_ID ? "Status" : column.displayName,
                                type: OttopilotColumnType.string,
                                filteringCapability_contains: true,
                                sortingCapability: true,
                                valueGetter: (journey: EventContainerWithEvents) => {
                                    const ownerRaw = leadOwnerColumnId !== undefined ? journey.additionalColumnValues[leadOwnerColumnId] : null;
                                    const isAssigned = ownerRaw !== null && ownerRaw !== undefined && (typeof ownerRaw !== "string" || ownerRaw.trim() !== "");
                                    return isAssigned ? "Assigned" : "Unassigned";
                                },
                                valueFormatter: (value: string) => value,
                                cellRenderer: (journey: EventContainerWithEvents) => {
                                    const ownerRaw = leadOwnerColumnId !== undefined ? journey.additionalColumnValues[leadOwnerColumnId] : null;
                                    const isAssigned = ownerRaw !== null && ownerRaw !== undefined && (typeof ownerRaw !== "string" || ownerRaw.trim() !== "");
                                    const label = isAssigned ? "Assigned" : "Unassigned";
                                    return (
                                        <span
                                            className="inline-flex items-center px-2 py-0.5 text-xs font-medium"
                                            style={{
                                                borderRadius: "3px",
                                                borderWidth: "0.5px",
                                                borderStyle: "solid",
                                                borderColor: isAssigned ? token("color.border.success") : token("color.chart.yellow.bolder"),
                                                background: isAssigned ? token("color.background.accent.lime.subtlest") : token("color.background.accent.yellow.subtlest"),
                                            }}
                                        >
                                            {label}
                                        </span>
                                    );
                                },
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (isAmaronInQueueView === true && column.id === AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID) {
                            return {
                                ...column,
                                displayName: "Status",
                                type: OttopilotColumnType.string,
                                filteringCapability_contains: true,
                                sortingCapability: true,
                                valueGetter: (journey: EventContainerWithEvents) => getInQueueAssignmentStatus(journey),
                                valueFormatter: (value: string) => formatAmaronInqueueStatusFilterValue(value),
                                filteringCapability_in: {
                                    valuesQueryFn: async () => getAmaronInqueueStatusFilterPickerValues(),
                                },
                                cellRenderer: (journey: EventContainerWithEvents) => {
                                    const label = getInQueueAssignmentStatus(journey);
                                    const isAssigned = label === "Assigned";
                                    return (
                                        <span
                                            className="inline-flex items-center px-2 py-0.5 text-xs font-medium"
                                            style={{
                                                borderRadius: "3px",
                                                borderWidth: "0.5px",
                                                borderStyle: "solid",
                                                borderColor: isAssigned ? token("color.border.success") : token("color.chart.yellow.bolder"),
                                                background: isAssigned ? token("color.background.accent.lime.subtlest") : token("color.background.accent.yellow.subtlest"),
                                            }}
                                        >
                                            {label}
                                        </span>
                                    );
                                },
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        return column;
                    });
                    const columnById = new Map(inQueueBaseColumns.map((column) => [column.id, column]));
                    const inQueueColumns = inQueueVisibleColumns
                        .map((columnId) => columnById.get(columnId))
                        .filter((column): column is OttopilotColumn<EventContainerWithEvents> => column !== undefined);
                    return inQueueColumns.length > 0 ? inQueueColumns : baseColumns;
                }
                if (isEmotoradLifecycleOverviewView === true) {
                    const columnById = new Map(baseColumns.map((column) => [column.id, column]));
                    const additionalColumnById = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [column.id, column]));
                    const lifecycleOverviewLabelById = new Map<string, string>([
                        [EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID, "AON"],
                        [EMOTORAD__EVENT_CONTAINER__CATEGORY__COLUMN_ID, "Category"],
                        [EMOTORAD__EVENT_CONTAINER__CREATED_BY_ROLE__COLUMN_ID, "Created by (Role)"],
                        [EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID, "Lifecycle Owner"],
                        [EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID, "L1 Disposition"],
                        [EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID, "L2 Disposition"],
                        [EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID, "L3 Disposition"],
                        [EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID, "RM Name"],
                        [EMOTORAD__EVENT_CONTAINER__TEST_RIDES_COUNT__COLUMN_ID, "Test rides count"],
                        ["createdAt", "Created on"],
                        ["updatedAt", "Modified at"],
                        ["updatedBy", "Modified by"],
                    ]);
                    const resolveLifecycleDispositionIds = (args: {l1Values?: Array<string>; l2Values?: Array<string>; l3Values?: Array<string>}): Array<string> => {
                        return getEmotoradDispositionIdsForLevels(args).map((id) => String(id));
                    };
                    const findOrganizationalUnitByPossibleId = (value: unknown): OrganizationalUnit | undefined => {
                        if (typeof value !== "string" || value.trim() === "") {
                            return undefined;
                        }
                        return organizationalUnits.find((unit) => unit.id === value);
                    };
                    const getOrganizationalUnitTemplate = (organizationalUnit: OrganizationalUnit | undefined): OrganizationalUnitTemplate | undefined => {
                        if (organizationalUnit === undefined) {
                            return undefined;
                        }
                        return organizationalUnitTemplates.find((template) => template.id === organizationalUnit.templateId);
                    };
                    const getOrganizationalUnitFieldValueByDisplayNames = (
                        organizationalUnit: OrganizationalUnit | undefined,
                        displayNames: ReadonlyArray<string>,
                    ): string | null => {
                        const organizationalUnitTemplate = getOrganizationalUnitTemplate(organizationalUnit);
                        if (organizationalUnit === undefined || organizationalUnitTemplate === undefined) {
                            return null;
                        }
                        const candidateDisplayNames = new Set(displayNames.map((name) => normalizeLabel(name)));
                        const field = organizationalUnitTemplate.additionalColumns.find((column) => candidateDisplayNames.has(normalizeLabel(column.displayName)));
                        if (field === undefined) {
                            return null;
                        }
                        const fieldValue = organizationalUnit.additionalColumnValues[field.id];
                        if (fieldValue === null || fieldValue === undefined || fieldValue === "") {
                            return null;
                        }
                        return String(fieldValue);
                    };
                    const getOrganizationalUnitFieldValueByHeuristics = (
                        organizationalUnit: OrganizationalUnit | undefined,
                        kind: "contact" | "email" | "pincode",
                    ): string | null => {
                        const organizationalUnitTemplate = getOrganizationalUnitTemplate(organizationalUnit);
                        if (organizationalUnit === undefined || organizationalUnitTemplate === undefined) {
                            return null;
                        }

                        if (kind === "contact") {
                            const phoneByType = organizationalUnitTemplate.additionalColumns.find(
                                (column) => column.type === OrganizationalUnitAdditionalColumnType.indianPhoneNumber,
                            );
                            if (phoneByType !== undefined) {
                                const value = organizationalUnit.additionalColumnValues[phoneByType.id];
                                if (value !== null && value !== undefined && String(value).trim() !== "") {
                                    return String(value);
                                }
                            }
                        }
                        if (kind === "email") {
                            const emailByType = organizationalUnitTemplate.additionalColumns.find((column) => column.type === OrganizationalUnitAdditionalColumnType.email);
                            if (emailByType !== undefined) {
                                const value = organizationalUnit.additionalColumnValues[emailByType.id];
                                if (value !== null && value !== undefined && String(value).trim() !== "") {
                                    return String(value);
                                }
                            }
                        }

                        const displayNamePattern = kind === "contact" ? /(contact|phone|mobile)/i : kind === "email" ? /email/i : /(pincode|pin[\s_-]?code|zip)/i;
                        const valuePattern = kind === "contact" ? /^\+?\d[\d\s-]{7,}$/ : kind === "email" ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/ : /^\d{5,7}$/;

                        const preferredByName = organizationalUnitTemplate.additionalColumns.find((column) => {
                            if (displayNamePattern.test(column.displayName) !== true) {
                                return false;
                            }
                            const value = organizationalUnit.additionalColumnValues[column.id];
                            return value !== null && value !== undefined && valuePattern.test(String(value).trim());
                        });
                        if (preferredByName !== undefined) {
                            return String(organizationalUnit.additionalColumnValues[preferredByName.id]);
                        }

                        const preferredByValue = organizationalUnitTemplate.additionalColumns.find((column) => {
                            const value = organizationalUnit.additionalColumnValues[column.id];
                            return value !== null && value !== undefined && valuePattern.test(String(value).trim());
                        });
                        if (preferredByValue !== undefined) {
                            return String(organizationalUnit.additionalColumnValues[preferredByValue.id]);
                        }

                        return null;
                    };
                    const formatLifecycleAssignedDealerValue = (value: unknown): string => {
                        if (value === null || value === undefined || value === "") {
                            return "-";
                        }
                        if (typeof value === "string") {
                            const ou = organizationalUnits.find((unit) => unit.id === value);
                            if (ou !== undefined) {
                                const ouTemplate = organizationalUnitTemplates.find((template) => template.id === ou.templateId);
                                if (ouTemplate !== undefined) {
                                    return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
                                }
                            }
                            return value;
                        }
                        return String(value);
                    };
                    const resolveLifecycleDealerFieldValue = (journey: EventContainerWithEvents, kind: "name" | "contact" | "email" | "pincode"): string => {
                        const assignedDealerRaw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID];
                        const assignedDealerOu =
                            findOrganizationalUnitByPossibleId(assignedDealerRaw) ??
                            findOrganizationalUnitByPossibleId(getJourneyOrganizationalUnitId(journey)) ??
                            findOrganizationalUnitByPossibleId(journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID]) ??
                            findOrganizationalUnitByPossibleId(journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID]) ??
                            findOrganizationalUnitByPossibleId(journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID]);
                        const getFieldFromAssignedDealerHierarchy = (
                            displayNames: ReadonlyArray<string>,
                            heuristicType: "contact" | "email" | "pincode",
                        ): string | null => {
                            if (assignedDealerOu === undefined) {
                                return null;
                            }
                            const visited = new Set<Uuid>();
                            let current: OrganizationalUnit | undefined = assignedDealerOu;
                            while (current !== undefined && visited.has(current.id) === false) {
                                visited.add(current.id);
                                const fromDisplayNames = getOrganizationalUnitFieldValueByDisplayNames(current, displayNames);
                                if (fromDisplayNames !== null) {
                                    return fromDisplayNames;
                                }
                                const fromHeuristics = getOrganizationalUnitFieldValueByHeuristics(current, heuristicType);
                                if (fromHeuristics !== null) {
                                    return fromHeuristics;
                                }
                                current = current.parentId !== null ? organizationalUnits.find((unit) => unit.id === current?.parentId) : undefined;
                            }
                            return null;
                        };
                        const extractDealershipIdsFromUserValue = (raw: unknown): Uuid[] => {
                            if (typeof raw === "string" && raw.trim() !== "") {
                                return [raw as Uuid];
                            }
                            if (Array.isArray(raw) === true) {
                                const ids = raw
                                    .map((item) => {
                                        if (typeof item === "string" && item.trim() !== "") {
                                            return item as Uuid;
                                        }
                                        if (typeof item === "object" && item !== null) {
                                            const obj = item as Record<string, unknown>;
                                            const candidate = obj.id ?? obj.value;
                                            if (typeof candidate === "string" && candidate.trim() !== "") {
                                                return candidate as Uuid;
                                            }
                                        }
                                        return null;
                                    })
                                    .filter((item): item is Uuid => item !== null);
                                return [...new Set(ids)];
                            }
                            if (typeof raw === "object" && raw !== null) {
                                const obj = raw as Record<string, unknown>;
                                const candidate = obj.id ?? obj.value;
                                if (typeof candidate === "string" && candidate.trim() !== "") {
                                    return [candidate as Uuid];
                                }
                            }
                            return [];
                        };
                        const getFieldFromDealershipUsers = (dealerOuId: Uuid | null, field: "contact" | "email" | "pincode"): string | null => {
                            if (dealerOuId === null) {
                                return null;
                            }
                            const fieldColumnId =
                                field === "contact"
                                    ? EMOTORAD__USER__CONTACT_NUMBER__COLUMN_ID
                                    : field === "email"
                                      ? EMOTORAD__USER__EMAIL__COLUMN_ID
                                      : EMOTORAD__USER__ADDRESS_PINCODE__COLUMN_ID;
                            for (const user of users) {
                                const dealershipRaw = user.additionalColumnValues[EMOTORAD__USER__DEALERSHIP__COLUMN_ID];
                                const dealershipIds = extractDealershipIdsFromUserValue(dealershipRaw);
                                if (dealershipIds.includes(dealerOuId) !== true) {
                                    continue;
                                }
                                const rawValue = user.additionalColumnValues[fieldColumnId];
                                if (typeof rawValue === "string" && rawValue.trim() !== "") {
                                    return rawValue.trim();
                                }
                            }
                            return null;
                        };
                        const assignedDealerId =
                            assignedDealerOu?.id ?? (typeof assignedDealerRaw === "string" && assignedDealerRaw.trim() !== "" ? (assignedDealerRaw as Uuid) : null);

                        if (kind === "name") {
                            return formatLifecycleAssignedDealerValue(assignedDealerRaw);
                        }
                        if (kind === "contact") {
                            const rawValue = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID];
                            if (rawValue !== null && rawValue !== undefined && String(rawValue).trim() !== "") {
                                const rawValueOu = findOrganizationalUnitByPossibleId(rawValue);
                                // Only return the raw value directly if it is NOT a UUID (i.e. it is an actual contact string).
                                const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(rawValue).trim());
                                if (rawValueOu === undefined && looksLikeUuid !== true) {
                                    return String(rawValue);
                                }
                            }
                            return (
                                getFieldFromAssignedDealerHierarchy(
                                    ["Contact Number", "Phone Number", "Mobile Number", "Dealer Contact", "Dealer Contact Number"],
                                    "contact",
                                ) ??
                                getFieldFromDealershipUsers(assignedDealerId, "contact") ??
                                "-"
                            );
                        }
                        if (kind === "email") {
                            const rawValue = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID];
                            if (rawValue !== null && rawValue !== undefined && String(rawValue).trim() !== "") {
                                const rawValueOu = findOrganizationalUnitByPossibleId(rawValue);
                                // Only return the raw value directly if it is NOT a UUID (i.e. it is an actual email string).
                                const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(rawValue).trim());
                                if (rawValueOu === undefined && looksLikeUuid !== true) {
                                    return String(rawValue);
                                }
                            }
                            return (
                                getFieldFromAssignedDealerHierarchy(["Email", "Email Address", "Dealer Email"], "email") ??
                                getFieldFromDealershipUsers(assignedDealerId, "email") ??
                                "-"
                            );
                        }
                        const rawValue = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID];
                        if (rawValue !== null && rawValue !== undefined && String(rawValue).trim() !== "") {
                            const rawValueOu = findOrganizationalUnitByPossibleId(rawValue);
                            if (rawValueOu === undefined) {
                                return String(rawValue);
                            }
                        }
                        return (
                            getFieldFromAssignedDealerHierarchy(["Pincode", "Dealer Pincode", "Pin Code"], "pincode") ??
                            getFieldFromDealershipUsers(assignedDealerId, "pincode") ??
                            "-"
                        );
                    };
                    const resolveLifecycleDealerOuId = (journey: EventContainerWithEvents): Uuid | null => {
                        const assignedDealerRaw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID];
                        const assignedDealerOu =
                            findOrganizationalUnitByPossibleId(assignedDealerRaw) ??
                            findOrganizationalUnitByPossibleId(getJourneyOrganizationalUnitId(journey)) ??
                            findOrganizationalUnitByPossibleId(journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID]) ??
                            findOrganizationalUnitByPossibleId(journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID]) ??
                            findOrganizationalUnitByPossibleId(journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID]);
                        return assignedDealerOu?.id ?? (typeof assignedDealerRaw === "string" && assignedDealerRaw.trim() !== "" ? (assignedDealerRaw as Uuid) : null);
                    };
                    const getLifecycleDealerFieldByLabel = (dealerId: Uuid | null, labels: ReadonlyArray<string>): string => {
                        if (dealerId === null) {
                            return "";
                        }
                        const dealerOu = organizationalUnits.find((unit) => unit.id === dealerId);
                        if (dealerOu === undefined) {
                            return "";
                        }
                        const template = organizationalUnitTemplates.find((item) => item.id === dealerOu.templateId);
                        if (template === undefined) {
                            return "";
                        }
                        for (const label of labels) {
                            const matchingColumn = template.additionalColumns.find((column) => {
                                return column.displayName.trim().toLowerCase() === label.toLowerCase();
                            });
                            if (matchingColumn === undefined) {
                                continue;
                            }
                            const rawValue = dealerOu.additionalColumnValues[matchingColumn.id];
                            if (rawValue !== null && rawValue !== undefined && String(rawValue).trim() !== "") {
                                return String(rawValue);
                            }
                        }
                        return "";
                    };
                    /**
                     * Same as Sales {@link resolveAssignedDealerZoneFromOuAncestorTree}: zone lives on an ancestor OU
                     * with Zone template (Name column), not on the dealership leaf.
                     */
                    const resolveLifecycleAssignedDealerZoneFromOuAncestorTree = (dealerOuId: Uuid | null): string => {
                        const zoneTemplateId = EmotoradOus.Zone as Uuid;
                        const zoneColumnLabels = ["Zone", "Dealer Zone", "Zone Name"] as const;

                        const ouNameFromNameColumn = (ou: (typeof organizationalUnits)[number]): string => {
                            const template = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
                            if (template === undefined) {
                                return "";
                            }
                            const nameColumn = template.additionalColumns.find((col) => col.displayName.trim().toLowerCase() === "name");
                            if (nameColumn === undefined) {
                                return "";
                            }
                            const nameValue = ou.additionalColumnValues[nameColumn.id];
                            return typeof nameValue === "string" && nameValue.trim() !== "" ? nameValue.trim() : "";
                        };

                        const zoneColumnValueOnOu = (ou: (typeof organizationalUnits)[number]): string | null => {
                            const template = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
                            if (template === undefined) {
                                return null;
                            }
                            for (const label of zoneColumnLabels) {
                                const column = template.additionalColumns.find((c) => c.displayName.trim().toLowerCase() === label.toLowerCase());
                                if (column === undefined) {
                                    continue;
                                }
                                const value = ou.additionalColumnValues[column.id];
                                if (value !== null && value !== undefined && String(value).trim() !== "") {
                                    return String(value).trim();
                                }
                            }
                            return null;
                        };

                        if (dealerOuId === null) {
                            return "";
                        }
                        const visited = new Set<Uuid>();
                        let current: (typeof organizationalUnits)[number] | undefined = organizationalUnits.find((unit) => unit.id === dealerOuId);
                        for (;;) {
                            if (current === undefined) {
                                break;
                            }
                            if (visited.has(current.id) === true) {
                                break;
                            }
                            const ou = current;
                            visited.add(ou.id);
                            if (ou.templateId === zoneTemplateId) {
                                const zoneName = ouNameFromNameColumn(ou);
                                if (zoneName !== "") {
                                    return zoneName;
                                }
                            }
                            current = ou.parentId !== null ? organizationalUnits.find((unit) => unit.id === ou.parentId) : undefined;
                        }
                        visited.clear();
                        current = organizationalUnits.find((unit) => unit.id === dealerOuId);
                        for (;;) {
                            if (current === undefined) {
                                break;
                            }
                            if (visited.has(current.id) === true) {
                                break;
                            }
                            const ou = current;
                            visited.add(ou.id);
                            const fromColumn = zoneColumnValueOnOu(ou);
                            if (fromColumn !== null) {
                                return fromColumn;
                            }
                            current = ou.parentId !== null ? organizationalUnits.find((unit) => unit.id === ou.parentId) : undefined;
                        }
                        return "";
                    };
                    const resolveLifecycleDealerCityStateZone = (journey: EventContainerWithEvents, kind: "city" | "state" | "zone"): string => {
                        const dealerId = resolveLifecycleDealerOuId(journey);
                        if (kind === "zone") {
                            const fromAncestorTree = resolveLifecycleAssignedDealerZoneFromOuAncestorTree(dealerId);
                            if (fromAncestorTree !== "") {
                                return fromAncestorTree;
                            }
                        }
                        const fromOu =
                            kind === "city"
                                ? getLifecycleDealerFieldByLabel(dealerId, ["City", "Dealer City"])
                                : kind === "state"
                                  ? getLifecycleDealerFieldByLabel(dealerId, ["State", "Dealer State"])
                                  : getLifecycleDealerFieldByLabel(dealerId, ["Zone", "Dealer Zone", "Zone Name"]);
                        if (fromOu !== "") {
                            return fromOu;
                        }
                        const columnId =
                            kind === "city"
                                ? EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID
                                : kind === "state"
                                  ? EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID
                                  : EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID;
                        const raw = journey.additionalColumnValues[columnId];
                        if (raw === undefined || raw === null || String(raw).trim() === "") {
                            return "-";
                        }
                        return String(raw);
                    };
                    const formatLifecycleKamRmStoredValue = (raw: string): string => {
                        const trimmed = raw.trim();
                        if (trimmed === "") {
                            return "-";
                        }
                        const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed);
                        if (looksLikeUuid === true) {
                            const userName = getUserDisplayName(trimmed as Uuid, users, userTemplates);
                            if (userName !== "?" && userName !== "-") {
                                return userName;
                            }
                        }
                        return trimmed;
                    };
                    const lifecycleOwnerStorageColumnId = leadOwnerColumnId ?? EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID;
                    const getLifecycleLeadOwnerStoredValue = (journey: EventContainerWithEvents): string => {
                        const raw = journey.additionalColumnValues[lifecycleOwnerStorageColumnId];
                        if (raw === null || raw === undefined) {
                            return "";
                        }
                        return String(raw).trim();
                    };
                    const getLifecycleKamRmStoredValue = (journey: EventContainerWithEvents, columnId: Uuid): string => {
                        const raw = journey.additionalColumnValues[columnId];
                        if (raw === null || raw === undefined) {
                            return "";
                        }
                        return String(raw).trim();
                    };
                    const findEmotoradLeadEventForJourney = (journey: EventContainerWithEvents) => {
                        return journey.events.find((ev) => String(ev.templateId) === String(EmotoradEventTypes.lead));
                    };
                    const leadEventTemplate = eventTemplates.find((t) => t.id === (EmotoradEventTypes.lead as Uuid));
                    const journeyCustomerStateColumn = additionalColumnById.get(EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID);
                    const journeyCustomerCityColumn = additionalColumnById.get(EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID);
                    const journeyCustomerZoneColumn = additionalColumnById.get(EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID);
                    const journeyActionPlatformColumn = additionalColumnById.get(EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID);
                    const enquiryCustomerStateColumn = getAdditionalColumns(leadEventTemplate).find((c) => c.id === EMOTORAD__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID);
                    const enquiryCustomerCityColumn = getAdditionalColumns(leadEventTemplate).find((c) => c.id === EMOTORAD__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID);
                    const enquiryCategoryColumn = getAdditionalColumns(leadEventTemplate).find((c) => c.id === EMOTORAD__EVENT__ENQUIRY__CATEGORY__COLUMN_ID);
                    const eventTemplateForJourneyColumnFormat = eventTemplates[0];
                    const lifecycleActionPlatformLabelsById = (() => {
                        const map = new Map<string, string>();
                        const addOptions = (opts: ReadonlyArray<{id: Uuid; displayName: string}> | undefined) => {
                            if (opts === undefined) return;
                            for (const o of opts) {
                                const key = String(o.id);
                                if (!map.has(key)) map.set(key, o.displayName);
                            }
                        };
                        for (const template of eventTemplates) {
                            for (const col of getAdditionalColumns(template)) {
                                if (
                                    col.id === EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID ||
                                    col.id === EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID ||
                                    col.id === EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID
                                ) {
                                    addOptions(getAdditionalColumnOptions(col, userDefinedObjectOptionsByTemplateId));
                                }
                            }
                        }
                        if (journeyActionPlatformColumn !== undefined) {
                            addOptions(getAdditionalColumnOptions(journeyActionPlatformColumn, userDefinedObjectOptionsByTemplateId));
                        }
                        for (const option of getEmotoradCurrentPlatformSelectOptions()) {
                            const key = String(option.id);
                            if (!map.has(key)) {
                                map.set(key, option.displayName);
                            }
                        }
                        return map;
                    })();
                    const formatLifecycleCategoryByStaticOptions = (raw: unknown): string => {
                        const s = String(raw ?? "").trim();
                        if (s === "") {
                            return "";
                        }
                        const workspaceOpts = getEmotoradDynamicJourneySelectOptions({
                            additionalColumns: getAdditionalColumns(journeysModuleConfiguration),
                            field: "category",
                        });
                        const fromWorkspace = workspaceOpts.find((o) => String(o.id).toLowerCase() === s.toLowerCase());
                        if (fromWorkspace !== undefined) {
                            return fromWorkspace.displayName;
                        }
                        const match = getEmotoradCategorySelectOptions().find((o) => String(o.id).toLowerCase() === s.toLowerCase());
                        return match?.displayName ?? s;
                    };
                    const formatLifecycleCategoryCell = (journey: EventContainerWithEvents): string => {
                        const inferEmotoradLifecycleCategoryFromPlatforms = (): string => {
                            const leadEv = findEmotoradLeadEventForJourney(journey);
                            const customerRaw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CUSTOMER_PLATFORM__COLUMN_ID];
                            const actionRaw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID];
                            const leadPlatformRaw = leadEv !== undefined ? leadEv.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID] : undefined;
                            const candidates = [customerRaw, actionRaw, leadPlatformRaw]
                                .map((r) =>
                                    String(r ?? "")
                                        .trim()
                                        .toLowerCase(),
                                )
                                .filter((x) => x !== "");
                            const ott = String(EmotoradCurrentPlatformEnum.OTTOPILOT).toLowerCase();
                            const agg = String(EmotoradCurrentPlatformEnum.DIFFERENT_AGGREGATORS).toLowerCase();
                            const web = String(EmotoradCurrentPlatformEnum.TESTRIDE_WEBPAGE).toLowerCase();
                            for (const c of candidates) {
                                if (c === agg) {
                                    return emotoradCategories[EmotoradCategoryEnum.AGGREGATOR].displayName;
                                }
                                if (c === ott || c === web) {
                                    return emotoradCategories[EmotoradCategoryEnum.ONLINE].displayName;
                                }
                            }
                            return "";
                        };
                        const primary = ((): string => {
                            const journeyCategoryColumn = additionalColumnById.get(EMOTORAD__EVENT_CONTAINER__CATEGORY__COLUMN_ID);
                            const fromJourney = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CATEGORY__COLUMN_ID];
                            if (fromJourney !== undefined && fromJourney !== null && String(fromJourney).trim() !== "" && journeyCategoryColumn !== undefined) {
                                if (eventTemplateForJourneyColumnFormat === undefined) {
                                    return formatLifecycleCategoryByStaticOptions(fromJourney);
                                }
                                return getUserDefinedObjectAdditionalColumnDetails(journeyCategoryColumn).stringValueGetter({
                                    value: fromJourney as never,
                                    users,
                                    userTemplates,
                                    organizationalUnits,
                                    organizationalUnitTemplates,
                                    products,
                                });
                            }
                            const lead = findEmotoradLeadEventForJourney(journey);
                            if (lead === undefined || leadEventTemplate === undefined || enquiryCategoryColumn === undefined) {
                                return fromJourney !== undefined && fromJourney !== null && String(fromJourney).trim() !== ""
                                    ? formatLifecycleCategoryByStaticOptions(fromJourney)
                                    : "";
                            }
                            const fromLead = lead.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__CATEGORY__COLUMN_ID];
                            if (fromLead === undefined || fromLead === null || String(fromLead).trim() === "") {
                                return fromJourney !== undefined && fromJourney !== null && String(fromJourney).trim() !== ""
                                    ? formatLifecycleCategoryByStaticOptions(fromJourney)
                                    : "";
                            }
                            return getUserDefinedObjectAdditionalColumnDetails(enquiryCategoryColumn).stringValueGetter({
                                value: fromLead as never,
                                users,
                                userTemplates,
                                organizationalUnits,
                                organizationalUnitTemplates,
                                products,
                            });
                        })();
                        if (primary.trim() !== "") {
                            return primary;
                        }
                        return inferEmotoradLifecycleCategoryFromPlatforms();
                    };
                    const formatLifecycleCreatedByRoleCell = (journey: EventContainerWithEvents): string => {
                        const resolveTemplateIdToRoleName = (templateIdRaw: unknown): string => {
                            const s = String(templateIdRaw ?? "").trim();
                            if (s === "") {
                                return "";
                            }
                            const tpl = userTemplates.find((t) => String(t.id).toLowerCase() === s.toLowerCase());
                            return tpl?.displayName ?? "";
                        };
                        const fromJourneyColumn = resolveTemplateIdToRoleName(journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CREATED_BY_ROLE__COLUMN_ID]);
                        if (fromJourneyColumn !== "") {
                            return fromJourneyColumn;
                        }
                        const lead = findEmotoradLeadEventForJourney(journey);
                        if (lead !== undefined) {
                            const fromLeadColumn = resolveTemplateIdToRoleName(lead.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID]);
                            if (fromLeadColumn !== "") {
                                return fromLeadColumn;
                            }
                            const leadCreator = users.find((u) => u.userId === lead.createdBy);
                            const fromLeadCreatorTemplate = resolveTemplateIdToRoleName(leadCreator?.templateId);
                            if (fromLeadCreatorTemplate !== "") {
                                return fromLeadCreatorTemplate;
                            }
                        }
                        const journeyCreator = users.find((u) => u.userId === journey.createdBy);
                        return resolveTemplateIdToRoleName(journeyCreator?.templateId);
                    };
                    const formatLifecycleCustomerGeographyDisplay = (
                        journey: EventContainerWithEvents,
                        journeyColumn: typeof journeyCustomerStateColumn | undefined,
                        journeyKey: Uuid,
                        enquiryColumn: typeof enquiryCustomerStateColumn | undefined,
                        enquiryValueKey: Uuid,
                    ): string => {
                        const fromJourney = journey.additionalColumnValues[journeyKey];
                        if (fromJourney !== undefined && fromJourney !== null && String(fromJourney).trim() !== "" && journeyColumn !== undefined) {
                            if (eventTemplateForJourneyColumnFormat === undefined) {
                                return String(fromJourney);
                            }
                            return getUserDefinedObjectAdditionalColumnDetails(journeyColumn).stringValueGetter({
                                value: fromJourney as never,
                                users,
                                userTemplates,
                                organizationalUnits,
                                organizationalUnitTemplates,
                                products,
                            });
                        }
                        const lead = findEmotoradLeadEventForJourney(journey);
                        if (lead === undefined || leadEventTemplate === undefined || enquiryColumn === undefined) {
                            return fromJourney !== undefined && fromJourney !== null ? String(fromJourney) : "";
                        }
                        const fromLead = lead.additionalColumnValues[enquiryValueKey];
                        if (fromLead === undefined || fromLead === null || String(fromLead).trim() === "") {
                            return "";
                        }
                        return getUserDefinedObjectAdditionalColumnDetails(enquiryColumn).stringValueGetter({
                            value: fromLead as never,
                            users,
                            userTemplates,
                            organizationalUnits,
                            organizationalUnitTemplates,
                            products,
                        });
                    };
                    const explicitLifecycleColumns = EMOTORAD_JOURNEY_TABLE_VISIBLE_COLUMN_IDS.map((columnId) => {
                        const existing = columnById.get(columnId);
                        if (columnId === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID) {
                            return {
                                ...existing,
                                id: columnId,
                                displayName: existing?.displayName ?? "Assigned Dealer",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => resolveLifecycleDealerFieldValue(journey, "name"),
                                valueFormatter: (value: string) => formatLifecycleAssignedDealerValue(value),
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID) {
                            return {
                                ...existing,
                                id: columnId,
                                displayName: existing?.displayName ?? "Assigned Dealer Contact",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => resolveLifecycleDealerFieldValue(journey, "contact"),
                                valueFormatter: (value: string) => value,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID) {
                            return {
                                ...existing,
                                id: columnId,
                                displayName: existing?.displayName ?? "Assigned Dealer Email",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => resolveLifecycleDealerFieldValue(journey, "email"),
                                valueFormatter: (value: string) => value,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID) {
                            return {
                                ...existing,
                                id: columnId,
                                displayName: existing?.displayName ?? "Dealer Pincode",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => resolveLifecycleDealerFieldValue(journey, "pincode"),
                                valueFormatter: (value: string) => value,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: lifecycleOverviewLabelById.get(columnId) ?? existing?.displayName ?? additionalColumn?.displayName ?? "Lifecycle Owner",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => getLifecycleLeadOwnerStoredValue(journey),
                                valueFormatter: (value: string) => formatLifecycleKamRmStoredValue(value),
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__LIFE_CYCLE_COUNT__COLUMN_ID) {
                            return {
                                ...existing,
                                id: columnId,
                                displayName: existing?.displayName ?? "Lifecycle Count",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => {
                                    const primary = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LIFE_CYCLE_COUNT__COLUMN_ID];
                                    if (primary !== null && primary !== undefined && String(primary).trim() !== "") {
                                        return String(primary);
                                    }
                                    const fallbackJourneyCount = journey.additionalColumnValues[EMOTORAD__CUSTOMERS__EVENT_CONTAINER_COUNT_COLUMN_ID];
                                    if (fallbackJourneyCount !== null && fallbackJourneyCount !== undefined && String(fallbackJourneyCount).trim() !== "") {
                                        return String(fallbackJourneyCount);
                                    }
                                    const fallbackLiveJourneyCount = journey.additionalColumnValues[EMOTORAD__CUSTOMERS__LIVE_EVENT_CONTAINER_COUNT_COLUMN_ID];
                                    if (fallbackLiveJourneyCount !== null && fallbackLiveJourneyCount !== undefined && String(fallbackLiveJourneyCount).trim() !== "") {
                                        return String(fallbackLiveJourneyCount);
                                    }
                                    return "-";
                                },
                                valueFormatter: (value: string) => value,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__TEST_RIDES_COUNT__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: lifecycleOverviewLabelById.get(columnId) ?? existing?.displayName ?? additionalColumn?.displayName ?? "Test rides count",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) =>
                                    emotoradJourneyTestRideCountCellString(journey.additionalColumnValues, columnId as Uuid),
                                valueFormatter: (value: string) => {
                                    const trimmed = value.trim();
                                    if (trimmed === "" || trimmed === "0") {
                                        return "";
                                    }
                                    return value;
                                },
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_AGE__COLUMN_ID) {
                            return {
                                ...existing,
                                id: columnId,
                                displayName: existing?.displayName ?? "Activity Age",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => resolveEmotoradLastActivityAgeDisplay(journey),
                                valueFormatter: (value: string) => value,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID) {
                            return {
                                ...existing,
                                id: columnId,
                                displayName: existing?.displayName ?? "Last Activity",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => resolveEmotoradLastActivityEventDisplay(journey, eventTemplates),
                                valueFormatter: (value: string) => value,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: journeyCustomerStateColumn?.displayName ?? existing?.displayName ?? additionalColumn?.displayName ?? "Customer State",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) =>
                                    formatLifecycleCustomerGeographyDisplay(
                                        journey,
                                        journeyCustomerStateColumn,
                                        EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
                                        enquiryCustomerStateColumn,
                                        EMOTORAD__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID,
                                    ),
                                valueFormatter: (value: string) => value,
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: journeyCustomerCityColumn?.displayName ?? existing?.displayName ?? additionalColumn?.displayName ?? "Customer City",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) =>
                                    formatLifecycleCustomerGeographyDisplay(
                                        journey,
                                        journeyCustomerCityColumn,
                                        EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
                                        enquiryCustomerCityColumn,
                                        EMOTORAD__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID,
                                    ),
                                valueFormatter: (value: string) => value,
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: journeyCustomerZoneColumn?.displayName ?? existing?.displayName ?? additionalColumn?.displayName ?? "Customer Zone",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) =>
                                    formatEmotoradLifecycleCustomerZoneDisplay(
                                        getAdditionalColumns,
                                        journey,
                                        journeysModuleConfiguration,
                                        users,
                                        userTemplates,
                                        organizationalUnits,
                                        organizationalUnitTemplates,
                                        products,
                                    ),
                                valueFormatter: (value: string) => (value.trim() === "" ? "-" : value),
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: journeyActionPlatformColumn?.displayName ?? existing?.displayName ?? additionalColumn?.displayName ?? "Action Platform",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => {
                                    const lead = findEmotoradLeadEventForJourney(journey);
                                    const rawActionPlatform =
                                        journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID] ??
                                        (lead !== undefined ? lead.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__CURRENT_PLATFORM__COLUMN_ID] : undefined) ??
                                        (lead !== undefined ? lead.additionalColumnValues[EMOTORAD__EVENT__SALES__ACTION_PLATFORM__COLUMN_ID] : undefined);
                                    if (rawActionPlatform === undefined || rawActionPlatform === null || String(rawActionPlatform).trim() === "") {
                                        return "";
                                    }
                                    return String(rawActionPlatform).trim();
                                },
                                valueFormatter: (raw: string) => {
                                    if (raw === "") {
                                        return "";
                                    }
                                    return lifecycleActionPlatformLabelsById.get(raw) ?? formatEmotoradCurrentPlatformCell(raw);
                                },
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__CATEGORY__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: lifecycleOverviewLabelById.get(columnId) ?? existing?.displayName ?? additionalColumn?.displayName ?? "Category",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => formatLifecycleCategoryCell(journey),
                                valueFormatter: (value: string) => value,
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__CREATED_BY_ROLE__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: lifecycleOverviewLabelById.get(columnId) ?? existing?.displayName ?? additionalColumn?.displayName ?? "Created by (Role)",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => formatLifecycleCreatedByRoleCell(journey),
                                valueFormatter: (value: string) => value,
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: existing?.displayName ?? additionalColumn?.displayName ?? "Lifecycle Status",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => {
                                    const raw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID];
                                    return raw === null || raw === undefined ? "" : String(raw).trim();
                                },
                                valueFormatter: (value: string) => (value === "" ? "" : formatEmotoradJourneyLifecycleStatusIdForDisplay(value)),
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: additionalColumn?.displayName ?? existing?.displayName ?? "Assigned Dealer City",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => resolveLifecycleDealerCityStateZone(journey, "city"),
                                valueFormatter: (value: string) => value,
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: additionalColumn?.displayName ?? existing?.displayName ?? "Assigned Dealer State",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => resolveLifecycleDealerCityStateZone(journey, "state"),
                                valueFormatter: (value: string) => value,
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: additionalColumn?.displayName ?? existing?.displayName ?? "Assigned Dealer Zone",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => resolveLifecycleDealerCityStateZone(journey, "zone"),
                                valueFormatter: (value: string) => value,
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                ...existing,
                                id: columnId,
                                displayName: additionalColumn?.displayName ?? existing?.displayName ?? "KAM Name",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => getLifecycleKamRmStoredValue(journey, EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID),
                                valueFormatter: (value: string) => formatLifecycleKamRmStoredValue(value),
                                sortingCapability: true,
                                filteringCapability_contains: false,
                                filteringCapability_in: {
                                    valuesQueryFn: async () => {
                                        const journeyColumnValuesResult = await getEventContainerColumnValues({
                                            workspaceId: workspace.id,
                                            column: columnId,
                                            templateId: journeysModuleConfiguration.id,
                                            intellsysBaseUrl: intellsysBaseUrl,
                                            accessToken: heimdallUser.accessToken,
                                        });
                                        if (journeyColumnValuesResult.success !== true) {
                                            throw journeyColumnValuesResult.error;
                                        }
                                        return compactEventContainerColumnValues(journeyColumnValuesResult.data);
                                    },
                                },
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID) {
                            return {
                                ...existing,
                                id: columnId,
                                displayName: "RM Name",
                                type: OttopilotColumnType.string,
                                valueGetter: (journey: EventContainerWithEvents) => getLifecycleKamRmStoredValue(journey, EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID),
                                valueFormatter: (value: string) => formatLifecycleKamRmStoredValue(value),
                                sortingCapability: true,
                                filteringCapability_contains: false,
                                filteringCapability_in: {
                                    valuesQueryFn: async () => {
                                        const journeyColumnValuesResult = await getEventContainerColumnValues({
                                            workspaceId: workspace.id,
                                            column: columnId,
                                            templateId: journeysModuleConfiguration.id,
                                            intellsysBaseUrl: intellsysBaseUrl,
                                            accessToken: heimdallUser.accessToken,
                                        });
                                        if (journeyColumnValuesResult.success !== true) {
                                            throw journeyColumnValuesResult.error;
                                        }
                                        return compactEventContainerColumnValues(journeyColumnValuesResult.data);
                                    },
                                },
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === "createdBy" || columnId === "updatedBy") {
                            return {
                                id: columnId,
                                type: OttopilotColumnType.uuid,
                                displayName: columnId === "createdBy" ? "Created by" : "Modified by",
                                valueGetter: (journey: EventContainerWithEvents) => (columnId === "createdBy" ? journey.createdBy : journey.updatedBy),
                                valueFormatter: (value: Uuid) => getUserDisplayName(value, users, userTemplates),
                                sortingCapability: true,
                                filteringCapability_in: {},
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (existing !== undefined) {
                            return existing;
                        }
                        if (columnId === "createdAt" || columnId === "updatedAt") {
                            return {
                                id: columnId,
                                type: OttopilotColumnType.iso8601DateTime,
                                displayName: columnId === "createdAt" ? "Created on" : "Modified at",
                                valueGetter: (journey: EventContainerWithEvents) => (columnId === "createdAt" ? journey.createdAt : journey.updatedAt),
                                valueFormatter: (value) => formatDateTime(value),
                                sortingCapability: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        const additionalColumn = additionalColumnById.get(columnId as Uuid);
                        const isAonColumn = columnId === EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID;
                        const isL1DispositionColumn = columnId === EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID;
                        const isL2DispositionColumn = columnId === EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID;
                        const isL3DispositionColumn = columnId === EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID;
                        const isCustomerNameColumn = columnId === EMOTORAD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID;
                        const isContactNumberColumn = columnId === EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID;
                        const supportsInFilter =
                            isAonColumn === true ||
                            isL1DispositionColumn === true ||
                            isL2DispositionColumn === true ||
                            isL3DispositionColumn === true ||
                            isCustomerNameColumn === true ||
                            isContactNumberColumn === true;
                        return {
                            id: columnId,
                            type: OttopilotColumnType.string,
                            displayName: lifecycleOverviewLabelById.get(columnId) ?? additionalColumn?.displayName ?? columnId,
                            valueGetter: (journey: EventContainerWithEvents) => String(journey.additionalColumnValues[columnId as Uuid] ?? ""),
                            sortingCapability: true,
                            filteringCapability_contains: supportsInFilter !== true,
                            filteringCapability_in:
                                supportsInFilter === true
                                    ? {
                                          valuesQueryFn: async () => {
                                              if (isL1DispositionColumn === true) {
                                                  return getEmotoradUniqueDispositionLevelValues("l1");
                                              }
                                              if (isL2DispositionColumn === true) {
                                                  return getEmotoradUniqueDispositionLevelValues("l2");
                                              }
                                              if (isL3DispositionColumn === true) {
                                                  return getEmotoradUniqueDispositionLevelValues("l3");
                                              }
                                              if (isAonColumn === true) {
                                                  return queryJourneyAonFilterValuesFromCreatedAt();
                                              }
                                              if (isCustomerNameColumn === true || isContactNumberColumn === true) {
                                                  const baseFilter = applyJourneysApiFilterConfiguration({
                                                      revisionId: generateUuid(),
                                                      lineItems: [],
                                                  });
                                                  const result = await getEventContainersWithEvents({
                                                      workspaceId: workspace.id,
                                                      offset: 0,
                                                      limit: JOURNEYS_CLIENT_SIDE_FILTER_PROBE_FETCH_LIMIT,
                                                      filters: toEventContainerFilters(mapFilterConfigurationToFields(baseFilter)),
                                                      orderBy: [],
                                                      templateId: journeysModuleConfiguration.id,
                                                      intellsysBaseUrl: intellsysBaseUrl,
                                                      accessToken: heimdallUser.accessToken,
                                                  });
                                                  if (result.success !== true) {
                                                      return [];
                                                  }
                                                  const values = new Set<string>();
                                                  for (const journey of result.data) {
                                                      const raw = journey.additionalColumnValues[columnId as Uuid];
                                                      const value = raw === null || raw === undefined ? "" : String(raw).trim();
                                                      if (value !== "") {
                                                          values.add(value);
                                                      }
                                                  }
                                                  return Array.from(values).sort();
                                              }
                                              const journeyColumnValuesResult = await getEventContainerColumnValues({
                                                  workspaceId: workspace.id,
                                                  column: columnId,
                                                  templateId: journeysModuleConfiguration.id,
                                                  intellsysBaseUrl: intellsysBaseUrl,
                                                  accessToken: heimdallUser.accessToken,
                                              });
                                              if (journeyColumnValuesResult.success !== true) {
                                                  throw journeyColumnValuesResult.error;
                                              }
                                              return compactEventContainerColumnValues(journeyColumnValuesResult.data);
                                          },
                                      }
                                    : undefined,
                            customFilter:
                                isAonColumn === true
                                    ? buildJourneyAonCustomFilterForColumn(emotoradJourneyAonConfig, String(EMOTORAD__EVENT_CONTAINER__AON__COLUMN_ID))
                                    : isL1DispositionColumn === true || isL2DispositionColumn === true || isL3DispositionColumn === true
                                      ? (args: Parameters<NonNullable<OttopilotColumn<EventContainerWithEvents>["customFilter"]>>[0]) => {
                                            const selectedItems = args.ephemeralValue.lineItems;
                                            if (selectedItems === null || selectedItems.length === 0) {
                                                args.controller.setFilterConfiguration((prev) => ({
                                                    revisionId: generateUuid(),
                                                    lineItems: stripJourneyDispositionLevelFilters(prev.lineItems),
                                                }));
                                                args.closePopup();
                                                return;
                                            }
                                            const dispositionIds =
                                                isL1DispositionColumn === true
                                                    ? resolveLifecycleDispositionIds({l1Values: selectedItems})
                                                    : isL2DispositionColumn === true
                                                      ? resolveLifecycleDispositionIds({l2Values: selectedItems})
                                                      : resolveLifecycleDispositionIds({l3Values: selectedItems});
                                            args.controller.setFilterConfiguration((prev) => ({
                                                revisionId: generateUuid(),
                                                lineItems: [
                                                    ...stripJourneyDispositionLevelFilters(prev.lineItems),
                                                    {
                                                        columnId: args.column.id,
                                                        operation: FilterOperation.in,
                                                        values: selectedItems,
                                                    },
                                                    {
                                                        columnId: "disposition",
                                                        operation: FilterOperation.in,
                                                        values: dispositionIds,
                                                    },
                                                ],
                                            }));
                                            args.closePopup();
                                        }
                                      : undefined,
                        } satisfies OttopilotColumn<EventContainerWithEvents>;
                    });
                    const lifecycleOverviewColumns = explicitLifecycleColumns.filter((column): column is OttopilotColumn<EventContainerWithEvents> => column !== undefined);
                    return lifecycleOverviewColumns.length > 0 ? lifecycleOverviewColumns : baseColumns;
                }
                if (isAmaronWorkspace(workspaceCapabilities) === true) {
                    const visibleColumnIds = mergeAmaronJourneyDefaultFilterColumnIdsIntoVisibleColumns(
                        isInQueueView === true ? inQueueVisibleColumns : journeyVisibleColumns,
                    );
                    const columnById = new Map(baseColumns.map((column) => [column.id, column]));
                    const additionalColumnById = new Map(getAdditionalColumns(journeysModuleConfiguration).map((column) => [column.id, column]));
                    const amaronUserFilterColumnDeps = {
                        users,
                        userTemplates,
                        platformUserDetailsByUserId,
                        workspaceId: workspace.id,
                        eventContainerTemplateId: journeysModuleConfiguration.id,
                        intellsysBaseUrl,
                        accessToken: heimdallUser.accessToken,
                    };
                    const explicitAmaronColumns = visibleColumnIds.map((columnId) => {
                        const dispositionLevel = AMARON_JOURNEY_DISPOSITION_LEVEL_BY_COLUMN_ID.get(columnId);
                        if (dispositionLevel !== undefined) {
                            return buildAmaronDispositionFilterColumn<EventContainerWithEvents>({
                                scope: "journey",
                                columnId,
                                dispositionLevel,
                                valueGetter: (journey) => String(journey.additionalColumnValues[columnId as Uuid] ?? ""),
                                sortingCapability: false,
                            });
                        }
                        if (columnId === AMARON__EVENT_CONTAINER__AON__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                id: columnId,
                                type: OttopilotColumnType.string,
                                displayName: additionalColumn?.displayName ?? "AON",
                                valueGetter: (journey: EventContainerWithEvents) => String(journey.additionalColumnValues[columnId as Uuid] ?? ""),
                                sortingCapability: true,
                                filteringCapability_contains: false,
                                filteringCapability_in: {
                                    valuesQueryFn: async () => {
                                        const journeyColumnValuesResult = await getEventContainerColumnValues({
                                            workspaceId: workspace.id,
                                            column: String(AMARON__EVENT_CONTAINER__AON__COLUMN_ID),
                                            templateId: journeysModuleConfiguration.id,
                                            intellsysBaseUrl: intellsysBaseUrl,
                                            accessToken: heimdallUser.accessToken,
                                        });
                                        if (journeyColumnValuesResult.success !== true) {
                                            return [];
                                        }
                                        return compactEventContainerColumnValues(journeyColumnValuesResult.data ?? []);
                                    },
                                },
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                id: columnId,
                                type: OttopilotColumnType.string,
                                displayName: additionalColumn?.displayName ?? "Source priority",
                                valueGetter: (journey: EventContainerWithEvents) => {
                                    const score = getAmaronSourcePriorityStoredScore(journey);
                                    if (score === null) {
                                        return "";
                                    }
                                    return String(score);
                                },
                                valueFormatter: (raw: string) => {
                                    if (raw === "") {
                                        return "";
                                    }
                                    const score = Number(raw);
                                    if (Number.isFinite(score) === false) {
                                        return "";
                                    }
                                    return formatAmaronSourcePriorityScoreForDisplay(score);
                                },
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (isInQueueView === true && columnId === AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID) {
                            return {
                                id: columnId,
                                displayName: "Status",
                                type: OttopilotColumnType.string,
                                filteringCapability_contains: true,
                                sortingCapability: true,
                                valueGetter: (journey: EventContainerWithEvents) => getInQueueAssignmentStatus(journey),
                                valueFormatter: (value: string) => formatAmaronInqueueStatusFilterValue(value),
                                filteringCapability_in: {
                                    valuesQueryFn: async () => getAmaronInqueueStatusFilterPickerValues(),
                                },
                                cellRenderer: (journey: EventContainerWithEvents) => {
                                    const label = getInQueueAssignmentStatus(journey);
                                    const isAssigned = label === "Assigned";
                                    return (
                                        <span
                                            className="inline-flex items-center px-2 py-0.5 text-xs font-medium"
                                            style={{
                                                borderRadius: "3px",
                                                borderWidth: "0.5px",
                                                borderStyle: "solid",
                                                borderColor: isAssigned ? token("color.border.success") : token("color.chart.yellow.bolder"),
                                                background: isAssigned ? token("color.background.accent.lime.subtlest") : token("color.background.accent.yellow.subtlest"),
                                            }}
                                        >
                                            {label}
                                        </span>
                                    );
                                },
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return buildJourneyUserReferenceFilterColumn(
                                columnId,
                                additionalColumn?.displayName ?? "Assigned Agent",
                                (journey) => journey.additionalColumnValues[AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID] as Uuid | null | undefined,
                                amaronUserFilterColumnDeps,
                            );
                        }
                        if (columnId === AMARON__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                id: columnId,
                                type: OttopilotColumnType.string,
                                displayName: additionalColumn?.displayName ?? "Scheduled Call Date",
                                valueGetter: (journey: EventContainerWithEvents) =>
                                    formatAmaronScheduledCallDateDisplay(journey.additionalColumnValues[AMARON__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID]),
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (isAmaronLeadsView === true && columnId === AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                id: columnId,
                                type: OttopilotColumnType.string,
                                displayName: additionalColumn?.displayName ?? "Last Activity",
                                valueGetter: (journey: EventContainerWithEvents) =>
                                    String(journey.additionalColumnValues[AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID] ?? ""),
                                valueFormatter: (value) => formatAmaronJourneyLastActivityDisplay(String(value ?? ""), eventTemplates),
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (isAmaronLeadsView === true && columnId === AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            return {
                                id: columnId,
                                type: OttopilotColumnType.string,
                                displayName: additionalColumn?.displayName ?? "Last Activity On",
                                valueGetter: (journey: EventContainerWithEvents) =>
                                    resolveAmaronLastActivityOnDisplay(journey, journey.additionalColumnValues[AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID]),
                                sortingCapability: true,
                                filteringCapability_contains: true,
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        if (columnId === "createdBy" || columnId === "updatedBy") {
                            return buildJourneyUserReferenceFilterColumn(
                                columnId,
                                columnId === "createdBy" ? "Created by" : "Modified by",
                                (journey) => (columnId === "createdBy" ? journey.createdBy : journey.updatedBy),
                                amaronUserFilterColumnDeps,
                            );
                        }
                        const existing = columnById.get(columnId);
                        if (existing !== undefined) {
                            const additionalColumn = additionalColumnById.get(columnId as Uuid);
                            if (additionalColumn?.type === UdoColumnType.user) {
                                return buildJourneyUserReferenceFilterColumn(
                                    columnId,
                                    existing.displayName,
                                    (journey) => journey.additionalColumnValues[columnId as Uuid] as Uuid | null | undefined,
                                    amaronUserFilterColumnDeps,
                                );
                            }
                            return existing;
                        }
                        if (columnId === "createdAt" || columnId === "updatedAt") {
                            const createdOnLabel = isInQueueView === true ? "Created on" : "Created at";
                            const modifiedOnLabel = isInQueueView === true ? "Modified on" : "Updated at";
                            return {
                                id: columnId,
                                type: OttopilotColumnType.iso8601DateTime,
                                displayName: columnId === "createdAt" ? createdOnLabel : modifiedOnLabel,
                                valueGetter: (journey: EventContainerWithEvents) => (columnId === "createdAt" ? journey.createdAt : journey.updatedAt),
                                valueFormatter: (value) => formatDateTime(value),
                                sortingCapability: true,
                                // Created at is owned by the default date-range control; keep Updated at addable.
                                filteringCapability_contains: columnId === "updatedAt",
                            } satisfies OttopilotColumn<EventContainerWithEvents>;
                        }
                        const additionalColumn = additionalColumnById.get(columnId as Uuid);
                        return {
                            id: columnId,
                            type: OttopilotColumnType.string,
                            displayName: additionalColumn?.displayName ?? columnId,
                            valueGetter: (journey: EventContainerWithEvents) => String(journey.additionalColumnValues[columnId as Uuid] ?? ""),
                            sortingCapability: true,
                            filteringCapability_contains: true,
                        } satisfies OttopilotColumn<EventContainerWithEvents>;
                    });
                    const amaronColumns = explicitAmaronColumns.filter((column): column is OttopilotColumn<EventContainerWithEvents> => column !== undefined);
                    const resolvedAmaronColumns = amaronColumns.length > 0 ? amaronColumns : baseColumns;
                    if (amaronJourneyStatusNavConfigForFilterUi === null) {
                        return resolvedAmaronColumns;
                    }
                    return applyAmaronModuleStatusFilterVisibilityToColumns(resolvedAmaronColumns, {
                        statusNavConfig: amaronJourneyStatusNavConfigForFilterUi,
                        activeStatusId: amaronJourneyActiveStatusIdForFilterUi,
                    });
                }
                if (isDuluxWorkspace(workspaceCapabilities) === true && isRegistryLeadsView === true) {
                    const duluxColumns = buildDuluxJourneyLeadsTableColumns(baseColumns, {
                        users,
                        userTemplates,
                        platformUserDetailsByUserId,
                        workspaceId: workspace.id,
                        intellsysBaseUrl,
                        accessToken: heimdallUser.accessToken,
                        eventContainerTemplateId: journeysModuleConfiguration.id,
                        tableViewColumns: journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns,
                        visibleColumnIds: journeyVisibleColumns,
                        additionalColumns: getAdditionalColumns(journeysModuleConfiguration),
                    });
                    if (duluxJourneyStatusNavConfigForFilterUi === null) {
                        return duluxColumns;
                    }
                    return applyModuleStatusFilterVisibilityToColumns(duluxColumns, {
                        statusNavConfig: duluxJourneyStatusNavConfigForFilterUi,
                        activeStatusId: duluxJourneyActiveStatusIdForFilterUi,
                        moduleStatusFilterScaffold: DULUX_MODULE_STATUS_FILTER_SCAFFOLD,
                    });
                }
                return baseColumns;
            })(),
        },
        views: isInQueueView
            ? [
                  ...((isEmotoradJourneysCustomer === true && emotoradInQueueListConfig.enabled === true) ||
                  (isAmaronInQueueView === true && amaronInQueueListConfig.enabled === true)
                      ? [
                            createOttopilotListView<EventContainerWithEvents>(uniqueUuid`0a9be57f-4c68-4dc1-b2fe-fef2df0bc812`, (journey, listCardContext) =>
                                journeysListPresentation.renderListCard(journey, listCardContext),
                            ),
                        ]
                      : []),
                  {
                      id: JOURNEYS_INQUEUE_TABLE_VIEW_ID,
                      viewType: ViewType.table,
                      visibleColumns: inQueueVisibleColumns,
                      otherArgs: {},
                  },
                  {
                      id: JOURNEYS_INQUEUE_KANBAN_VIEW_ID,
                      viewType: ViewType.kanbanBoard,
                      filterFieldId:
                          isBobcardWorkspace(workspace) === true
                              ? BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID
                              : isAmaronWorkspace(workspaceCapabilities) === true
                                ? AMARON_INQUEUE_STATUS_FIELD
                                : EMOTORAD_INQUEUE_STATUS_FIELD,
                      columnConfig:
                          isEmotoradInQueueView === true
                              ? emotoradInQueueKanbanColumnConfigForStatus
                              : isAmaronInQueueView === true
                                ? amaronInQueueKanbanColumnConfigForStatus
                                : [
                                      {
                                          id: uniqueUuid`75a844f6-4306-435b-b6f3-c72d20084f71`,
                                          displayName: "Assigned",
                                          background: token("color.background.accent.green.subtlest"),
                                          textColor: token("color.text"),
                                          headerBorderRadius: "4px 4px 0 0",
                                          headerBorderColor: token("color.border"),
                                          headerBorderWidth: "0.5px",
                                          possibleValues: ["assigned"],
                                      },
                                      {
                                          id: uniqueUuid`3f2e2eb7-8f74-4aa1-a6e1-ff4f87be5e95`,
                                          displayName: "Unassigned",
                                          background: token("color.background.accent.yellow.subtlest"),
                                          textColor: token("color.text"),
                                          headerBorderRadius: "4px 4px 0 0",
                                          headerBorderColor: token("color.border"),
                                          headerBorderWidth: "0.5px",
                                          possibleValues: ["unassigned"],
                                      },
                                  ],
                      cardRenderer: (journey: EventContainerWithEvents) =>
                          isEmotoradWorkspace(workspace) === true ? (
                              renderEmotoradInQueueKanbanCard(journey, eventContainerRowEngagement.tableRowActions)
                          ) : (
                              <JourneyKanbanBoardCard
                                  journey={journey}
                                  rowActionsConfig={isAmaronInQueueView === true ? amaronInQueueKanbanRowActions : effectiveJourneyRowActions}
                                  suppressPhoneDial={true}
                              />
                          ),
                      ...(isAmaronInQueueView === true
                          ? {
                                onKanbanCardClick: (journey: EventContainerWithEvents) => {
                                    void navigate({
                                        to: "/w/$workspaceId/ottopilot/event-containers/$eventContainerId",
                                        params: {workspaceId: workspace.id, eventContainerId: journey.id},
                                        search: buildEventContainerProfileNavigateSearch(
                                            buildEventContainerProfileFromForList({
                                                isArchivedView: isArchivedSlice,
                                                isInQueueView: true,
                                            }),
                                        ),
                                    });
                                },
                                kanbanLayout: AMARON_INQUEUE_KANBAN_LAYOUT,
                            }
                          : {}),
                  },
                  ...(inQueueCalendarConfig !== undefined
                      ? [
                            {
                                id: JOURNEYS_INQUEUE_CALENDAR_VIEW_ID,
                                viewType: ViewType.calendar as const,
                                getEventType: getInQueueCalendarEventType,
                                legendItems: inQueueCalendarLegendItems,
                                getEventTypeLabel: getInQueueCalendarTypeLabel,
                                getEventColor: getInQueueCalendarTypeColor,
                                allDayStyle: inQueueCalendarAllDayStyle,
                                eventOpensDaySummaryModal: true,
                            },
                        ]
                      : []),
              ]
            : [
                  ...((isEmotoradLifecycleOverviewView === true && emotoradLifecycleListConfig.enabled === true) ||
                  (isRegistryLeadsView === true && registryJourneysLeadsListConfig?.enabled === true)
                      ? [
                            createOttopilotListView<EventContainerWithEvents>(JOURNEYS_LIST_VIEW_ID, (journey, listCardContext) =>
                                journeysListPresentation.renderListCard(journey, listCardContext),
                            ),
                        ]
                      : []),
                  {
                      id: uniqueUuid`f74f6723-d5a2-4d84-9733-d1676d2408c1`,
                      viewType: ViewType.table,
                      visibleColumns:
                          journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns !== undefined
                              ? isBobcardWorkspace(workspace) || isEmotoradLifecycleOverviewView === true || isRegistryLeadsView === true
                                  ? journeyVisibleColumns
                                  : journeysModuleConfiguration.configuration.eventContainersPage.tableViewColumns.map((c) => c.id)
                              : isBobcardWorkspace(workspace) || isEmotoradLifecycleOverviewView === true || isRegistryLeadsView === true
                                ? journeyVisibleColumns
                                : [
                                      // Default visible columns
                                      // "id",
                                      ...getAdditionalColumns(journeysModuleConfiguration).map((column) => column.id),
                                      "status",
                                      "disposition",
                                      "createdBy",
                                      "createdAt",
                                      "updatedBy",
                                      "updatedAt",
                                  ],
                      otherArgs: {
                          // Removed navigation - view/edit buttons will open panels instead
                          // rowClass: "cursor-pointer",
                          // onRowClicked: (event) => {
                          //     navigate({
                          //         to: `/w/${workspace.id}/ottopilot/event-containers/${event.data.id}`,
                          //     });
                          // },
                      },
                  },
                  // {
                  //     id: uniqueUuid`929a5e7f-d4cf-4bb2-b0d3-d696101efb31`,
                  //     viewType: ViewType.cards,
                  // },
                  {
                      id: uniqueUuid`e1df721b-c513-4e60-9e5f-2495b277fdb4`,
                      viewType: ViewType.kanbanBoard,
                          filterFieldId:
                              isEmotoradLifecycleOverviewView === true
                                  ? EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID
                                  : isAmaronLeadsView === true
                                    ? AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID
                                    : isDuluxLeadsView === true
                                      ? DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID
                                      : journeysModuleConfiguration.configuration.eventContainersPage.kanbanBoardPageView.filterFieldId,
                          columnConfig:
                              isEmotoradLifecycleOverviewView === true
                                  ? emotoradLifecycleKanbanColumnConfigForStatus
                                  : isAmaronLeadsView === true
                                    ? amaronLeadsKanbanColumnConfigForStatus
                                    : isDuluxLeadsView === true
                                      ? duluxLeadsKanbanColumnConfigForStatus
                                      : journeysModuleKanbanColumnConfigForSlice,
                      ...(isDuluxLeadsView === true
                          ? {
                                kanbanLayout: {
                                    ...DULUX_MODULE_KANBAN_LAYOUT,
                                    ...(isArchivedSlice === true ? ARCHIVED_MODULE_KANBAN_LAYOUT : {}),
                                },
                            }
                          : isArchivedSlice === true
                            ? {kanbanLayout: ARCHIVED_MODULE_KANBAN_LAYOUT}
                            : {}),
                      cardRenderer: (journey: EventContainerWithEvents) =>
                          isEmotoradLifecycleOverviewView === true ? (
                              <EmotoradLifecycleKanbanBoardCard
                                  journey={journey}
                                  rowActionsConfig={effectiveJourneyRowActions}
                                  onDispositionClick={
                                      showKanbanDispositionAction === true &&
                                      isEventContainerEngagementRestrictedByGlobalConstraints(
                                          journey,
                                          journeyRowActionGlobalConstraints,
                                      ) !== true
                                          ? () => openDispositionJourney(journey)
                                          : undefined
                                  }
                              />
                          ) : (
                              <JourneyKanbanBoardCard
                                  journey={journey}
                                  rowActionsConfig={effectiveJourneyRowActions}
                                  onDispositionClick={
                                      showKanbanDispositionAction === true &&
                                      isEventContainerEngagementRestrictedByGlobalConstraints(
                                          journey,
                                          journeyRowActionGlobalConstraints,
                                      ) !== true
                                          ? () => openDispositionJourney(journey)
                                          : undefined
                                  }
                              />
                          ),
                  },
                  ...(journeyCalendarConfig !== undefined
                      ? [
                            {
                                id: JOURNEYS_CALENDAR_VIEW_ID,
                                viewType: ViewType.calendar as const,
                                getEventType: getJourneyLifecycleCalendarEventType,
                                legendItems: journeyCalendarLegendItems,
                                getEventTypeLabel: getJourneyCalendarTypeLabel,
                                getEventColor: journeyCalendarGetEventColorForView,
                                allDayStyle: journeyCalendarAllDayStyleForView,
                                scheduleEmptyState: journeyCalendarScheduleEmptyState,
                                onCreateEventClick: onJourneyCalendarCreateClick,
                                eventOpensDaySummaryModal: true,
                            },
                        ]
                      : []),
                  //   ...(isEmotoradLifecycleOverviewView === true
                  //       ? [
                  //             {
                  //                 id: JOURNEYS_EMOTORAD_LIFECYCLE_CALENDAR_VIEW_ID,
                  //                 viewType: ViewType.calendar as const,
                  //                 getEventType: getEmotoradLifecycleCalendarEventType,
                  //                 legendItems: inQueueCalendarLegendItems,
                  //                 getEventTypeLabel: getInQueueCalendarTypeLabel,
                  //                 getEventColor: getInQueueCalendarTypeColor,
                  //                 allDayStyle: inQueueCalendarAllDayStyle,
                  //             },
                  //         ]
                  //       : []),
              ],
        initialViewId: initialViewId,
        initialSortConfiguration: initialJourneySortConfiguration,
        showDefaultSortAsActive: true,
        initialVisibleFilters: {
            revisionId: uniqueUuid`db459f43-34c9-4d3d-9600-456c1f441ffb`,
            lineItems: initialVisibleFilterColumnIds,
        },
        // searchColumns: [DULUX__EVENT_CONTAINER__NAME__COLUMN_ID]
        // pageSize: 10,
        renderTableNoDataEmptyState: renderJourneysTableNoDataEmptyState,
        listCardChrome: (
            <>
                {eventContainerRowEngagement.scheduleCallModal}
                {eventContainerRowEngagement.archiveModals}
                {listNotesPanelChrome}
                {((isEmotoradLifecycleOverviewView === true && emotoradLifecycleListConfig.includeEventCreateDrawer === true) ||
                    (isRegistryLeadsView === true && registryJourneysLeadsListConfig?.includeEventCreateDrawer === true)) &&
                sharedJourneyFooterBindings.selectedDispositionId !== null ? (
                    <WorkspaceListCardCreateChrome
                        opened={sharedJourneyFooterBindings.selectedDispositionId !== null}
                        onClose={() => {
                            sharedJourneyFooterBindings.setSelectedDispositoinId(null);
                            sharedJourneyFooterBindings.setSelectedCreateJourneyId(null);
                        }}
                        workspaceId={workspace.id}
                        items={listCreateDrawerItems}
                        eventContainerId={sharedJourneyFooterBindings.selectedCreateJourneyId}
                    />
                ) : null}
            </>
        ),
        readPanelComponent: (row, isExpanded) => {
            const panel = journeysDataViewerControllerRef.current?.currentlyOpenRowEngagementPanel;
            if (
                panel?.rowEngagementType === RowEngagementType.read &&
                panel.rowEngagementData?.id === row.id &&
                panel.readPanelLayout === "entityProfile"
            ) {
                return (
                    <JourneyEntityProfileAside
                        journey={row}
                        profileFrom={eventContainerRowEngagement.profileNavigationFrom}
                        onClose={() => {
                            journeysDataViewerControllerRef.current?.setCurrentlyOpenRowEngagementPanel({
                                rowEngagementType: RowEngagementType.none,
                                rowEngagementPanelType: RowEngagementPanelType.none,
                                rowEngagementData: null,
                            });
                        }}
                    />
                );
            }

            return (
                <EventContainerViewPanel
                    row={row}
                    isExpanded={isExpanded}
                    eventContainerTemplate={effectiveJourneysModuleConfigurationForPanel}
                    eventTemplates={eventTemplates}
                    notesEventContainerSourceLabel={journeysRowDisplayNameVariants.singularSentenceCase}
                    useTwoColumnFieldGrid={useTwoColumnJourneyFieldGrid}
                    useInQueueHeader={isScaffoldStyleJourneyPanel}
                    useEmotoradLifecycleOverviewStatusHeader={isEmotoradLifecycleOverviewView === true}
                    useAmaronLeadsStatusHeader={isAmaronLeadsView === true}
                    showEventContainerProfileLink={isArchivedSlice !== true && isAmaronInQueueView !== true}
                    resolveInQueueAssignmentLabel={isInQueueView === true ? getInQueueAssignmentStatus : undefined}
                    inQueueAssignmentOwnerColumnId={isInQueueView === true ? inQueueAssignmentOwnerColumnId : null}
                />
            );
        },
        updatePanelComponent: (row, isExpanded) => {
            return (
                <EventContainerEditPanel
                    row={row}
                    isExpanded={isExpanded}
                    eventContainerTemplate={effectiveJourneysModuleConfigurationForEditPanel}
                    eventTemplates={eventTemplates}
                    notesEventContainerSourceLabel={journeysRowDisplayNameVariants.singularSentenceCase}
                    hideNotesSection={isAmaronInQueueView === true}
                    useInQueueEditLayout={isScaffoldStyleJourneyPanel}
                    useDuluxHeaderStyle={isDuluxLeadsView === true}
                    fieldGridColumnCount={isDuluxLeadsView === true ? 2 : undefined}
                    useEmotoradLifecycleOverviewStatusHeader={isEmotoradLifecycleOverviewView === true}
                    useAmaronLeadsStatusHeader={isAmaronLeadsView === true}
                    useRequiredAsteriskOnly={isDuluxLeadsView === true}
                    bobcardInQueueLeadOwnerColumnId={
                        isInQueueView === true &&
                        isBobcardWorkspace(workspace) === true &&
                        journeysModuleConfiguration.configuration.eventContainersPage.leadOwnerColumnId !== undefined
                            ? journeysModuleConfiguration.configuration.eventContainersPage.leadOwnerColumnId
                            : undefined
                    }
                    emotoradLifecycleOwnerColumnId={
                        isEmotoradWorkspace(workspace) === true
                            ? isEmotoradInQueueView === true
                                ? (emotoradInQueueLifecycleOwnerColumnId ?? undefined)
                                : EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID
                            : undefined
                    }
                    resolveInQueueAssignmentLabel={isInQueueView === true ? getInQueueAssignmentStatus : undefined}
                    inQueueAssignmentOwnerColumnId={isInQueueView === true ? inQueueAssignmentOwnerColumnId : null}
                    onSaveSuccess={() => {
                        journeysDataViewerController.setCurrentlyOpenRowEngagementPanel({
                            rowEngagementType: RowEngagementType.none,
                            rowEngagementPanelType: RowEngagementPanelType.none,
                            rowEngagementData: null,
                        });
                    }}
                />
            );
        },
        showEditActionPerRow: isInQueueView === true,
        canEditRow: (row) => {
            if (isArchivedSlice === true) {
                return false;
            }
            if (row === null || row === undefined) {
                return false;
            }
            if (isInQueueView === true) {
                return getInQueueAssignmentStatus(row) === "Unassigned";
            }
            return true;
        },
        tableRowActions: effectiveJourneyRowActions,
        additionalActionComponent: undefined,
        tableSelectionBulkAction:
            isArchivedSlice === true ? "restore" : isAmaronInQueueView === true ? "none" : "delete",
        onRequestRestoreSelectedRows:
            isArchivedSlice === true
                ? (rows) => {
                      setRestoreJourneyTargets(rows);
                      setIsRestoreJourneyModalOpen(true);
                  }
                : undefined,
        rowEngagementDropdownOptions: (row) => {
            const exportColumns = orderColumnsLikeTable(
                (journeysDataViewerController.columns.lineItems as Array<OttopilotColumn<EventContainerWithEvents>>).filter(
                    isDuluxWorkspace(workspaceCapabilities) === true ? duluxColumnIsExportable : (col) => col.id !== "id",
                ),
                journeysDataViewerController.tableColumnConfig?.orderedVisibleColumnIds,
            );

            const dataToExport = transformRowForExport(row, exportColumns);

            const exportAndCopyItems = [
                <ExportDataDropdownItem
                    key="export"

                    onNativeCsvExport={isEmotoradWorkspace(workspace) ? undefined : () => nativeCsvRowExportRef.current(row.id)}
                    isVisible={isEmotoradWorkspace(workspace) !== true}
                />,
                <CopyDataDropdownItem
                    key="copy"
                    data={dataToExport}
                    showFlag={showFlag}
                />,
            ];

            if (isArchivedSlice !== true) {
                return exportAndCopyItems;
            }

            return [
                ...exportAndCopyItems,
                ...(journeysModuleConfiguration.configuration.statusColumnConfig !== undefined
                    ? [
                          <RestoreDataDropdownItem
                              key="restore"
                              onSelect={() => {
                                  setRestoreJourneyTargets([row]);
                                  setIsRestoreJourneyModalOpen(true);
                              }}
                          />,
                      ]
                    : []),
            ];
        },
    });

    journeysDataViewerControllerRef.current = journeysDataViewerController;

    const restoreJourneyMutation = useMutation({
        mutationFn: async () => {
            const statusColumnConfig = journeysModuleConfiguration.configuration.statusColumnConfig;
            const targets = restoreJourneyTargetsRef.current;
            if (statusColumnConfig === undefined || targets === null || targets.length === 0) {
                throw new Error("Cannot restore this journey");
            }

            for (const journey of targets) {
                const result = await restoreEventContainerWithEvents({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl,
                    workspaceId: workspace.id,
                    workspace: workspace,
                    workspaceCapabilities,
                    eventContainer: journey,
                    statusColumnConfig,
                    eventTemplates,
                });

                if (result.success !== true) {
                    throw result.error;
                }
            }
        },
        onSuccess: () => {
            setShowRestoreSuccessToast(true);
            setIsRestoreJourneyModalOpen(false);
            setRestoreJourneyTargets(null);
            const controller = journeysDataViewerControllerRef.current;
            if (controller !== null) {
                controller.setCurrentlyOpenRowEngagementPanel({
                    rowEngagementType: RowEngagementType.none,
                    rowEngagementPanelType: RowEngagementPanelType.none,
                    rowEngagementData: null,
                });
            }
            void invalidateDataViewerQueriesAfterJourneyArchive(queryClient, journeysDataViewerQueryKeyPrefix, workspace.id);
        },
        onError: (error: unknown) => {
            showFlag({
                title: "Restore failed",
                description: error instanceof Error ? error.message : "Could not restore the journey. Please try again.",
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 4,
            });
        },
    });

    openInQueueKanbanViewPanelRef.current = (journey) => {
        journeysDataViewerController.setCurrentlyOpenRowEngagementPanel({
            rowEngagementType: RowEngagementType.read,
            rowEngagementPanelType: RowEngagementPanelType.sidebar,
            rowEngagementData: journey,
        });
    };

    useEffect(() => {
        if (activeView === "create" && isDhiraaguWorkspace(workspace) === true) {
            navigate({to: "/w/$workspaceId/ottopilot/dhiraagu/journey/create", params: {workspaceId: workspace.id}});
        }

        journeysDataViewerController.setCurrentViewId(selectedViewId);

        const filterBaselineScope = `${workspace.id}:${isInQueueView === true ? "inqueue" : "lifecycle"}`;
        const previousFilterBaselineScope = journeysFilterBaselineScopeRef.current;
        journeysFilterBaselineScopeRef.current = filterBaselineScope;

        if (previousFilterBaselineScope !== null && previousFilterBaselineScope !== filterBaselineScope) {
            if (isBobcardWorkspace(workspace) === true || isAmaronWorkspace(workspaceCapabilities) === true) {
                journeysDataViewerController.setFilterConfiguration(
                    mergeDefaultCreatedAtMonthFilter({
                        revisionId: uniqueUuid`1cf0f7e8-2f21-4ae6-9461-33f5bb8f6e56`,
                        lineItems: isInQueueView ? [] : leadViewFilterLineItems,
                    }),
                );
                journeysDataViewerController.setVisibleFilters({
                    revisionId: uniqueUuid`8ec0f0d5-9ca9-4f4d-9b0c-69c9be9855a6`,
                    lineItems: initialVisibleFilterColumnIds,
                });
            } else if (isEmotoradWorkspace(workspace) === true) {
                const emotoradBaselineLineItems = isInQueueView
                    ? []
                    : mergeEmotoradLifecycleOverviewMandatoryJourneyApiFilters(
                          {
                              revisionId: uniqueUuid`e7c8b1a0-3d4e-5f67-8a9b-0c1d2e3f4a5b`,
                              lineItems: [],
                          },
                          emotoradLifecycleOverviewMandatoryJourneyApiFilterLineItems,
                      ).lineItems;
                journeysDataViewerController.setFilterConfiguration(
                    mergeDefaultCreatedAtMonthFilter({
                        revisionId: uniqueUuid`e7c8b1a0-3d4e-5f67-8a9b-0c1d2e3f4a5b`,
                        lineItems: emotoradBaselineLineItems,
                    }),
                );
            }
            if ((isEmotoradLifecycleOverviewView === true || isEmotoradInQueueView === true) && initialVisibleFilterColumnIds.length > 0) {
                journeysDataViewerController.setVisibleFilters({
                    revisionId: uniqueUuid`c4e8f0a1-2b3d-5e6f-9a0b-1c2d3e4f5a6b`,
                    lineItems: initialVisibleFilterColumnIds,
                });
            }
        }
    }, [
        activeView,
        selectedViewId,
        workspace,
        isInQueueView,
        leadViewFilterLineItems,
        initialVisibleFilterColumnIds,
        isEmotoradLifecycleOverviewView,
        isEmotoradInQueueView,
        emotoradLifecycleOverviewMandatoryJourneyApiFilterLineItems,
    ]);

    const emotoradJourneyStatusNavConfig = useMemo(() => {
        if (isEmotoradLifecycleOverviewView !== true) {
            return null;
        }
        return getEmotoradJourneyLifecycleStatusNavConfig();
    }, [isEmotoradLifecycleOverviewView]);

    useSyncModuleStatusFilterFromSearch({
        statusColumnId: emotoradJourneyStatusNavConfig?.statusColumnId ?? null,
        setFilterConfiguration: journeysDataViewerController.setFilterConfiguration,
        enabled: isEmotoradWorkspace(workspace) === true && emotoradJourneyStatusNavConfig !== null,
        validStatusIds: getEmotoradModuleStatusNavValidStatusIds(emotoradJourneyStatusNavConfig),
    });

    const amaronJourneyLeadsStatusNavConfig = useMemo(() => {
        if (isAmaronLeadsView !== true) {
            return null;
        }
        return getAmaronJourneyLeadsStatusNavConfig();
    }, [isAmaronLeadsView]);

    useSyncModuleStatusFilterFromSearch({
        statusColumnId: amaronJourneyLeadsStatusNavConfig?.statusColumnId ?? null,
        setFilterConfiguration: journeysDataViewerController.setFilterConfiguration,
        enabled: isAmaronWorkspace(workspaceCapabilities) === true && amaronJourneyLeadsStatusNavConfig !== null,
        validStatusIds: getAmaronModuleStatusNavValidStatusIds(amaronJourneyLeadsStatusNavConfig),
        statusIdFromSearch: search.statusId,
        ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
        alwaysUseInOperation: true,
        // Keep Display-by Active/Inactive when sidebar statusId is cleared (same as config pages).
        journeyScopeFilterConfig: amaronLeadsJourneyScopeFilterConfig,
    });

    /** Runs after sidebar status sync; parent Leads view defaults Display by to Active. */
    useLayoutEffect(() => {
        if (isAmaronWorkspace(workspaceCapabilities) !== true || isAmaronLeadsView !== true) {
            return;
        }
        if (amaronLeadsJourneyScopeFilterConfig === undefined) {
            return;
        }
        const statusId = resolveModuleStatusIdForFilter({
            statusIdFromSearch: search.statusId,
            validStatusIds: getAmaronModuleStatusNavValidStatusIds(amaronJourneyLeadsStatusNavConfig),
            ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
        });
        if (statusId !== null) {
            return;
        }

        journeysDataViewerController.setFilterConfiguration((prev) => {
            if (getJourneyScopeSelectionFromFilter(prev, amaronLeadsJourneyScopeFilterConfig) !== "all") {
                return prev;
            }
            return applyJourneyScopeSelectionToFilterConfiguration(prev, amaronLeadsJourneyScopeFilterConfig, "active");
        });
    }, [
        amaronJourneyLeadsStatusNavConfig,
        amaronLeadsJourneyScopeFilterConfig,
        isAmaronLeadsView,
        journeySliceConfig.ignoreStatusIdSearchParam,
        journeysDataViewerController.setFilterConfiguration,
        search.statusId,
        workspace,
    ]);

    useSyncInQueueAssignmentFilterFromSearch({
        leadOwnerColumnId: inQueueAssignmentOwnerColumnId,
        setFilterConfiguration: journeysDataViewerController.setFilterConfiguration,
        enabled: isInQueueView === true && inQueueAssignmentOwnerColumnId !== null,
        validStatusIds: inQueueAssignmentValidStatusIds,
        statusIdFromSearch: search.statusId,
        ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
    });

    useHideAmaronModuleStatusFilterUiInChildView({
        enabled: isAmaronWorkspace(workspaceCapabilities) === true && amaronJourneyStatusNavConfigForFilterUi !== null,
        statusNavConfig: amaronJourneyStatusNavConfigForFilterUi,
        setVisibleFilters: journeysDataViewerController.setVisibleFilters,
        validStatusIds: getAmaronModuleStatusNavValidStatusIds(amaronJourneyStatusNavConfigForFilterUi),
        statusIdFromSearch: search.statusId,
        ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
    });

    useSyncModuleStatusFilterFromSearch({
        statusColumnId: duluxJourneyStatusNavConfigForFilterUi?.statusColumnId ?? null,
        setFilterConfiguration: journeysDataViewerController.setFilterConfiguration,
        enabled: isDuluxWorkspace(workspaceCapabilities) === true && duluxJourneyStatusNavConfigForFilterUi !== null,
        validStatusIds: getDuluxModuleStatusNavValidStatusIds(duluxJourneyStatusNavConfigForFilterUi),
        statusIdFromSearch: search.statusId,
        ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
        alwaysUseInOperation: true,
    });

    useHideModuleStatusFilterUiInChildView({
        enabled: isDuluxWorkspace(workspaceCapabilities) === true && duluxJourneyStatusNavConfigForFilterUi !== null,
        statusNavConfig: duluxJourneyStatusNavConfigForFilterUi,
        setVisibleFilters: journeysDataViewerController.setVisibleFilters,
        validStatusIds: getDuluxModuleStatusNavValidStatusIds(duluxJourneyStatusNavConfigForFilterUi),
        statusIdFromSearch: search.statusId,
        ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
        moduleStatusFilterScaffold: DULUX_MODULE_STATUS_FILTER_SCAFFOLD,
    });

    const journeysExportDocumentTitle =
        isInQueueView === true
            ? "InQueue"
            : isEmotoradLifecycleOverviewView === true
              ? "Lifecycle Overview"
              : journeysModuleConfiguration.configuration.moduleDisplayNameVariants.pluralSentenceCase;

    const journeysPngFilename = buildPageTitleExportFilename(journeysExportDocumentTitle, "png");

    // Shared domain query translation, independent of either CSV transport.
    const resolveJourneysCsvQuery = (controller: typeof journeysDataViewerController) => {
        let filterConfiguration = controller.filterConfiguration;
        if (
            isAmaronLeadsView === true
            && amaronLeadsJourneyScopeFilterConfig !== undefined
            && resolveModuleStatusIdForFilter({
                statusIdFromSearch: search.statusId,
                validStatusIds: getAmaronModuleStatusNavValidStatusIds(amaronJourneyLeadsStatusNavConfig),
                ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
            }) === null
            && getJourneyScopeSelectionFromFilter(filterConfiguration, amaronLeadsJourneyScopeFilterConfig) === "all"
        ) {
            filterConfiguration = applyJourneyScopeSelectionToFilterConfiguration(
                filterConfiguration, amaronLeadsJourneyScopeFilterConfig, "active",
            );
        }
        if (isInQueueView === true && inQueueAssignmentOwnerColumnId !== null) {
            const inQueueStatusId = resolveModuleStatusIdForFilter({
                statusIdFromSearch: search.statusId,
                validStatusIds: inQueueAssignmentValidStatusIds,
                ignoreStatusIdSearchParam: journeySliceConfig.ignoreStatusIdSearchParam,
            });
            filterConfiguration = applyInQueueAssignmentStatusIdToFilterConfiguration(
                filterConfiguration, inQueueAssignmentOwnerColumnId, inQueueStatusId,
            );
        }
        const bobcardAssignmentStatusFilters = isBobcardWorkspace(workspace) === true
            ? filterConfiguration.lineItems.filter((lineItem) => lineItem.columnId === BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID) : [];
        const bobcardLeadOwnerColumnId = isBobcardWorkspace(workspace) === true
            ? (leadOwnerColumnId ?? BOBCARD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID) : undefined;
        const bobcardAssignmentBackendLineItems: FilterConfiguration["lineItems"] = bobcardLeadOwnerColumnId !== undefined && isInQueueView !== true
            ? bobcardAssignmentStatusFilters.flatMap((lineItem) => {
                const values = lineItem.operation === FilterOperation.in ? lineItem.values.map((value) => value.toLowerCase()) : [];
                const wantsAssigned = values.includes("assigned");
                const wantsUnassigned = values.includes("unassigned");
                if (wantsAssigned === wantsUnassigned) {return [];}
                return [{columnId: bobcardLeadOwnerColumnId, operation: wantsAssigned ? FilterOperation.notEquals : FilterOperation.equals, value: null}];
            }) : [];
        const inQueuePartition = isInQueueView === true && inQueueAssignmentOwnerColumnId !== null
            ? partitionInQueueFiltersForBackend({lineItems: filterConfiguration.lineItems,
                statusFilterColumnIds: inQueueUiStatusFilterColumnIds, leadOwnerColumnId: inQueueAssignmentOwnerColumnId}) : null;
        const backendLineItems = inQueuePartition !== null
            ? [...inQueuePartition.backendLineItems, ...inQueuePartition.inQueueAssignmentBackendLineItems]
            : filterConfiguration.lineItems.filter((lineItem) =>
                !(isBobcardWorkspace(workspace) === true && lineItem.columnId === BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID)
                && !(isBobcardWorkspace(workspace) === true && lineItem.columnId === BOBCARD__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID)
                && !(isAmaronWorkspace(workspaceCapabilities) === true && isAmaronJourneyDispositionUiFilterColumn(lineItem.columnId) === true)
                && !(isEmotoradLifecycleOverviewView === true && isJourneyDispositionUiFilterColumn(lineItem.columnId) === true));
        const amaronBackendLineItems = isAmaronWorkspace(workspaceCapabilities) === true && isAmaronInQueueView !== true
            ? appendAmaronJourneyDispositionApiFilterIfMissing(backendLineItems, filterConfiguration.lineItems) : backendLineItems;
        const filterConfigurationForBackend: FilterConfiguration = {
            ...filterConfiguration,
            lineItems: [...amaronBackendLineItems, ...bobcardAssignmentBackendLineItems].map(normalizeEnumFilterLineItem),
        };
        const mappedFilterConfiguration = mapFilterConfigurationToFields(applyJourneysApiFilterConfiguration(filterConfigurationForBackend));
        const exportFilterConfiguration = isAmaronWorkspace(workspaceCapabilities) === true
            ? sanitizeAmaronDispositionFiltersForExport(mappedFilterConfiguration, "journey") : mappedFilterConfiguration;
        return {
            filters: toEventContainerFilters(exportFilterConfiguration, controller.searchConfiguration),
            orderBy: toEventContainerOrderBy(mapSortConfigurationToFields(controller.sortConfiguration)),
            templateId: journeysModuleConfiguration.id,
        };
    };

    const {

        onExportCsv: handleExportAllRowsLikeCsv,
        onExportCsvForRowId: exportJourneyRowCsv,
        onExportPng: handleExportAllRowsAsPng,
    } = useOttopilotExportActions({
        controller: journeysDataViewerController,

        pngFilename: journeysPngFilename,
        documentTitle: journeysExportDocumentTitle,
        columnIsExportableFn: isDuluxWorkspace(workspaceCapabilities) === true ? duluxColumnIsExportable : undefined,
        csvExportMode: isEmotoradWorkspace(workspace) ? {kind: "disabled"} : {kind: "native", config: {
            baseUrl: intellsysBaseUrl,
            path: "/ottopilot/event-containers/export",
            workspaceId: workspace.id,
            userId: heimdallUser.id,
            nullFilterColumnId: resolveTableColumnField,
            resolveColumnId: (column) => {
                let field = resolveTableColumnField(column.id);
                if (isAmaronWorkspace(workspaceCapabilities) && isAmaronJourneyDispositionUiFilterColumn(field)) {field = "disposition";}
                if ([AMARON_INQUEUE_STATUS_FIELD, BOBCARD_ASSIGNMENT_STATUS_COLUMN_ID, AMARON__EVENT_CONTAINER__INQUEUE_STATUS__COLUMN_ID].includes(field)) {field = "assignmentStatus";}
                return nativeCsvEventColumn(getAdditionalColumns, field, journeysModuleConfiguration, undefined, isAmaronWorkspace(workspaceCapabilities));
            },
            resolveQuery: resolveJourneysCsvQuery,
        }},
        onError: (message) => {
            showFlag({
                title: "Export failed",
                description: message,
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
        },
    });
    nativeCsvRowExportRef.current = exportJourneyRowCsv;

    const handleOpenArchivedData = () => {
        void navigate({
            to: `/w/${workspace.id}/ottopilot/event-containers/archived`,
            search: {view: search.view ?? "table"},
        });
    };

    return (
        <>
            <TableOnlyPage
                title={
                    isInQueueView
                        ? "InQueue"
                        : isArchivedSlice
                          ? journeySliceConfig.pageTitle
                          : isEmotoradLifecycleOverviewView
                            ? "Lifecycle Overview"
                            : journeysModuleConfiguration.configuration.moduleDisplayNameVariants.pluralSentenceCase
                }
                titleRightSection={
                    <div className={isMobile ? "grid grid-flow-col auto-cols-max gap-[8px]" : "grid grid-flow-col auto-cols-max gap-ds-space-100"}>
                        {createJourneyHref !== null &&
                            journeySliceConfig.capabilities.showCreate === true &&
                            (isMobile ? (
                                <Link
                                    to={createJourneyHref}
                                    className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px]"
                                    style={{
                                        backgroundColor: token("color.background.brand.bold", "#0052CC"),
                                        color: token("color.icon.inverse", "#FFFFFF"),
                                    }}
                                >
                                    <AddIcon
                                        label={`Create ${
                                            isEmotoradLifecycleOverviewView
                                                ? "Lifecycle Overview"
                                                : journeysModuleConfiguration.configuration.moduleDisplayNameVariants.singularSentenceCase
                                        }`}
                                    />
                                </Link>
                            ) : (
                                <AnankeLinkButton
                                    appearance="primary"
                                    href={createJourneyHref}
                                >
                                    + Create{" "}
                                    {isEmotoradLifecycleOverviewView
                                        ? "Lifecycle Overview"
                                        : journeysModuleConfiguration.configuration.moduleDisplayNameVariants.singularSentenceCase}
                                </AnankeLinkButton>
                            ))}

                        {journeySliceConfig.capabilities.showExportMenu === true &&
                            (isMobile ? (
                                <button
                                    type="button"
                                    onClick={() => setMoreActionsDrawerOpen(true)}

                                    className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"

                                    style={{
                                        borderColor: token("color.border", "#091E4224"),
                                        backgroundColor: token("elevation.surface", "#FFFFFF"),
                                        color: token("color.icon", "#44546F"),
                                    }}
                                >
                                    {<ShowMoreVerticalIcon label="More actions" />}
                                </button>
                            ) : (
                                <AnankeDropdownMenu<HTMLButtonElement>
                                    placement="bottom-end"
                                    shouldRenderToParent={true}
                                    trigger={({triggerRef, ...triggerProps}) => {
                                        const {isSelected: _isSelected, ...buttonTriggerProps} =
                                            triggerProps as typeof triggerProps & {isSelected?: boolean};
                                        void _isSelected;
                                        return (
                                            <button
                                                {...buttonTriggerProps}
                                                ref={triggerRef}
                                                type="button"

                                                className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"

                                                style={{
                                                    borderColor: token("color.border", "#091E4224"),
                                                    backgroundColor: token("elevation.surface", "#FFFFFF"),
                                                    color: token("color.icon", "#44546F"),
                                                }}
                                            >
                                                {(
                                                    <ShowMoreVerticalIcon label="More actions" />
                                                )}
                                            </button>
                                        );
                                    }}
                                >
                                    <AnankeDropdownItemGroup>
                                        {isAmaronWorkspace(workspaceCapabilities) === true ? (
                                            <ExportFlatDropdownItems

                                                onExportCsv={isEmotoradWorkspace(workspace) === true ? undefined : handleExportAllRowsLikeCsv}
                                            />
                                        ) : isDuluxWorkspace(workspaceCapabilities) === true ? (
                                            <ExportNestedDropdownItem

                                                onExportCsv={isEmotoradWorkspace(workspace) === true ? undefined : handleExportAllRowsLikeCsv}
                                            />
                                        ) : isEmotoradWorkspace(workspace) === true ? null : (
                                            <AnankeDropdownItem

                                                onClick={() => handleExportAllRowsLikeCsv()}
                                                elemBefore={
                                                    (
                                                        <DownloadIcon
                                                            label=""
                                                            color={token("color.icon")}
                                                        />
                                                    )
                                                }
                                                elemAfter={
                                                    (
                                                        <ChevronRightIcon
                                                            label=""
                                                            size="small"
                                                            color={token("color.icon.subtle")}
                                                        />
                                                    )
                                                }
                                            >
                                                {"Export as CSV"}
                                            </AnankeDropdownItem>
                                        )}
                                    </AnankeDropdownItemGroup>
                                    {showArchivedDataMenuItem === true ? (
                                        <AnankeDropdownItemGroup hasSeparator>
                                            <AnankeDropdownItem
                                                onClick={handleOpenArchivedData}
                                                elemBefore={<DeleteIcon label="" />}
                                            >
                                                Archived Data
                                            </AnankeDropdownItem>
                                        </AnankeDropdownItemGroup>
                                    ) : null}
                                </AnankeDropdownMenu>
                            ))}
                    </div>
                }
                stickyTitleSection={true}
                bottomSection={
                    <>
                        <OttopilotDataViewer
                            controller={journeysDataViewerController}
                        />
                        {isEmotoradLifecycleOverviewView === true && selectedDispositionJourney !== null ? (
                            <EmotoradLeadDispositionModal
                                key={`emotorad-disposition-${selectedDispositionJourney.id}`}
                                onClose={() => {
                                    setSelectedDispositionJourney(null);
                                }}
                                event={{
                                    id: selectedDispositionJourney.id,
                                    templateId: EmotoradEventTypes.lead as Uuid,
                                    eventContainerId: selectedDispositionJourney.id,
                                    eventContainer: selectedDispositionJourney,
                                    additionalColumnValues: {},
                                    revisionId: selectedDispositionJourney.id,
                                    createdAt: selectedDispositionJourney.createdAt,
                                    updatedAt: selectedDispositionJourney.updatedAt,
                                    createdBy: selectedDispositionJourney.createdBy,
                                    updatedBy: selectedDispositionJourney.updatedBy,
                                    status: OttopilotEventStatus.active,
                                }}
                                eventContainerTemplate={journeysModuleConfiguration}
                                queryKeyPrefix={[uniqueUuid`efadf6c4-78b9-4e6e-b474-0f8c46f661bf`]}
                            />
                        ) : null}
                        {isRegistryLeadsView === true && workspaceListSupport !== null && selectedDispositionJourney !== null && isDuluxWorkspace(workspaceCapabilities) === true ? (
                            <DuluxLeadDispositionModal
                                key={`dulux-disposition-${selectedDispositionJourney.id}`}
                                onClose={() => {
                                    setSelectedDispositionJourney(null);
                                }}
                                event={{
                                    id: selectedDispositionJourney.id,
                                    templateId: selectedDispositionJourney.id,
                                    eventContainerId: selectedDispositionJourney.id,
                                    eventContainer: selectedDispositionJourney,
                                    additionalColumnValues: {},
                                    revisionId: selectedDispositionJourney.id,
                                    createdAt: selectedDispositionJourney.createdAt,
                                    updatedAt: selectedDispositionJourney.updatedAt,
                                    createdBy: selectedDispositionJourney.createdBy,
                                    updatedBy: selectedDispositionJourney.updatedBy,
                                    status: OttopilotEventStatus.active,
                                }}
                                eventContainerTemplate={journeysModuleConfiguration}
                                triggerConfiguration={workspaceListSupport.disposition.triggerConfiguration}
                                queryKeyPrefix={[uniqueUuid`efadf6c4-78b9-4e6e-b474-0f8c46f661bf`]}
                            />
                        ) : null}
                        {isRegistryLeadsView === true && workspaceListSupport !== null && selectedDispositionJourney !== null && isDuluxWorkspace(workspaceCapabilities) !== true ? (
                            <EventContainerListDispositionModal
                                key={`journey-disposition-${selectedDispositionJourney.id}`}
                                onClose={() => {
                                    setSelectedDispositionJourney(null);
                                }}
                                event={{
                                    id: selectedDispositionJourney.id,
                                    templateId: selectedDispositionJourney.id,
                                    eventContainerId: selectedDispositionJourney.id,
                                    eventContainer: selectedDispositionJourney,
                                    additionalColumnValues: {},
                                    revisionId: selectedDispositionJourney.id,
                                    createdAt: selectedDispositionJourney.createdAt,
                                    updatedAt: selectedDispositionJourney.updatedAt,
                                    createdBy: selectedDispositionJourney.createdBy,
                                    updatedBy: selectedDispositionJourney.updatedBy,
                                    status: OttopilotEventStatus.active,
                                }}
                                eventContainerTemplate={journeysModuleConfiguration}
                                triggerConfiguration={workspaceListSupport.disposition.triggerConfiguration}
                                dispositionConfiguration={workspaceListSupport.disposition.dispositionConfiguration}
                                dispositionUiLevels={workspaceListSupport.disposition.dispositionUiLevels}
                                getLeadDisplayName={workspaceListSupport.disposition.getLeadDisplayName}
                                queryKeyPrefix={[uniqueUuid`efadf6c4-78b9-4e6e-b474-0f8c46f661bf`]}
                            />
                        ) : null}
                    </>
                }
            />

            {isMobile && journeySliceConfig.capabilities.showExportMenu === true && (
                <>
                    <BottomDrawer
                        open={moreActionsDrawerOpen}
                        setOpen={setMoreActionsDrawerOpen}
                        title="More actions"
                        fitContentOnOpen={true}
                    >
                        <ModuleMoreActionsDrawerContent
                            onExport={() => {
                                setMoreActionsDrawerOpen(false);
                                setExportDrawerOpen(true);
                            }}
                            onArchivedData={
                                showArchivedDataMenuItem === true
                                    ? () => {
                                          setMoreActionsDrawerOpen(false);
                                          handleOpenArchivedData();
                                      }
                                    : undefined
                            }
                        />
                    </BottomDrawer>
                    <BottomDrawer
                        open={exportDrawerOpen}
                        setOpen={setExportDrawerOpen}
                        title="Export"
                        fitContentOnOpen={true}
                    >
                        <ExportOptionsDrawerContent
                            onExportAsCsv={isEmotoradWorkspace(workspace) === true ? undefined : () => {
                                handleExportAllRowsLikeCsv();
                                setExportDrawerOpen(false);
                            }}
                            {...(isAmaronWorkspace(workspaceCapabilities) !== true
                                ? {
                                      onExportAsPng: () => {
                                          handleExportAllRowsAsPng();
                                          setExportDrawerOpen(false);
                                      },
                                  }
                                : {})}
                        />
                    </BottomDrawer>
                </>
            )}
            {restoreJourneyTargets !== null && restoreJourneyTargets.length > 0 ? (
                <RestoreConfirmationModal
                    isOpen={isRestoreJourneyModalOpen}
                    setIsOpen={(isOpen) => {
                        setIsRestoreJourneyModalOpen(isOpen);
                        if (isOpen !== true) {
                            setRestoreJourneyTargets(null);
                        }
                    }}
                    itemType="journey"
                    onConfirm={async () => {
                        await restoreJourneyMutation.mutateAsync();
                    }}
                    isLoading={restoreJourneyMutation.isPending}
                />
            ) : null}
            {showRestoreSuccessToast === true ? (
                <TableSuccessToast
                    title="Success"
                    description="Lifecycle details has been restored successfully."
                    backgroundColor={RESTORE_SUCCESS_TOAST_BACKGROUND}
                    onClose={() => setShowRestoreSuccessToast(false)}
                />
            ) : null}
        </>
    );
}

export type KanbanCardDetail = {
    iconId: Uuid;
    label: string;
    value: string;
};

function getJourneyKanbanSystemFieldStringValue({
    field,
    journey,
    users,
    userTemplates,
}: {
    field: KanbanCardSystemFieldId;
    journey: OttopilotEventContainer;
    users: User[];
    userTemplates: UserTemplate[];
}): string | null {
    switch (field) {
        case "createdBy": {
            return getUserDisplayName(journey.createdBy, users, userTemplates);
        }
        case "updatedBy": {
            return getUserDisplayName(journey.updatedBy, users, userTemplates);
        }
        case "createdAt": {
            return formatDateTime(journey.createdAt);
        }
        case "updatedAt": {
            return formatDateTime(journey.updatedAt);
        }
        default: {
            const _exhaustive: never = field;
            throw new Error(`Unhandled kanban system field: ${String(_exhaustive)}`);
        }
    }
}

function getKanbanCardDataFromViewDetails(getAdditionalColumns: CapabilityUdoColumnResolver, {
    journeyModuleConfiguration,
    journey,
    users,
    userTemplates,
    organizationalUnits,
    organizationalUnitTemplates,
    products,
    fillEmptyDetailSlots = false,
}: {
    journeyModuleConfiguration: JourneysModuleConfiguration;
    journey: OttopilotEventContainer;
    users: User[];
    userTemplates: UserTemplate[];
    btlActivities: Array<BtlActivity>;
    organizationalUnits: OrganizationalUnit[];
    organizationalUnitTemplates: OrganizationalUnitTemplate[];
    eventTemplates: Array<OttopilotEventTemplate>;
    products: Array<ProductCatalogue>;
    fillEmptyDetailSlots?: boolean;
}): {heading: string | null; details: KanbanCardDetail[][]} {
    const {details: data, heading: headingArray} =
        journeyModuleConfiguration.configuration.eventContainersPage.kanbanBoardCardView;

    const columnTypeMap = new Map(getAdditionalColumns(journeyModuleConfiguration).map((col) => [col.id, col.type]));

    const getStringValue = (dataSourceId: Uuid | KanbanCardSystemFieldId): string | null => {
        if (isKanbanCardSystemFieldId(dataSourceId) === true) {
            return getJourneyKanbanSystemFieldStringValue({
                field: dataSourceId,
                journey: journey,
                users: users,
                userTemplates: userTemplates,
            });
        }
        const columnUuid = dataSourceId as Uuid;
        const type = columnTypeMap.get(columnUuid);
        if (type === undefined) return null;
        return getUserDefinedObjectAdditionalColumnDetails(getAdditionalColumns(journeyModuleConfiguration).find((column) => column.id === columnUuid)!).stringValueGetter({
            value: journey.additionalColumnValues[columnUuid] as never,
            users,
            userTemplates,
            organizationalUnits,
            organizationalUnitTemplates,
            products: products,
        });
    };
    const details = data.map((column) =>
        column.map((columnDetail) => {
            let value: string | null = null;

            if (columnDetail.type === "SingleColumn") {
                value = getStringValue(columnDetail.dataSource);
            } else if (columnDetail.type === "MultipleColumn") {
                const values = columnDetail.dataSource.map(getStringValue).filter((v): v is string => v !== null);
                value = values.length > 0 ? values.join(columnDetail.seperator) : null;
            }

            if (fillEmptyDetailSlots === true) {
                return {
                    iconId: columnDetail.iconId,
                    label: columnDetail.label,
                    value: formatKanbanCardFieldValue(value),
                };
            }

            return value !== null ? {iconId: columnDetail.iconId, label: columnDetail.label, value} : null;
        }).filter((item): item is KanbanCardDetail => item !== null),
    );
    let headingValue: string | null = null;
    for (const item of headingArray) {
        const value = getStringValue(item);
        if (value !== null) {
            headingValue = value;
            break;
        }
    }
    return {
        heading: fillEmptyDetailSlots === true ? formatKanbanCardFieldValue(headingValue) : (headingValue ?? null),
        details,
    };
}

function formatAmaronKanbanDispositionLevel(raw: unknown): string {
    if (typeof raw !== "string" || raw.trim() === "") {
        return "";
    }
    return flatDispositionToDisplayNameFormat(raw.trim());
}

function isKanbanActionPlatformDetailLabel(label: string): boolean {
    const normalized = label.trim().toLowerCase();
    return normalized.includes("platform") === true && normalized.includes("disposition") !== true && normalized.includes("utm") !== true;
}

function isKanbanUtmPlatformDetailLabel(label: string): boolean {
    const normalized = label.trim().toLowerCase();
    return normalized.includes("utm") === true && normalized.includes("platform") === true;
}

function isKanbanRemarksDetailLabel(label: string): boolean {
    const normalized = label.trim().toLowerCase();
    return normalized === "remarks" || normalized === "remark" || normalized.includes("remark");
}

function isKanbanServiceRequestedDetailLabel(label: string): boolean {
    const normalized = label.trim().toLowerCase();
    return normalized.includes("service") && normalized.includes("request");
}

function isKanbanUtmSourceDetailLabel(label: string): boolean {
    const normalized = label.trim().toLowerCase();
    return normalized.includes("utm") && normalized.includes("source");
}

type AmaronKanbanSplitCardDetails = {
    platformDetail: KanbanCardDetail | null;
    serviceRequestedDetail: KanbanCardDetail | null;
    utmPlatformDetail: KanbanCardDetail | null;
    utmSourceDetail: KanbanCardDetail | null;
    remarksDetail: KanbanCardDetail | null;
    remainingKanbanCardData: KanbanCardDetail[][];
};

function splitAmaronKanbanCardDetails(details: KanbanCardDetail[][] | undefined): AmaronKanbanSplitCardDetails {
    let platformDetail: KanbanCardDetail | null = null;
    let serviceRequestedDetail: KanbanCardDetail | null = null;
    let utmPlatformDetail: KanbanCardDetail | null = null;
    let utmSourceDetail: KanbanCardDetail | null = null;
    let remarksDetail: KanbanCardDetail | null = null;
    const remainingKanbanCardData: KanbanCardDetail[][] = [];
    for (const row of details ?? []) {
        const remainingRow: KanbanCardDetail[] = [];
        for (const element of row) {
            if (serviceRequestedDetail === null && isKanbanServiceRequestedDetailLabel(element.label) === true) {
                serviceRequestedDetail = element;
                continue;
            }
            if (utmSourceDetail === null && isKanbanUtmSourceDetailLabel(element.label) === true) {
                utmSourceDetail = element;
                continue;
            }
            if (utmPlatformDetail === null && isKanbanUtmPlatformDetailLabel(element.label) === true) {
                utmPlatformDetail = element;
                continue;
            }
            if (isKanbanRemarksDetailLabel(element.label) === true) {
                if (remarksDetail === null) {
                    remarksDetail = element;
                }
                continue;
            }
            if (platformDetail === null && isKanbanActionPlatformDetailLabel(element.label) === true) {
                platformDetail = element;
                continue;
            }
            remainingRow.push(element);
        }
        if (remainingRow.length > 0) {
            remainingKanbanCardData.push(remainingRow);
        }
    }
    return {platformDetail, serviceRequestedDetail, utmPlatformDetail, utmSourceDetail, remarksDetail, remainingKanbanCardData};
}

function resolveJourneyKanbanAdditionalColumnDisplay(getAdditionalColumns: CapabilityUdoColumnResolver, {
    journey,
    columnId,
    journeyModuleConfiguration,
    users,
    userTemplates,
    organizationalUnits,
    organizationalUnitTemplates,
    products,
}: {
    journey: OttopilotEventContainer;
    columnId: Uuid;
    journeyModuleConfiguration: JourneysModuleConfiguration;
    users: User[];
    userTemplates: UserTemplate[];
    btlActivities: Array<BtlActivity>;
    organizationalUnits: OrganizationalUnit[];
    organizationalUnitTemplates: OrganizationalUnitTemplate[];
    eventTemplates: Array<OttopilotEventTemplate>;
    products: Array<ProductCatalogue>;
}): string {
    const column = getAdditionalColumns(journeyModuleConfiguration).find((col) => col.id === columnId);
    if (column === undefined) {
        return formatKanbanCardFieldValue(null);
    }
    const rawValue = journey.additionalColumnValues[columnId];
    if (rawValue === null || rawValue === undefined) {
        return formatKanbanCardFieldValue(null);
    }
    if (typeof rawValue === "string" && rawValue.trim() === "") {
        return formatKanbanCardFieldValue(null);
    }
    const displayValue = getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
        value: journey.additionalColumnValues[columnId] as never,
        users,
        userTemplates,
        organizationalUnits,
        organizationalUnitTemplates,
        products,
    });
    if (displayValue === "?" || displayValue.trim() === "") {
        return formatKanbanCardFieldValue(null);
    }
    return displayValue;
}

function AmaronKanbanDetailFieldRow({detail}: {detail: KanbanCardDetail}) {
    const Icon = journeyKanbanCardIconsMap[detail.iconId];
    const value = formatKanbanCardFieldValue(detail.value);
    return (
        <div className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-ds-space-100 items-start min-w-0">
            <div className="w-6 h-6 flex shrink-0 items-center justify-center [&_svg]:h-4 [&_svg]:w-4">{Icon !== undefined ? Icon({label: ""}) : null}</div>
            <div className="min-w-0">
                <div
                    className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 line-clamp-1 text-ellipsis"
                    style={{
                        fontFamily: "Inter",
                        color: token("color.text.subtlest"),
                    }}
                >
                    {detail.label}
                </div>
                <div
                    className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 line-clamp-2 text-ellipsis"
                    style={{
                        fontFamily: "Inter",
                        color: token("color.text"),
                    }}
                >
                    {value}
                </div>
            </div>
        </div>
    );
}

function AmaronKanbanSecondaryDetailsSection({
    serviceRequestedDetail,
    remarksDetail,
    utmPlatformDetail,
    utmSourceDetail,
}: {
    serviceRequestedDetail: KanbanCardDetail;
    remarksDetail: KanbanCardDetail;
    utmPlatformDetail: KanbanCardDetail;
    utmSourceDetail: KanbanCardDetail;
}) {
    return (
        <>
            <VerticalSpacer className="h-ds-space-150" />
            <div className="w-full h-px bg-ds-border" />
            <VerticalSpacer className="h-ds-space-150" />
            <div className="grid grid-cols-2 gap-x-ds-space-150 gap-y-ds-space-100 items-start">
                <div className="min-w-0">
                    <AmaronKanbanDetailFieldRow detail={serviceRequestedDetail} />
                </div>
                <div className="min-w-0">
                    <AmaronKanbanDetailFieldRow detail={remarksDetail} />
                </div>
                <div className="min-w-0">
                    <AmaronKanbanDetailFieldRow detail={utmPlatformDetail} />
                </div>
                <div className="min-w-0">
                    <AmaronKanbanDetailFieldRow detail={utmSourceDetail} />
                </div>
            </div>
        </>
    );
}

function formatAmaronKanbanCreatedAtLine(isoString: string): string {
    if (isoString === "") {
        return "-";
    }
    const dt = DateTime.fromISO(isoString, {zone: "Asia/Kolkata"});
    if (dt.isValid !== true) {
        return "-";
    }
    return `${dt.toFormat("d LLL yyyy")}, ${dt.toFormat("h:mm a").toLowerCase()}`;
}

function AmaronKanbanMetaRow({
    createdAt,
    onDispositionClick,
    dispositionLabel,
    dispositionTooltip,
}: {
    createdAt: string;
    onDispositionClick?: () => void;
    dispositionLabel: string;
    dispositionTooltip?: string;
}) {
    return (
        <div className="flex flex-wrap items-center gap-x-ds-space-100 gap-y-ds-space-050 justify-between">
            <div className="grid grid-flow-col justify-start gap-x-ds-space-075 items-center min-w-0">
                <CalendarIcon
                    label=""
                    size="small"
                    color={token("color.icon.subtle")}
                />
                <span
                    className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200"
                    style={{fontFamily: "Inter", color: token("color.text.subtle")}}
                >
                    {formatAmaronKanbanCreatedAtLine(createdAt)}
                </span>
                {onDispositionClick !== undefined ? (
                    <KanbanCardDispositionControl
                        label=""
                        tooltip={dispositionTooltip !== undefined ? dispositionTooltip : dispositionLabel}
                        compact={true}
                        onClick={onDispositionClick}
                    />
                ) : null}
            </div>
        </div>
    );
}

function isKanbanCustomerNameDetailLabel(label: string): boolean {
    const normalized = label.trim().toLowerCase();
    return normalized.includes("customer name") || normalized === "full name" || normalized === "name";
}

function isKanbanContactNumberDetailLabel(label: string): boolean {
    const normalized = label.trim().toLowerCase();
    return normalized.includes("contact") || normalized.includes("phone");
}

function filterAmaronKanbanIdentityDetails(details: KanbanCardDetail[][]): KanbanCardDetail[][] {
    return details
        .map((row) =>
            row.filter(
                (element) =>
                    isKanbanCustomerNameDetailLabel(element.label) !== true && isKanbanContactNumberDetailLabel(element.label) !== true,
            ),
        )
        .filter((row) => row.length > 0);
}

function AmaronKanbanCardIdentity({customerName}: {customerName: string}) {
    return (
        <div className="min-w-0">
            <div
                className="text-[1rem] font-ds-font-weight-bold leading-ds-space-300 line-clamp-1 text-ellipsis"
                style={{fontFamily: "Inter", color: token("color.text")}}
            >
                {customerName.trim() !== "" ? customerName : "—"}
            </div>
        </div>
    );
}

function JourneyKanbanEventsBar({
    eventCount,
    eventsData,
    eventsBarOpen,
    onToggleEventsBar,
}: {
    eventCount: number;
    eventsData: Array<{
        eventType: Uuid;
        eventName: string;
        count: number;
    }>;
    eventsBarOpen: boolean;
    onToggleEventsBar: (e: MouseEvent) => void;
}) {
    return (
        <>
            <div className="w-full h-px bg-ds-border" />
            <div className="w-full px-ds-space-200 py-ds-space-150">
                <div className="grid grid-cols-[auto_auto] gap-x-ds-space-150 justify-between">
                    <div className="flex gap-ds-space-075 items-center">
                        <SubtasksIcon
                            size={"small"}
                            label=""
                        />
                        <span
                            className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200"
                            style={{fontFamily: "Inter", color: token("color.text.subtle")}}
                        >
                            {eventCount} Events
                        </span>
                    </div>
                    <AnankeIconButton
                        type="button"
                        spacing="compact"
                        onClick={onToggleEventsBar}
                        icon={eventsBarOpen ? ChevronUpIcon : ChevronDownIcon}
                        label=""
                        appearance="subtle"
                    />
                </div>
                <div
                    className={twJoin(
                        "grid transition-[grid-template-rows] duration-300 ease-in-out",
                        eventsBarOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                    )}
                >
                    <div className="overflow-hidden">
                        <div className="grid grid-cols-2 pt-ds-space-100 gap-ds-space-075 w-full">
                            <ItemBuilder
                                items={eventsData}
                                itemBuilder={(event) => (
                                    <div
                                        key={event.eventType}
                                        className="rounded-md grid grid-cols-[minmax(0,1fr)_auto] gap-x-ds-space-100 items-center py-ds-space-075 px-ds-space-100 min-w-0"
                                        style={{
                                            fontFamily: "Inter",
                                            color: token("color.text.subtle"),
                                            background: token("color.background.disabled"),
                                        }}
                                    >
                                        <span
                                            className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 line-clamp-1 text-ellipsis min-w-0"
                                            style={{fontFamily: "Inter", color: token("color.text")}}
                                        >
                                            {event.eventName}
                                        </span>
                                        <span
                                            className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 line-clamp-1 text-ellipsis shrink-0"
                                            style={{fontFamily: "Inter", color: token("color.text")}}
                                        >
                                            {event.count}
                                        </span>
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function JourneyKanbanBoardCard({
    journey,
    rowActionsConfig,
    onDispositionClick,
    suppressPhoneDial = false,
}: {
    journey: EventContainerWithEvents;
    rowActionsConfig: TableRowActionsConfig<EventContainerWithEvents>;
    onDispositionClick?: () => void;
    suppressPhoneDial?: boolean;
}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const {workspace, workspaceCapabilities, userDefinedObjectOptionsByTemplateId} = useWorkspace();
    const journeysModuleConfiguration = useOnlyEventContainerTemplate();
    const journeyDispositionId = getOptionalEventContainerDispositionId(journey, journeysModuleConfiguration);
    const journeyDispositionOptions = getEventContainerDispositionOptions(getAdditionalColumns, journeysModuleConfiguration, userDefinedObjectOptionsByTemplateId);
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const btlActivities = useBtlActivities() as Array<BtlActivity>;
    const eventTemplates = useEventTemplates();
    const products = useProductCatalogue() ?? [];
    const contactNumberColumn = journeysModuleConfiguration.configuration.eventContainersPage.callButtonColumnId;
    const contactRaw = contactNumberColumn !== null ? journey.additionalColumnValues[contactNumberColumn] : undefined;
    const kanbanPhoneDial = useMemo(() => (suppressPhoneDial === true ? null : resolveKanbanPhoneDial(contactRaw)), [contactRaw, suppressPhoneDial]);
    const {eventsData, lastEventData} = useMemo<{
        eventsData: Array<{
            eventType: Uuid;
            eventName: string;
            displayNameVariants: OttopilotEventTemplate["configuration"]["displayNameVariants"];
            count: number;
        }>;
        lastEventData: {id: Uuid; eventName: string; eventType: Uuid} | null;
    }>(() => {
        const eventsData = eventTemplates.map((config) => ({
            eventType: config.id,
            eventName: config.displayName,
            displayNameVariants: config.configuration.displayNameVariants,
            count: journey.events.filter((event) => event.templateId === config.id).length,
        }));
        const lastEvent = journey.events.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const lastEventData =
            lastEvent !== undefined
                ? eventTemplates.find((item) => {
                      return item.id === lastEvent.templateId;
                  })
                : null;
        if (lastEventData !== null && lastEventData !== undefined && lastEvent !== undefined) {
            return {
                eventsData,
                lastEventData: {
                    id: lastEvent.id,
                    eventName: lastEventData.displayName,
                    eventType: lastEvent.templateId,
                },
            };
        }
        return {eventsData, lastEventData: null};
    }, [journey.id, journey.events.length, eventTemplates]);

    const {date: createdDate, time: createdTime} = formatDateAndTime(journey.createdAt);
    const {heading: kanbanCardHeading, details: kanbanCardData} = useMemo(
        () =>
            getKanbanCardDataFromViewDetails(getAdditionalColumns, {
                journeyModuleConfiguration: journeysModuleConfiguration,
                journey,
                users,
                userTemplates,
                btlActivities,
                organizationalUnits,
                organizationalUnitTemplates,
                eventTemplates: eventTemplates,
                products: products,
                fillEmptyDetailSlots: isAmaronWorkspace(workspaceCapabilities) === true,
            }),
        [getAdditionalColumns,
            journey.id,
            journeysModuleConfiguration,
            journey,
            users,
            userTemplates,
            btlActivities,
            organizationalUnits,
            organizationalUnitTemplates,
            eventTemplates,
            products,
            workspace.id,
        ],
    );
    const [isHovered, setIsHovered] = useState(false);
    const [eventsBarOpen, setEventsBarOpen] = useState(false);
    const dispositionLabel = flatDispositionToDisplayNameFormat(
        journeyDispositionOptions.find((disposition) => disposition.id === journeyDispositionId)?.displayName ?? "?",
    );
    const isAmaronLeadsKanban = isAmaronWorkspace(workspaceCapabilities) === true;
    const isDuluxLeadsKanban = isDuluxWorkspace(workspaceCapabilities) === true;
    const amaronDispositionPresentation = useMemo(() => {
        if (isAmaronLeadsKanban !== true) {
            return null;
        }
        const l1 = formatAmaronKanbanDispositionLevel(journey.additionalColumnValues[AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID]);
        const l2 = formatAmaronKanbanDispositionLevel(journey.additionalColumnValues[AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID]);
        const l3 = formatAmaronKanbanDispositionLevel(journey.additionalColumnValues[AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID]);
        const l4 = formatAmaronKanbanDispositionLevel(journey.additionalColumnValues[AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID]);
        const parts = [l1, l2, l3, l4].filter((part) => part !== "");
        return {
            label: l4 !== "" ? l4 : l3 !== "" ? l3 : l2 !== "" ? l2 : l1,
            tooltip: parts.length > 0 ? `Dispositions: ${parts.join(" | ")}` : "",
        };
    }, [isAmaronLeadsKanban, journey.id, journey.additionalColumnValues]);
    const duluxDispositionPresentation = useMemo(() => {
        if (isDuluxLeadsKanban !== true) {
            return null;
        }
        const matchedDisposition = journeyDispositionOptions.find((disposition) => disposition.id === journeyDispositionId);
        if (matchedDisposition === undefined) {
            return null;
        }
        const levels = dispositionToLevelFormat(matchedDisposition.displayName);
        if (levels.trim() === "") {
            return null;
        }
        return {
            tooltip: `Dispositions: ${levels}`,
        };
    }, [isDuluxLeadsKanban, journey.id, journeyDispositionId, journeyDispositionOptions]);
    const kanbanDispositionLabel =
        isDuluxLeadsKanban === true
            ? ""
            : amaronDispositionPresentation !== null && amaronDispositionPresentation.label !== ""
              ? amaronDispositionPresentation.label
              : dispositionLabel;
    const kanbanDispositionTooltip =
        amaronDispositionPresentation !== null && amaronDispositionPresentation.tooltip !== ""
            ? amaronDispositionPresentation.tooltip
            : duluxDispositionPresentation !== null && duluxDispositionPresentation.tooltip !== ""
              ? duluxDispositionPresentation.tooltip
              : undefined;
    const amaronKanbanCardDetails = useMemo(() => {
        if (isAmaronLeadsKanban !== true) {
            return {
                platformDetail: null as KanbanCardDetail | null,
                serviceRequestedDetail: null as KanbanCardDetail | null,
                utmPlatformDetail: null as KanbanCardDetail | null,
                utmSourceDetail: null as KanbanCardDetail | null,
                remarksDetail: null as KanbanCardDetail | null,
                remainingKanbanCardData: kanbanCardData,
            };
        }
        return splitAmaronKanbanCardDetails(kanbanCardData);
    }, [isAmaronLeadsKanban, kanbanCardData]);
    const cardDetailsToRender = useMemo(() => {
        const details = isAmaronLeadsKanban === true ? (amaronKanbanCardDetails.remainingKanbanCardData ?? []) : (kanbanCardData ?? []);
        if (isAmaronLeadsKanban !== true) {
            return details;
        }
        return filterAmaronKanbanIdentityDetails(details);
    }, [isAmaronLeadsKanban, amaronKanbanCardDetails.remainingKanbanCardData, kanbanCardData]);
    const amaronKanbanIdentity = useMemo(() => {
        if (isAmaronLeadsKanban !== true) {
            return null;
        }
        const mergedValues: Record<Uuid, unknown> = {...journey.additionalColumnValues};
        mergedValues[AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID] = "";
        const customerName = processOttopilotStringTemplate(journeysModuleConfiguration.configuration.displayNameTemplate, mergedValues).trim();
        return {customerName};
    }, [isAmaronLeadsKanban, journey.id, journey.additionalColumnValues, journeysModuleConfiguration.configuration.displayNameTemplate]);
    const amaronKanbanSecondaryDetails = useMemo(() => {
        if (isAmaronLeadsKanban !== true) {
            return null;
        }
        const journeyKanbanColumnDeps = {
            journey,
            journeyModuleConfiguration: journeysModuleConfiguration,
            users,
            userTemplates,
            btlActivities,
            organizationalUnits,
            organizationalUnitTemplates,
            eventTemplates,
            products,
        };
        const serviceRequestedColumn = getAdditionalColumns(journeysModuleConfiguration).find(
            (column) => column.id === AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
        );
        const utmPlatformColumn = getAdditionalColumns(journeysModuleConfiguration).find((column) => column.id === AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID);
        const utmSourceColumn = getAdditionalColumns(journeysModuleConfiguration).find((column) => column.id === AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID);
        const remarksColumn = getAdditionalColumns(journeysModuleConfiguration).find((column) => column.id === AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID);
        const remarksLabel = remarksColumn?.displayName.replace(/\s*\(Recent\)\s*/i, "").trim() || "Remarks";
        const serviceRequestedDetail: KanbanCardDetail = {
            iconId: AnankeServiceIcon,
            label: serviceRequestedColumn?.displayName ?? "Service requested",
            value: resolveJourneyKanbanAdditionalColumnDisplay(getAdditionalColumns, {
                ...journeyKanbanColumnDeps,
                columnId: AMARON__EVENT_CONTAINER__SERVICE_REQUESTED__COLUMN_ID,
            }),
        };
        const remarksDetail: KanbanCardDetail = {
            iconId: AnankeInformationCircleIcon,
            label: remarksLabel,
            value: resolveJourneyKanbanAdditionalColumnDisplay(getAdditionalColumns, {
                ...journeyKanbanColumnDeps,
                columnId: AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
            }),
        };
        const utmPlatformDetail: KanbanCardDetail = {
            iconId: AnankeDevicesIcon,
            label: utmPlatformColumn?.displayName ?? "UTM Platform",
            value: resolveJourneyKanbanAdditionalColumnDisplay(getAdditionalColumns, {
                ...journeyKanbanColumnDeps,
                columnId: AMARON__EVENT_CONTAINER__UTM_PLATFORM__COLUMN_ID,
            }),
        };
        const utmSourceDetail: KanbanCardDetail = {
            iconId: AnankeDatabaseStorageCacheIcon,
            label: utmSourceColumn?.displayName ?? "UTM Source",
            value: resolveJourneyKanbanAdditionalColumnDisplay(getAdditionalColumns, {
                ...journeyKanbanColumnDeps,
                columnId: AMARON__EVENT_CONTAINER__UTM_SOURCE__COLUMN_ID,
            }),
        };
        return {serviceRequestedDetail, remarksDetail, utmPlatformDetail, utmSourceDetail};
    }, [getAdditionalColumns,
        isAmaronLeadsKanban,
        journey.id,
        journey.additionalColumnValues,
        journeysModuleConfiguration,
        users,
        userTemplates,
        btlActivities,
        organizationalUnits,
        organizationalUnitTemplates,
        eventTemplates,
        products,
    ]);
    return (
        <div
            className="relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className={twJoin(`w-full`, "shadow-sm", "relative group", "max-sm:hidden")}
                style={{
                    borderRadius: token("radius.small"),
                    background: isHovered ? token("color.background.disabled") : token("color.icon.inverse"),
                    boxShadow: "0 0 1px 0 rgba(30, 31, 33, 0.12), 0 0 8px 0 rgba(30, 31, 33, 0.16)",
                }}
            >
                <div className="w-full pt-ds-space-200 px-ds-space-200 pb-ds-space-150">
                    <div
                        className="grid grid-flow-col gap-ds-space-100 justify-between"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="min-w-0 overflow-hidden">
                            {amaronKanbanIdentity !== null ? (
                                <AmaronKanbanCardIdentity customerName={amaronKanbanIdentity.customerName} />
                            ) : (
                                <div
                                    className="text-[1rem] font-ds-font-weight-bold leading-ds-space-300 line-clamp-1 text-ellipsis"
                                    style={{fontFamily: "Inter", color: token("color.text")}}
                                >
                                    {kanbanCardHeading}
                                </div>
                            )}
                        </div>
                        <KanbanCardRowActions
                            row={journey}
                            config={rowActionsConfig}
                            phoneDial={kanbanPhoneDial}
                        />
                    </div>
                    <VerticalSpacer className="h-ds-space-100" />
                    {isAmaronLeadsKanban === true ? (
                        <AmaronKanbanMetaRow
                            createdAt={journey.createdAt}
                            onDispositionClick={onDispositionClick}
                            dispositionLabel={kanbanDispositionLabel}
                            dispositionTooltip={kanbanDispositionTooltip}
                        />
                    ) : (
                        <div className="flex flex-wrap justify-between gap-ds-space-100 items-center">
                            <div className="flex flex-wrap items-center gap-x-ds-space-100 gap-y-ds-space-050 min-w-0">
                                {lastEventData !== null ? (
                                    <div className="[&>div>span:first-of-type]:m-0!">
                                        <AnankeSimpleTag text={lastEventData.eventName} />
                                    </div>
                                ) : null}
                                {onDispositionClick !== undefined ? (
                                    <KanbanCardDispositionControl
                                        label={kanbanDispositionLabel}
                                        tooltip={kanbanDispositionTooltip}
                                        compact={isDuluxLeadsKanban === true}
                                        onClick={onDispositionClick}
                                    />
                                ) : null}
                            </div>
                            <div className="line-clamp-1 grid grid-flow-col justify-start gap-x-ds-space-075 items-center shrink-0">
                                <ClockIcon label="" />
                                <span
                                    className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200"
                                    style={{fontFamily: "Inter", color: token("color.text.subtle")}}
                                >
                                    {createdDate}
                                </span>
                                <span
                                    className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200"
                                    style={{fontFamily: "Inter", color: token("color.text.subtle")}}
                                >
                                    {createdTime}
                                </span>
                            </div>
                        </div>
                    )}
                    {amaronKanbanSecondaryDetails !== null ? (
                        <AmaronKanbanSecondaryDetailsSection
                            serviceRequestedDetail={amaronKanbanSecondaryDetails.serviceRequestedDetail}
                            remarksDetail={amaronKanbanSecondaryDetails.remarksDetail}
                            utmPlatformDetail={amaronKanbanSecondaryDetails.utmPlatformDetail}
                            utmSourceDetail={amaronKanbanSecondaryDetails.utmSourceDetail}
                        />
                    ) : null}
                    {cardDetailsToRender.length > 0 ? (
                        <>
                            <VerticalSpacer className="h-ds-space-150" />
                            <div className="w-full h-px bg-ds-border" />
                            <VerticalSpacer className="h-ds-space-150" />
                            <div className="grid gap-y-ds-space-150">
                                <ItemBuilder
                                    items={cardDetailsToRender}
                                    itemBuilder={(elements, elementsIndex) => (
                                        <div
                                            key={elementsIndex}
                                            className={twJoin(
                                                "grid gap-x-ds-space-150 gap-y-ds-space-100 items-start",
                                                elements.length === 2 ? "grid-cols-2" : "grid-cols-1",
                                            )}
                                        >
                                            <ItemBuilder
                                                items={elements}
                                                itemBuilder={(element, elementId) => (
                                                    <div
                                                        key={elementId}
                                                        className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-ds-space-100 items-start min-w-0"
                                                    >
                                                        <div className="w-6 h-6 flex shrink-0 items-center justify-center [&_svg]:h-4 [&_svg]:w-4">
                                                            {journeyKanbanCardIconsMap[element.iconId]!({
                                                                label: "",
                                                            })}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div
                                                                className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 line-clamp-1 text-ellipsis"
                                                                style={{
                                                                    fontFamily: "Inter",
                                                                    color: token("color.text.subtlest"),
                                                                }}
                                                            >
                                                                {element.label}
                                                            </div>
                                                            <div
                                                                className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 line-clamp-2 text-ellipsis"
                                                                style={{
                                                                    fontFamily: "Inter",
                                                                    color: token("color.text"),
                                                                }}
                                                            >
                                                                {element.value}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    )}
                                />
                            </div>
                        </>
                    ) : null}

                    <VerticalSpacer className="h-ds-space-100" />
                </div>
                <JourneyKanbanEventsBar
                    eventCount={journey.events.length}
                    eventsData={eventsData}
                    eventsBarOpen={eventsBarOpen}
                    onToggleEventsBar={(e) => {
                        e.stopPropagation();
                        setEventsBarOpen((prev) => !prev);
                    }}
                />
            </div>
            <div
                className={twJoin(`w-full`, "shadow-sm", "relative group", "sm:hidden")}
                style={{
                    borderRadius: token("radius.small"),
                    background: token("color.icon.inverse"),
                    boxShadow: "0 0 1px 0 rgba(30, 31, 33, 0.12), 0 0 8px 0 rgba(30, 31, 33, 0.16)",
                }}
            >
                <div className="w-full pt-ds-space-200 px-ds-space-200 pb-ds-space-150">
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-ds-space-100 justify-between items-start">
                        <div className="min-w-0 overflow-hidden">
                            {amaronKanbanIdentity !== null ? (
                                <AmaronKanbanCardIdentity customerName={amaronKanbanIdentity.customerName} />
                            ) : (
                                <div
                                    className="text-[1rem] font-ds-font-weight-bold leading-ds-space-300 line-clamp-1 text-ellipsis"
                                    style={{fontFamily: "Inter", color: token("color.text")}}
                                >
                                    {kanbanCardHeading}
                                </div>
                            )}
                        </div>
                        <KanbanCardRowActions
                            row={journey}
                            config={rowActionsConfig}
                            phoneDial={kanbanPhoneDial}
                        />
                    </div>
                    {isAmaronLeadsKanban === true ? (
                        <>
                            <VerticalSpacer className="h-ds-space-100" />
                            <AmaronKanbanMetaRow
                                createdAt={journey.createdAt}
                                onDispositionClick={onDispositionClick}
                                dispositionLabel={kanbanDispositionLabel}
                                dispositionTooltip={kanbanDispositionTooltip}
                            />
                            {amaronKanbanSecondaryDetails !== null ? (
                                <AmaronKanbanSecondaryDetailsSection
                                    serviceRequestedDetail={amaronKanbanSecondaryDetails.serviceRequestedDetail}
                                    remarksDetail={amaronKanbanSecondaryDetails.remarksDetail}
                                    utmPlatformDetail={amaronKanbanSecondaryDetails.utmPlatformDetail}
                                    utmSourceDetail={amaronKanbanSecondaryDetails.utmSourceDetail}
                                />
                            ) : null}
                        </>
                    ) : (
                        <>
                            <VerticalSpacer className="h-ds-space-100" />
                            <div className="flex flex-wrap items-center gap-x-ds-space-100 gap-y-ds-space-050 min-w-0">
                                {lastEventData !== null ? (
                                    <div className="[&>div>span:first-of-type]:m-0!">
                                        <AnankeSimpleTag text={lastEventData.eventName} />
                                    </div>
                                ) : null}
                                {onDispositionClick !== undefined ? (
                                    <KanbanCardDispositionControl
                                        label={kanbanDispositionLabel}
                                        tooltip={kanbanDispositionTooltip}
                                        compact={isDuluxLeadsKanban === true}
                                        onClick={onDispositionClick}
                                    />
                                ) : null}
                            </div>
                        </>
                    )}
                </div>
                <JourneyKanbanEventsBar
                    eventCount={journey.events.length}
                    eventsData={eventsData}
                    eventsBarOpen={eventsBarOpen}
                    onToggleEventsBar={(e) => {
                        e.stopPropagation();
                        setEventsBarOpen((prev) => !prev);
                    }}
                />
            </div>
        </div>
    );
}
